// 易经64卦牌意数据
// IChing 64 Hexagrams Meanings Data

export interface ICchingScene {
    career: string;
    relationship: string;
    wealth: string;
    health: string;
    universal: string;
    transition: string;
}

export interface IChingInterpretation {
    keywords: string[];
    keywordsEn?: string[];
    imageDescription: string;
    imageDescriptionEn?: string;
    generalMeaning: string;
    generalMeaningEn?: string;
    scenes: ICchingScene;
    scenesEn?: ICchingScene;
}

export interface IChingMeaning {
    id: string;           // 卦序号 001-064
    name: string;         // 英文名
    nameCn: string;       // 中文名 (如：乾卦)
    symbol: string;       // 卦象符号 (如：☰☰)
    trigrams: {
        upper: string;    // 上卦
        lower: string;    // 下卦
    };
    introduction: string; // 卦辞简介
    introductionEn?: string; // 英文简介
    upright: IChingInterpretation;
    reversed: IChingInterpretation;
}

// 易经64卦数据
export const ichingMeanings: Record<string, IChingMeaning> = {

    // 01 - 乾卦 (乾为天)
    '001': {
        id: '001',
        name: 'Qian (The Creative)',
        nameCn: '乾卦',
        symbol: '☰☰',
        trigrams: { upper: '乾', lower: '乾' },
        introduction: '乾卦是六十四卦之首，由两个乾（天）卦组成，象征纯阳之气、刚健进取。卦辞"元亨利贞"代表最高创造力与领导力。',
        introductionEn: 'Qian is the first of the 64 hexagrams, composed of two Heaven trigrams. It symbolizes pure yang energy, strength, and progress. The judgment "Sublime Success" represents the highest creative and leadership power.',
        upright: {
            keywords: ['刚健进取', '创造力', '领导力', '开创', '成功', '自强不息', '天行健', '龙德'],
            keywordsEn: ['Strength', 'Creativity', 'Leadership', 'Initiative', 'Success', 'Self-improvement', 'Heaven', 'Dragon'],
            imageDescription: '天行健，两个乾卦重叠，象征纯阳之气充盈天地，六爷皆阳，龙腾九天。',
            imageDescriptionEn: 'Heaven in motion. Two Qian trigrams stacked represent pure yang energy filling heaven and earth. The dragon soars to the highest heaven.',
            generalMeaning: '处于最有创造力与行动力的阶段，时机成熟适合开创大业，但需保持谦逊。',
            generalMeaningEn: 'A phase of maximum creativity and action. The time is ripe for great undertakings, but maintain humility to avoid excess.',
            scenes: {
                career: '事业运势极佳，适合开创新项目、担当领导角色，努力必有成就。',
                relationship: '感情中处于主导地位，适合主动出击表达心意。',
                wealth: '财运亨通，适合投资开创，但需见好就收。',
                health: '精力充沛、体能极佳，但需防止因过劳而损耗元气。',
                universal: '当下是开创与行动的最佳时机，发挥领导力同时保持谦逊。',
                transition: '从蓄势阶段进入全力开创的正向转折。'
            },
            scenesEn: {
                career: 'Excellent career prospects. Ideal for launching new projects and taking leadership roles. Efforts will be rewarded.',
                relationship: 'In a dominant position in love. Take initiative to express feelings, but don\'t be overbearing.',
                wealth: 'Financial fortune is strong. Good for investments but know when to stop.',
                health: 'Energy is at its peak, but guard against burnout from overwork.',
                universal: 'Now is the best time for creation and action. Lead with confidence but stay humble.',
                transition: 'A positive shift from preparation into full creative momentum.'
            }
        },
        reversed: {
            keywords: ['亢龙有悔', '过刚易折', '傲慢自负', '盛极而衰', '失去控制', '刚愬自用'],
            keywordsEn: ['Arrogance', 'Excess', 'Decline', 'Overreach', 'Hubris', 'Loss of control'],
            imageDescription: '亢龙之象，阳极生阴，龙飞过高将有坠落之险。',
            imageDescriptionEn: 'The arrogant dragon. Yang at its extreme turns to yin. Flying too high leads to a dangerous fall.',
            generalMeaning: '过于刚强或傲慢导致失衡，盛极而衰的警示，需收敛锋芒。',
            generalMeaningEn: 'Excessive strength or arrogance leads to imbalance. A warning of decline after the peak. Time to moderate.',
            scenes: {
                career: '事业上可能因过于自信或强势而遭遇挫折。',
                relationship: '感情中过于强势或控制欲强，可能吓跑对方。',
                wealth: '投资过于激进可能遭受损失，需见好就收。',
                health: '精力过度消耗，需注意休息调养。',
                universal: '当下需收敛锋芒，反思自身是否过于强势。',
                transition: '从巅峰状态可能转向衰退的警示转折。'
            },
            scenesEn: {
                career: 'Setbacks due to overconfidence or dominance. Listen to others.',
                relationship: 'Being too controlling may push partners away or cause conflict.',
                wealth: 'Aggressive investments may lead to losses. Return to stability.',
                health: 'Excessive energy expenditure. Rest and recuperate.',
                universal: 'Time to moderate your approach. Reflect on whether you are being too forceful.',
                transition: 'A warning shift from peak towards potential decline.'
            }
        }
    },

    // 02 - 坤卦 (坤为地)
    '002': {
        id: '002',
        name: 'Kun (The Receptive)',
        nameCn: '坤卦',
        symbol: '☷☷',
        trigrams: { upper: '坤', lower: '坤' },
        introduction: '坤卦由两个坤（地）组成，象征纯阴之德、柔顺厚载。',
        introductionEn: 'Kun consists of two Earth trigrams, representing total Yin energy, receptivity, and the nurturing power of the Earth. It emphasizes the wisdom of following and supporting.',
        upright: {
            keywords: ['柔顺厚载', '包容承载', '顺势而为', '辅助配合', '厚德载物'],
            keywordsEn: ['Receptivity', 'Nurturing', 'Devotion', 'Support', 'Yielding', 'Sustainability'],
            imageDescription: '地势坤，两个坤卦重叠，象征大地承载万物。',
            imageDescriptionEn: 'Earth upon Earth. Pure Yin virtue fills the land, sustaining all life with boundless tolerance and gentle strength.',
            generalMeaning: '处于需要柔顺配合、承载包容的阶段，适合辅助他人。',
            generalMeaningEn: 'A phase requiring gentleness and support. Success comes through following a leader and maintaining a receptive attitude.',
            scenes: {
                career: '事业上适合担当辅助角色，配合团队或领导。',
                relationship: '感情中以包容理解为主，顺应对方节奏。',
                wealth: '财运稳健，适合保守理财，不宜冒险。',
                health: '身体需要滋养调理，注意休息。',
                universal: '当下需柔顺配合，以包容态度应对万事。',
                transition: '从主导位置转向辅助配合的角色转换。'
            },
            scenesEn: {
                career: 'Thrive in supportive roles; quiet dedication yields rich rewards through collaboration.',
                relationship: 'Emphasize patience; adapting to your partner’s pace creates lasting harmony.',
                wealth: 'Stable financial phase; focus on saving rather than aggressive speculation.',
                health: 'Prioritize rest and nutrition; gentle internal recovery is needed.',
                universal: 'Practice yielding and tolerance; being receptive is your path to peace.',
                transition: 'A shift from active leading to a nurturing, supportive role.'
            }
        },
        reversed: {
            keywords: ['过于软弱', '丧失原则', '盲目顺从', '被动消极'],
            keywordsEn: ['Weakness', 'Indecision', 'Passivity', 'Stagnation', 'Lost Principles'],
            imageDescription: '坤德过柔，阴极无阳，大地松软无法承载。',
            imageDescriptionEn: 'Yin without balance. The earth becomes too soft to support anything, reflecting a lack of direction.',
            generalMeaning: '过于柔顺导致丧失原则，盲目顺从而迷失自我。',
            generalMeaningEn: 'Excessive passivity leads to loss of self. Being too yielding may result in being exploited or marginalized.',
            scenes: {
                career: '事业上过于被动，缺乏主见，可能被人利用。',
                relationship: '感情中一味迁就对方，失去自我。',
                wealth: '理财过于保守错失机会，或被人欺骗。',
                health: '身体过于虚弱，需适度增强体能。',
                universal: '需找回内在主见，柔顺不等于软弱无原则。',
                transition: '从顺从的状态转向找回自主性的调整。'
            },
            scenesEn: {
                career: 'Being too passive may lead to being overlooked or exploited in the workplace.',
                relationship: 'Losing your identity by over-accommodating; creates an unhealthy imbalance.',
                wealth: 'Missing opportunities due to fear; potential for being financially misled.',
                health: 'Low physical vitality; need for active strengthening and movement.',
                universal: 'Reclaim your inner core; gentleness must be balanced with firm principles.',
                transition: 'A warning of losing autonomy through extreme passivity.'
            }
        }
    },

    // 03 - 屯卦 (水雷屯)
    '003': {
        id: '003',
        name: 'Zhun (Difficulty at the Beginning)',
        nameCn: '屯卦',
        symbol: '☵☳',
        trigrams: { upper: '坎', lower: '震' },
        introduction: '象征草木破土萌芽的艰难时刻，万事开头难。',
        introductionEn: 'Zhun represents the initial struggle of growth. Like a sprout breaking through hard earth, it symbolizes the birth pains of new ventures.',
        upright: {
            keywords: ['创始艰难', '萌芽待发', '蓄势待机', '建立根基'],
            keywordsEn: ['Initial Struggle', 'Potential', 'Foundation', 'Patience', 'Budding'],
            imageDescription: '雷雨交加，种子破土，虽难但充满生机。',
            imageDescriptionEn: 'Clouds and Thunder. A sprout struggles to emerge; the process is difficult but the potential is enormous.',
            generalMeaning: '事物初创阶段，需耐心蓄势，不宜急进。',
            generalMeaningEn: 'A time of birth and beginning. Obstacles are natural now; focus on building a solid foundation rather than quick expansion.',
            scenes: {
                career: '事业刚起步，困难重重但充满潜力。',
                relationship: '感情处于萌芽阶段，需要耐心培养。',
                wealth: '财运处于积累期，收益不明显。',
                health: '身体遇到小状况，需调理打好基础。',
                universal: '万事开头难，耐心蓄势是关键。',
                transition: '从无到有的创始阶段，正向的开始。'
            },
            scenesEn: {
                career: 'A startup phase; face initial hurdles with persistence to build a lasting career.',
                relationship: 'Early romance; delicate feelings that require time and care to blossom.',
                wealth: 'Accumulation phase; returns aren\'t immediate but long-term seeds are being sown.',
                health: 'Pay attention to minor signs; build a healthy foundation for the future.',
                universal: 'Do not rush the dawn; persevere through the current "birth pains."',
                transition: 'The difficult but necessary transition from concept to reality.'
            }
        },
        reversed: {
            keywords: ['急于求成', '根基不稳', '时机未到', '夭折'],
            keywordsEn: ['Impatience', 'Stagnation', 'Poor Timing', 'Aborted Effort'],
            imageDescription: '种子破土受阻，雷雨过猛导致萌芽夭折。',
            imageDescriptionEn: 'The sprout is crushed by the storm; premature efforts lead to wasted energy and failure.',
            generalMeaning: '急于求成导致根基不稳，时机未到强行推进。',
            generalMeaningEn: 'Haste leads to failure. Attempting to force growth before the environment is ready causes the venture to fail.',
            scenes: {
                career: '起步受挫，方向不明或操之过急。',
                relationship: '感情操之过急，对方尚未准备好。',
                wealth: '投资时机不成熟，急于获利反而损失。',
                health: '问题反复，需要更耐心的调理。',
                universal: '放慢脚步，审视时机是否成熟。',
                transition: '创始遇阻的困难转折，需调整策略。'
            },
            scenesEn: {
                career: 'Career failure due to rushing; lack of preparation leads to an early exit.',
                relationship: 'Pushing for commitment too soon results in rejection or conflict.',
                wealth: 'Speculating prematurely; financial loss due to lack of a safety net.',
                health: 'Recurring symptoms; health struggles due to incomplete recovery.',
                universal: 'Retreat and reorganize; the current approach is leading to a dead end.',
                transition: 'A shift from difficult progress to frustrating stagnation.'
            }
        }
    },

    // 04 - 蒙卦 (山水蒙)
    '004': {
        id: '004',
        name: 'Meng (Youthful Folly)',
        nameCn: '蒙卦',
        symbol: '☶☵',
        trigrams: { upper: '艮', lower: '坎' },
        introduction: '象征蒙昧待启、学习成长的状态。',
        introductionEn: 'Meng represents the state of being uninformed or immature. It emphasizes the need for education and the correct attitude between student and teacher.',
        upright: {
            keywords: ['蒙昧待启', '求学求教', '虚心学习', '循序渐进'],
            keywordsEn: ['Ignorance', 'Learning', 'Inexperience', 'Mentorship', 'Curiosity'],
            imageDescription: '山下有泉，泉水初出，需要引导方成大河。',
            imageDescriptionEn: 'A spring gushing at the foot of a mountain. It is pure but directionless, requiring guidance to flow.',
            generalMeaning: '处于需要学习成长的阶段，启蒙开智终会通达。',
            generalMeaningEn: 'A time for guidance. Acknowledge your lack of knowledge and seek a mentor. Sincerity in learning brings success.',
            scenes: {
                career: '需学习新技能，虚心请教前辈会有收获。',
                relationship: '感情不够成熟，需要学习经营关系。',
                wealth: '财务知识欠缺，宜学习理财，不宜盲目。',
                health: '对健康了解不足，需学习养生方法。',
                universal: '学习成长的关键期，保持虚心。',
                transition: '从无知到求知的启蒙阶段。'
            },
            scenesEn: {
                career: 'Learning curve; apprentice stage; success through following expert advice.',
                relationship: 'Naivety in love; need for maturity and understanding of partner’s needs.',
                wealth: 'Financial inexperience; study the market before committing capital.',
                health: 'Ignorance of body signals; seek professional medical advice.',
                universal: 'Embrace the role of the student; wisdom starts with curiosity.',
                transition: 'A positive shift from confusion to the path of knowledge.'
            }
        },
        reversed: {
            keywords: ['顽固愚昧', '拒绝学习', '盲目自信', '不开窍'],
            keywordsEn: ['Stubbornness', 'Refusal to Learn', 'Arrogance', 'Failure of Education'],
            imageDescription: '泉水被山石堵塞，象征蒙昧无法开启。',
            imageDescriptionEn: 'Blocked energy. The spring is choked with rocks; arrogance prevents the acquisition of wisdom.',
            generalMeaning: '拒绝学习或过于顽固，错失成长机会。',
            generalMeaningEn: 'Willful ignorance. Doubting the teacher or clinging to folly leads to stagnation and continued error.',
            scenes: {
                career: '不愿学习新事物而停滞不前。',
                relationship: '固执己见，不愿听取意见，冲突难解。',
                wealth: '理财态度顽固，可能导致损失。',
                health: '不重视健康知识，不良习惯难改。',
                universal: '需放下傲慢接受学习，否则持续迷茫。',
                transition: '从蒙昧陷入更深的迷失。'
            },
            scenesEn: {
                career: 'Career stagnation due to ego; ignoring feedback leading to repeated mistakes.',
                relationship: 'Stubborn refusal to grow together; repetitive toxic patterns.',
                wealth: 'Financial folly; losing money by following outdated or biased ideas.',
                health: 'Neglecting doctors\' advice; sticking to harmful habits despite warnings.',
                universal: 'Arrogance is your greatest enemy; you are blind to your own folly.',
                transition: 'A shift from potential growth to willful stagnation.'
            }
        }
    },

    // 05 - 需卦 (水天需)
    '005': {
        id: '005',
        name: 'Xu (Waiting)',
        nameCn: '需卦',
        symbol: '☵☰',
        trigrams: { upper: '坎', lower: '乾' },
        introduction: '象征云雨在天，待时而降。',
        introductionEn: 'Xu represents waiting with confidence. It suggests that strength exists, but obstacles (water) lie ahead, requiring one to wait for the right moment.',
        upright: {
            keywords: ['耐心等待', '时机未到', '蓄势待发', '养精蓄锐'],
            keywordsEn: ['Patience', 'Waiting', 'Confidence', 'Preparation', 'Foresight'],
            imageDescription: '云雨积聚天上，刚健之德在下等待。',
            imageDescriptionEn: 'Clouds rising to the heavens. It will rain eventually; for now, one eats, drinks, and waits in peace.',
            generalMeaning: '时机未成熟，应养精蓄锐、静待天时。',
            generalMeaningEn: 'The time is not yet ripe for action. Use this delay to nourish yourself and gather strength. Trust that the rain will fall.',
            scenes: {
                career: '时机未到，需耐心等待机会，急躁有害。',
                relationship: '感情需时间培养，不宜催促，顺其自然。',
                wealth: '投资时机未到，等待入场点，保持观望。',
                health: '身体需调养恢复时间，不要急于求成。',
                universal: '耐心等待，保持信心，时机成熟自有结果。',
                transition: '从行动阶段进入等待蓄势的调整期。'
            },
            scenesEn: {
                career: 'Market timing; wait for the best window to launch; avoid hasty deals.',
                relationship: 'Allow the connection to grow naturally; don\'t force a commitment.',
                wealth: 'Strategic patience; keep your capital ready for the coming opportunity.',
                health: 'Slow recovery is still progress; allow nature to heal the body.',
                universal: 'Wait with joy and confidence; your time is coming.',
                transition: 'A strategic shift from striving to active patience.'
            }
        },
        reversed: {
            keywords: ['焦躁不安', '时机错失', '消极拖延', '放弃希望'],
            keywordsEn: ['Impatience', 'Haste', 'Missed Opportunity', 'Anxiety', 'Panic'],
            imageDescription: '云雨迟迟不降，等待变成煎熬，或错失时机。',
            imageDescriptionEn: 'Waiting in vain or acting too soon. The rain falls while you are unprepared, or you run into danger blindly.',
            generalMeaning: '失去耐心导致判断失误，或消极等待。',
            generalMeaningEn: 'Wasted waiting. Fear or restlessness leads to premature action that results in running into the very danger you were avoiding.',
            scenes: {
                career: '因焦躁而做出错误决定，或错失机会。',
                relationship: '因缺乏耐心破坏关系，或无尽等待无果。',
                wealth: '仓促投资，或过度保守错失良机。',
                health: '调养期缺乏耐心，影响恢复效果。',
                universal: '审视等待意义，避免冲动决定。',
                transition: '从安心等待转向焦虑或放弃。'
            },
            scenesEn: {
                career: 'Impulsive resignation or signing bad contracts due to fear of missing out.',
                relationship: 'Ruining a bond through insecurity; acting out of desperate loneliness.',
                wealth: 'Panic selling or late entry into investments; losing out due to jitters.',
                health: 'Trying too many aggressive cures too fast; health setbacks.',
                universal: 'Calm your mind; your anxiety is creating unnecessary trouble.',
                transition: 'A shift from poised waiting to chaotic desperation.'
            }
        }
    },

    // 06 - 讼卦 (天水讼)
    '006': {
        id: '006',
        name: 'Song (Conflict)',
        nameCn: '讼卦',
        symbol: '☰☵',
        trigrams: { upper: '乾', lower: '坎' },
        introduction: '象征立场冲突、争执难免，需适可而止。',
        introductionEn: 'Song represents conflict and litigation. It warns that even if you are right, pursuing a dispute to the bitter end will bring misfortune.',
        upright: {
            keywords: ['争讼冲突', '意见分歧', '适可而止', '理性解决'],
            keywordsEn: ['Conflict', 'Dispute', 'Litigation', 'Compromise', 'Mediation'],
            imageDescription: '天向上行、水向下流，方向相背，象征分歧。',
            imageDescriptionEn: 'Heaven moves up, Water moves down. They pull apart, creating friction and legal entanglement.',
            generalMeaning: '存在争议的阶段，需理性处理分歧，中吉终凶。',
            generalMeaningEn: 'You are in a disagreement. Seek mediation early. Winning the argument might cause a greater loss in the long run.',
            scenes: {
                career: '遇到合作分歧或竞争冲突，需妥善处理。',
                relationship: '意见不一，需沟通协商，避免激烈争吵。',
                wealth: '财务可能有纠纷，需要法律或中间人调解。',
                health: '因冲突而紧张焦虑，需学会放松调整。',
                universal: '保持理性、适可而止，寻求中庸之道。',
                transition: '从和平状态进入争议期，需智慧化解。'
            },
            scenesEn: {
                career: 'Contract disputes; workplace rivalry; settle out of court if possible.',
                relationship: 'Heated arguments; differing life goals; need for an honest third-party view.',
                wealth: 'Legal battles over assets; money disagreements with partners.',
                health: 'Stress-induced ailments; internal conflict affecting wellness.',
                universal: 'Stop the fight early; a compromise is a victory here.',
                transition: 'A shift from peace into a period of challenge and debate.'
            }
        },
        reversed: {
            keywords: ['两败俱伤', '纠缠不休', '冲突激化', '官司缠身'],
            keywordsEn: ['Endless Feud', 'Total Loss', 'Stubbornness', 'Legal Failure'],
            imageDescription: '天水背离愈甚，冲突失控，陷入消耗战。',
            imageDescriptionEn: 'Drowning in legalities. The conflict escalates beyond reason, leading to exhaustion of resources and spirit.',
            generalMeaning: '争讼过度导致两败俱伤，陷入消耗。',
            generalMeaningEn: 'Refusing to settle. Your stubbornness is causing a collapse. It is better to admit defeat or walk away than to lose everything.',
            scenes: {
                career: '事业因过度争执受损，可能卷入官司。',
                relationship: '感情因持续争吵破裂，双方都受伤害。',
                wealth: '财务因官司或纠纷而大量消耗。',
                health: '身心因持续冲突而严重受损。',
                universal: '立即止损，放下争执，纠缠只会更糟。',
                transition: '从可控争议转向失控冲突的恶化。'
            },
            scenesEn: {
                career: 'Losing a lawsuit; professional ruin through bitter competition; toxic environment.',
                relationship: 'Ugly divorce or breakup; permanent resentment; refusal to forgive.',
                wealth: 'Bankruptcy through legal fees; total loss of disputed funds.',
                health: 'Severe nervous breakdown; chronic illness from long-term stress.',
                universal: 'Drop the case; the cost of "winning" is far too high.',
                transition: 'A shift from disagreement to total destruction of rapport.'
            }
        }
    },

    // 07 - 师卦 (地水师)
    '007': {
        id: '007',
        name: 'Shi (The Army)',
        nameCn: '师卦',
        symbol: '☷☵',
        trigrams: { upper: '坤', lower: '坎' },
        introduction: '象征组织领导、集体力量与纪律。',
        introductionEn: 'Shi represents the military and organized force. It signifies the need for discipline, a clear cause, and an experienced leader to manage mass energy.',
        upright: {
            keywords: ['组织领导', '纪律秩序', '集体力量', '战略'],
            keywordsEn: ['Discipline', 'Leadership', 'Group Power', 'Strategy', 'Mission'],
            imageDescription: '水蕴于地中，象征军队蓄势，统一指挥。',
            imageDescriptionEn: 'Water hidden in the Earth. It represents the potential power of the masses under firm, responsible leadership.',
            generalMeaning: '发挥集体力量的阶段，以纪律为本方能成功。',
            generalMeaningEn: 'A time for organized action. Success depends on following a proven leader and maintaining strict order within your "ranks."',
            scenes: {
                career: '适合担当领导组织角色，以身作则。',
                relationship: '需一方承担引导责任，建立秩序。',
                wealth: '财务需系统规划，有序管理才能稳健。',
                health: '需有纪律的规划，系统调理和锻炼。',
                universal: '发挥领导力，以团队之力应对挑战。',
                transition: '从分散状态转向有组织的正向整合。'
            },
            scenesEn: {
                career: 'Team management; leading a project; strict adherence to company policy.',
                relationship: 'Establishing boundaries; taking responsibility for the relationship\'s direction.',
                wealth: 'Systematic financial planning; disciplined saving and budgeting.',
                health: 'A rigorous fitness or medical regimen; systematic recovery.',
                universal: 'Mobilize your resources; you need a strategy to win.',
                transition: 'A shift from chaos to organized strength.'
            }
        },
        reversed: {
            keywords: ['组织混乱', '内部分裂', '失去控制', '滥用权力'],
            keywordsEn: ['Disorganization', 'Failure of Command', 'Mutiny', 'Inefficiency'],
            imageDescription: '水在地上泛滥失控，领导失能，组织混乱。',
            imageDescriptionEn: 'The army in retreat. Lack of clear orders leads to confusion and defeat. The leader has lost authority.',
            generalMeaning: '领导无能或组织混乱，可能权力被滥用。',
            generalMeaningEn: 'Conflict within the team. Without a clear leader or cause, your energy is scattered and the project will likely fail.',
            scenes: {
                career: '团队管理混乱，缺乏有效领导，项目失败。',
                relationship: '缺乏明确方向与责任划分，陷入混乱。',
                wealth: '管理无序，支出失控，造成损失。',
                health: '管理无纪律，计划难执行，效果不佳。',
                universal: '反思领导方式，重建秩序，避免混乱。',
                transition: '从有序状态转向混乱失控的负面转折。'
            },
            scenesEn: {
                career: 'Micro-management; internal sabotage; failure of leadership leading to loss.',
                relationship: 'Chaotic home life; power struggles; lack of mutual respect.',
                wealth: 'Financial leaks; disorganized accounts; loss through bad leadership.',
                health: 'Lack of self-control; skipping treatments; physical disorder.',
                universal: 'The group is falling apart; restore discipline or leave.',
                transition: 'A shift from unity to internal collapse.'
            }
        }
    },

    // 08 - 比卦 (水地比)
    '008': {
        id: '008',
        name: 'Bi (Holding Together)',
        nameCn: '比卦',
        symbol: '☵☷',
        trigrams: { upper: '坎', lower: '坤' },
        introduction: '象征亲密联合、众人归附与诚信。',
        introductionEn: 'Bi represents union and solidarity. It is about the power of friendship, alliances, and finding your place within a supportive group.',
        upright: {
            keywords: ['亲密联合', '团结协作', '真诚信任', '联盟'],
            keywordsEn: ['Union', 'Alliance', 'Friendship', 'Harmony', 'Belonging'],
            imageDescription: '水汇聚于地上，万流归海，众人归心。',
            imageDescriptionEn: 'Water on the Earth. All small streams flow together to form a lake, symbolizing mutual attraction and unity.',
            generalMeaning: '需要联合他人、融入集体，共同发展。',
            generalMeaningEn: 'A time for partnership. Seek out those with similar goals. Sincerity and consistency will cement your bonds.',
            scenes: {
                career: '适合加入团队或结盟，辅助他人。',
                relationship: '亲密和谐，彼此信任，稳固发展。',
                wealth: '适合合伙投资或信任的人共同理财。',
                health: '可寻求他人帮助共同改善健康。',
                universal: '建立联盟的好时机，真诚相待。',
                transition: '从孤立状态转向融入集体的正向转折。'
            },
            scenesEn: {
                career: 'Networking; finding a supportive work culture; joint ventures.',
                relationship: 'Deep intimacy; choosing the right partner; a harmonious social life.',
                wealth: 'Prospering through partnerships; shared financial interests.',
                health: 'Group therapy; finding a workout buddy; social well-being.',
                universal: 'Join with others; strength lies in your connections.',
                transition: 'A positive shift from isolation to togetherness.'
            }
        },
        reversed: {
            keywords: ['关系破裂', '信任缺失', '孤立无援', '背叛'],
            keywordsEn: ['Exclusion', 'Broken Trust', 'Betrayal', 'Loneliness', 'Rejection'],
            imageDescription: '水在地上泛滥或干涸，象征联合失败。',
            imageDescriptionEn: 'Dried up water. People are drifting apart; the bond is superficial or based on false pretenses.',
            generalMeaning: '联合失败或关系破裂，因信任缺失孤立。',
            generalMeaningEn: 'The wrong association. You may be trying to join a group where you don\'t belong, or your partners are not trustworthy.',
            scenes: {
                career: '团队关系破裂，合作失败，被排挤。',
                relationship: '出现信任危机，陷入疏远或背叛。',
                wealth: '合伙投资出问题，被合作方欺骗。',
                health: '缺乏支持系统，独自面对健康问题。',
                universal: '审视关系真诚度，修复破裂信任。',
                transition: '从紧密联合转向关系破裂的负面转折。'
            },
            scenesEn: {
                career: 'Being ostracized; backstabbing partners; failed networking.',
                relationship: 'Feeling like an outsider in your own relationship; betrayal; alienation.',
                wealth: 'Financial disputes with partners; being left out of a deal.',
                health: 'Loneliness affecting health; lack of a support network.',
                universal: 'Check your allies; you may be standing with the wrong people.',
                transition: 'A shift from unity to painful isolation.'
            }
        }
    },

    // 09 - 小畜卦 (风天小畜)
    '009': {
        id: '009',
        name: 'Xiao Xu (Small Accumulating)',
        nameCn: '小畜卦',
        symbol: '☴☰',
        trigrams: { upper: '巽', lower: '乾' },
        introduction: '象征小有积蓄但时机未到，需继续积累。',
        introductionEn: 'Xiao Xu represents small restraints or minor gains. It suggests that while progress is happening, major breakthroughs are still being held back.',
        upright: {
            keywords: ['小有积蓄', '时机渐近', '继续积累', '准备'],
            keywordsEn: ['Small Progress', 'Gentle Restraint', 'Shadows', 'Persuasion', 'Patience'],
            imageDescription: '风行天上，密云将雨未雨，时机尚差一步。',
            imageDescriptionEn: 'The wind blows high in the sky. Dense clouds gather but no rain falls yet. The force is gentle but persistent.',
            generalMeaning: '处于小有积蓄但未大成，需继续准备。',
            generalMeaningEn: 'A time of minor success. You have some influence, but not enough to force a major change. Use gentle persuasion instead of power.',
            scenes: {
                career: '有进展但尚未突破，需积累能力资源。',
                relationship: '在培养中，有好感但未确定，需投入。',
                wealth: '小有积蓄，不足以大投资，继续储蓄。',
                health: '逐步恢复中，未完全康复，需调养。',
                universal: '积蓄力量关键期，保持耐心准备。',
                transition: '从准备阶段逐渐接近时机成熟。'
            },
            scenesEn: {
                career: 'Incremental gains; wait for a bigger opportunity; refine your skills.',
                relationship: 'Small improvements in communication; sweet but slow development.',
                wealth: 'Minor financial wins; good time to save small amounts consistently.',
                health: 'Slow but steady recovery; need for fresh air and gentle movement.',
                universal: 'Focus on the small things; they are the building blocks of greatness.',
                transition: 'A shift from total stillness to small, steady gains.'
            }
        },
        reversed: {
            keywords: ['积蓄不足', '急于突破', '准备不充分', '受阻'],
            keywordsEn: ['Stalled', 'Rainfall (Conflict)', 'Ineffective', 'Out of Control'],
            imageDescription: '密云散去，雨未能降，积蓄不足。',
            imageDescriptionEn: 'The clouds break without rain. The small restraint fails, leading to potential conflict or a loss of what was gathered.',
            generalMeaning: '积蓄不足就急于行动，导致努力受阻。',
            generalMeaningEn: 'Internal tension breaks out. You are unable to keep the peace or sustain your small gains due to a lack of deeper foundation.',
            scenes: {
                career: '准备不足就仓促行动，导致失败受挫。',
                relationship: '感情操之过急，对方尚未准备好。',
                wealth: '资金不足就冒然投资，可能损失。',
                health: '身体尚未恢复就过度活动，导致反复。',
                universal: '审视准备是否充分，切勿急躁。',
                transition: '从积蓄阶段受挫或倒退的负面转折。'
            },
            scenesEn: {
                career: 'A project falling apart at the last minute; ineffective management of resources.',
                relationship: 'Old arguments resurfacing; losing the small ground you gained in love.',
                wealth: 'Dipping into savings; losing money through impatience.',
                health: 'Relapse into illness; nervous tension causing exhaustion.',
                universal: 'The "rain" has come; face the storm you couldn\'t prevent.',
                transition: 'A shift from careful planning to sudden messy outcomes.'
            }
        }
    },

    // 10 - 履卦 (天泽履)
    '010': {
        id: '010',
        name: 'Lü (Treading)',
        nameCn: '履卦',
        symbol: '☰☱',
        trigrams: { upper: '乾', lower: '兑' },
        introduction: '象征小心谨慎地行事，如履虎尾。',
        introductionEn: 'Lü means treading or conduct. It teaches how to move through a dangerous world by following proper etiquette and maintaining caution.',
        upright: {
            keywords: ['谨慎行事', '循礼守规', '小心翼翼', '自律'],
            keywordsEn: ['Caution', 'Etiquette', 'Conduct', 'Danger', 'Vigilance'],
            imageDescription: '天高泽低，各安其位，如履虎尾不咥人。',
            imageDescriptionEn: 'Treading on the tail of a tiger. Through correct behavior and respect for power, one remains unhurt.',
            generalMeaning: '如履薄冰，只要得当守礼就能安然度过。',
            generalMeaningEn: 'A high-stakes situation. Success depends on your behavior. Be polite, follow the rules, and show respect to those in power.',
            scenes: {
                career: '需谨慎行事，遵守规则，处理敏感事务。',
                relationship: '尊重边界，循序渐进，不可越矩。',
                wealth: '财务决策需谨慎，不可冒险，稳健上。',
                health: '需小心维护，注意危险因素，预防。',
                universal: '当下如履薄冰，谨慎行事方安全。',
                transition: '进入需要小心行事的谨慎阶段。'
            },
            scenesEn: {
                career: 'Navigating office politics; dealing with a powerful boss; strict adherence to law.',
                relationship: 'Walking on eggshells; need for extreme tact; respect for a partner’s limits.',
                wealth: 'Handling risky investments with precision; very careful budgeting.',
                health: 'Preventative care; avoiding high-risk physical activities.',
                universal: 'Proper conduct is your shield; do not be arrogant.',
                transition: 'A shift from ease into a situation requiring total alertness.'
            }
        },
        reversed: {
            keywords: ['鲁莽冒失', '越礼妄为', '危险临近', '不知进退'],
            keywordsEn: ['Recklessness', 'Disaster', 'Insult', 'Misstep', 'Arrogance'],
            imageDescription: '踩中虎尾被咬，鲁莽冒失而遭受伤害。',
            imageDescriptionEn: 'The tiger bites. Arrogance or a breach of conduct leads to a painful and direct consequence.',
            generalMeaning: '越礼妄为导致触怒他人或陷入危险。',
            generalMeaningEn: 'A miscalculation. You have offended someone powerful or ignored a danger, and now you are facing the fallout.',
            scenes: {
                career: '因冒失或不守规则惹祸，可能得罪人。',
                relationship: '因不尊重边界伤害对方或被拒绝。',
                wealth: '财务因冒险决策而遭受损失。',
                health: '因疏忽大意而导致健康问题。',
                universal: '反思行为，改正鲁莽，避免损失。',
                transition: '因不慎重而陷入危险的负面转折。'
            },
            scenesEn: {
                career: 'Getting fired for unprofessionalism; offending a client; public scandal.',
                relationship: 'A relationship ending due to lack of respect or boundary-crossing.',
                wealth: 'Losing money through sheer arrogance; getting "bitten" by a bad deal.',
                health: 'Accidents due to carelessness; ignoring vital health warnings.',
                universal: 'You stepped on the tiger; now you must deal with the anger.',
                transition: 'A shift from safe treading to a direct hit of misfortune.'
            }
        }
    },
    // 11 - 泰卦 (地天泰)
    '011': {
        id: '011',
        name: 'Tai (Peace)',
        nameCn: '泰卦',
        symbol: '☷☰',
        trigrams: { upper: '坤', lower: '乾' },
        introduction: '天地交泰，阴阳和谐。象征万物通达，进入和平繁荣的黄金时期。',
        introductionEn: 'Tai represents total harmony. Heaven (Yang) is below Earth (Yin), so their energies meet and intermingle. It signifies a golden age of peace, balance, and unobstructed flow.',
        upright: {
            keywords: ['天地交泰', '阴阳和谐', '通达顺畅', '繁荣', '吉祥'],
            keywordsEn: ['Harmony', 'Peace', 'Prosperity', 'Success', 'Balance', 'Union'],
            imageDescription: '乾阳上升、坤阴下降，二气交感，万物欣欣向荣。',
            imageDescriptionEn: 'The breath of heaven and earth meets in perfect unity. All life flourishes under this balanced energy.',
            generalMeaning: '处于最顺畅和谐的阶段，天时地利人和，万事通达。',
            generalMeaningEn: 'A peak period of good fortune. Everything is in its right place, and progress happens naturally without resistance.',
            scenes: {
                career: '事业极顺，付出有丰厚回报，贵人相助，前途光明。',
                relationship: '感情美满和谐，相互理解，是最佳婚恋状态。',
                wealth: '财运极佳，收益丰厚，投资顺利，财务繁荣。',
                health: '身心健康，阴阳平衡，精力充沛，状态极佳。',
                universal: '最顺遂的时期，珍惜把握，同时居安思危。',
                transition: '从困难阶段进入顺遂通达的极大正向转折。'
            },
            scenesEn: {
                career: 'Exceptional career success; powerful support from others; major goals reached with ease.',
                relationship: 'Perfect emotional resonance; mutual growth; a time of profound love and happiness.',
                wealth: 'Financial abundance; profitable investments; all resources flowing smoothly.',
                health: 'Peak vitality; excellent mental and physical balance; rapid healing.',
                universal: 'Enjoy the golden moment; the universe is in total alignment with you.',
                transition: 'A magnificent breakthrough from struggle to universal success.'
            }
        },
        reversed: {
            keywords: ['否极泰来', '盛极而衰', '和谐破裂', '失去平衡'],
            keywordsEn: ['Declining Fortune', 'Instability', 'Discord', 'Fragile Peace', 'Transition'],
            imageDescription: '天地分离，阴阳不再交感，繁荣背后暗藏危机。',
            imageDescriptionEn: 'The energies begin to pull apart. The period of peak harmony is coming to an end, requiring vigilance.',
            generalMeaning: '盛极而衰的警示，和谐状态可能被打破，需居安思危。',
            generalMeaningEn: 'A warning of fading luck. The peace is fragile; arrogance or neglect could lead to a sudden downfall.',
            scenes: {
                career: '事业巅峰后可能出现调整，需防范潜在风险。',
                relationship: '感情美满后可能出现小矛盾，需用心维护。',
                wealth: '财务繁荣期后需谨慎，避免过度消费。',
                health: '健康状态极佳时更需保养，防止骄纵。',
                universal: '虽顺遂但需防范变化，为调整期做准备。',
                transition: '从通泰顺遂可能转向调整期的预警转折。'
            },
            scenesEn: {
                career: 'The honeymoon phase ends; hidden issues arise in projects; need to guard achievements.',
                relationship: 'Small cracks appear in harmony; do not take your partner for granted.',
                wealth: 'Income plateaus; watch for hidden expenses; curb impulsive luxury spending.',
                health: 'Minor symptoms reappear; do not neglect your wellness routine just because you feel good.',
                universal: 'The cycle is turning; stay humble to maintain your position.',
                transition: 'A subtle shift from peak stability toward necessary re-adjustment.'
            }
        }
    },

    // 12 - 否卦 (天地否)
    '012': {
        id: '012',
        name: 'Pi (Standstill)',
        nameCn: '否卦',
        symbol: '☰☷',
        trigrams: { upper: '乾', lower: '坤' },
        introduction: '天地不交，阴阳隔绝。象征闭塞不通、人际疏离的困难时期。',
        introductionEn: 'Pi represents a state of stagnation. Heaven (Yang) moves up and Earth (Yin) moves down, meaning they never meet. It signifies a breakdown in communication and a time when small people prevail.',
        upright: {
            keywords: ['闭塞不通', '阴阳隔绝', '困顿时期', '坚守正道'],
            keywordsEn: ['Stagnation', 'Blockage', 'Disconnect', 'Adversity', 'Isolation', 'Silent'],
            imageDescription: '阳气上浮、阴气下沉，上下不通，万物枯落。',
            imageDescriptionEn: 'Heaven and Earth drift apart. Communication ceases, and the world becomes a cold, barren place.',
            generalMeaning: '处于闭塞困顿的阶段，事事不顺，需退守正道待时转运。',
            generalMeaningEn: 'Progress is impossible right now. It is a time to withdraw into your principles. Do not attempt to force results in a closed environment.',
            scenes: {
                career: '事业陷入困境，进展受阻，暂时收敛，等待时机。',
                relationship: '感情沟通不畅，存在隔阂，需耐心，不宜强求。',
                wealth: '财运低迷，收益减少，需保守理财。',
                health: '身体状况下滑，需要调养，不宜勉强。',
                universal: '困顿时期，坚守正道，养精蓄锐，否极泰来。',
                transition: '进入困顿闭塞的逆境阶段。'
            },
            scenesEn: {
                career: 'Project blockages; lack of support from management; feeling silenced or ignored.',
                relationship: 'Emotional distance; wall of silence between partners; temporary separation.',
                wealth: 'Financial drought; assets are frozen or unreachable; minimize spending.',
                health: 'Internal blockages; low energy; feeling "stuck" in a cycle of illness.',
                universal: 'Hold on to your integrity; the world is not listening, so listen to yourself.',
                transition: 'A sudden entry into a period of temporary isolation and difficulty.'
            }
        },
        reversed: {
            keywords: ['否极泰来', '转机出现', '困境将解', '曙光初现'],
            keywordsEn: ['Reopening', 'End of Stagnation', 'New Hope', 'Connection', 'Breakthrough'],
            imageDescription: '闭塞之象渐解，阴阳开始流通，转机已在眼前。',
            imageDescriptionEn: 'The barrier breaks. Yin and Yang begin to communicate again, and light returns to the world.',
            generalMeaning: '困境将要结束，转机已经出现，坚持正道终见曙光。',
            generalMeaningEn: 'The standstill is ending. People are starting to listen again, and frozen situations are beginning to thaw. Success is returning.',
            scenes: {
                career: '困境出现转机，机会渐渐浮现，把握时机。',
                relationship: '隔阂有望化解，沟通逐渐顺畅。',
                wealth: '财运开始回升，困顿期即将过去。',
                health: '身体逐渐恢复，健康状况改善中。',
                universal: '困境正在解除，保持信心，好运将至。',
                transition: '从否闭状态开始转向通泰的正向转折。'
            },
            scenesEn: {
                career: 'Barriers being removed; sudden resolution of an old deadlock; new leadership.',
                relationship: 'Healing a rift; the first honest conversation in a long time; reconciliation.',
                wealth: 'Flow of cash returns; finding new ways to generate income.',
                health: 'Energy blockages clearing; finding a treatment that actually works.',
                universal: 'The long winter is over; step back into the world with confidence.',
                transition: 'A positive shift from isolation back into the flow of life.'
            }
        }
    },

    // 13 - 同人卦 (天火同人)
    '013': {
        id: '013',
        name: 'Tong Ren (Fellowship)',
        nameCn: '同人卦',
        symbol: '☰☲',
        trigrams: { upper: '乾', lower: '离' },
        introduction: '志同道合之人聚集，象征真诚团结、公正无私的团体精神。',
        introductionEn: 'Tong Ren represents fellowship among people. It signifies the union of hearts and minds toward a common, noble goal, emphasizing universal brotherhood and openness.',
        upright: {
            keywords: ['志同道合', '团结协作', '公正无私', '联盟'],
            keywordsEn: ['Fellowship', 'Community', 'Universal Goals', 'Unity', 'Cooperation', 'Openness'],
            imageDescription: '火与天同向上升，众人心志相同，团结一致向上。',
            imageDescriptionEn: 'Fire burning up toward Heaven. Like minds joined in high purpose, working together without secret agendas.',
            generalMeaning: '处于与志同道合之人合作的阶段，集体力量能成大事。',
            generalMeaningEn: 'A time for teamwork. Seek success through collaboration and public efforts. Honesty and transparency are essential for maintaining unity.',
            scenes: {
                career: '事业上遇到志同道合伙伴，团队协作顺畅。',
                relationship: '彼此理解，心意相通，关系和谐美好。',
                wealth: '适合合伙投资，团体力量带来更大收益。',
                health: '可加入健康社群，与志同道合的人一起养生。',
                universal: '团结合作的好时机，与志同道合之人共同努力。',
                transition: '从孤独奋斗转向团队合作的正向转折。'
            },
            scenesEn: {
                career: 'Great team synergy; finding the right business partners; success through public projects.',
                relationship: 'Deep spiritual affinity; a relationship based on shared values and social activities.',
                wealth: 'Wealth through crowd-funding or partnerships; collective prosperity.',
                health: 'Healing in a community setting; group fitness or collective wellness.',
                universal: 'Find your tribe; you are stronger when you join with others.',
                transition: 'A positive shift from individual effort to collective power.'
            }
        },
        reversed: {
            keywords: ['内部分裂', '意见不合', '同床异梦', '不和谐'],
            keywordsEn: ['Exclusivity', 'Clique', 'Internal Conflict', 'Divided Hearts', 'Selfishness'],
            imageDescription: '火与天离心，表面同心实则分崩离析。',
            imageDescriptionEn: 'The fire flickers and the sky darkens. People are working together for the wrong reasons, leading to cliques.',
            generalMeaning: '团队内部出现分歧，因私心或意见不合导致分裂。',
            generalMeaningEn: 'A warning against secret motives. If the group is built on narrow self-interest rather than universal truth, it will fall apart.',
            scenes: {
                career: '团队合作出现裂痕，意见分歧，需要沟通。',
                relationship: '表面和谐实则有隔阂，需坦诚化解。',
                wealth: '合伙投资出现分歧，利益分配不均。',
                health: '健康社群松散，缺乏真正的支持。',
                universal: '需化解团队分歧，回归公正无私的初心。',
                transition: '从团结状态转向分裂的负面转折。'
            },
            scenesEn: {
                career: 'Workplace politics; hidden agendas destroying a team; failed partnerships.',
                relationship: 'Feeling lonely despite being together; separate goals; lack of transparency.',
                wealth: 'Arguments over profits; legal issues with partners; unfair distribution.',
                health: 'Conflicting medical advice; lack of social support in recovery.',
                universal: 'Look for the hidden rift; you cannot move forward with a divided heart.',
                transition: 'A negative shift from unity toward fragmentation.'
            }
        }
    },

    // 14 - 大有卦 (火天大有)
    '014': {
        id: '014',
        name: 'Da You (Great Possession)',
        nameCn: '大有卦',
        symbol: '☲☰',
        trigrams: { upper: '离', lower: '乾' },
        introduction: '火在天上，光照万物。象征盛大丰收与物质、精神的双重富足。',
        introductionEn: 'Da You represents "Great Possession" or abundance. Fire is in the sky, illuminating all things. It signifies a time of great success, wealth, and having the power to do good.',
        upright: {
            keywords: ['大有收获', '丰盛富足', '光明普照', '成功'],
            keywordsEn: ['Abundance', 'Grand Success', 'Wealth', 'Influence', 'Radiance', 'Generosity'],
            imageDescription: '太阳高悬，光明普照四方，万物繁荣生长。',
            imageDescriptionEn: 'The sun shines brilliantly from the zenith. All creatures prosper under its warmth and light.',
            generalMeaning: '处于大有收获的丰盛阶段，事业成功、财富充裕。',
            generalMeaningEn: 'You are in a position of strength and wealth. Use this abundance to help others and lead with kindness. Your influence is at its peak.',
            scenes: {
                career: '事业大有成就，获认可，贵人相助。',
                relationship: '感情美满丰富，彼此珍惜，幸福满溢。',
                wealth: '财运极佳，收益丰厚，财富积累可观。',
                health: '身体健康充满活力，精神饱满。',
                universal: '丰收的时期，珍惜所有，同时保持谦逊。',
                transition: '进入丰盛富足的巅峰阶段。'
            },
            scenesEn: {
                career: 'Major career breakthrough; leading a large organization; extreme professional respect.',
                relationship: 'A rich, fulfilling emotional life; a partner who brings abundance; radiant love.',
                wealth: 'Peak financial period; successful scaling of assets; large-scale prosperity.',
                health: 'Radiant health; overflowing energy; total physical well-being.',
                universal: 'You have much to give; let your light shine and be generous.',
                transition: 'A peak shift from simple progress into grand realization.'
            }
        },
        reversed: {
            keywords: ['财不露白', '骄傲自满', '失去光芒', '过度'],
            keywordsEn: ['Arrogance', 'Ostentation', 'Loss of Assets', 'Prideful Fall', 'Excess'],
            imageDescription: '光芒过盛灼伤万物，或因骄傲而失去照耀。',
            imageDescriptionEn: 'The sun is too hot, scorching the earth, or setting into darkness due to neglect.',
            generalMeaning: '丰盛后可能因骄傲或挥霍而失去，需节制。',
            generalMeaningEn: 'A warning against arrogance. If you show off your wealth or become prideful, your "possessions" will quickly dwindle or bring trouble.',
            scenes: {
                career: '成就感可能导致骄傲自满，引发问题。',
                relationship: '感情中因满足而疏忽，需持续经营。',
                wealth: '财富可能因挥霍或管理不善而减少。',
                health: '健康可能因疏忽或放纵而出问题。',
                universal: '需保持谦逊节制，避骄败业。',
                transition: '从丰盛可能转向衰落的警示转折。'
            },
            scenesEn: {
                career: 'Ego getting in the way of success; professional burnout from trying to be "too big."',
                relationship: 'Taking a partner for granted; relationship strain due to focusing only on status.',
                wealth: 'Financial waste to maintain an image; over-leveraging; greed leading to loss.',
                health: 'Illness from luxury or over-indulgence; high blood pressure; stress.',
                universal: 'Success is a responsibility, not just a reward; re-center your humility.',
                transition: 'A warning shift from mastery toward potential loss.'
            }
        }
    },

    // 15 - 谦卦 (地山谦)
    '015': {
        id: '015',
        name: 'Qian (Modesty)',
        nameCn: '谦卦',
        symbol: '☷☶',
        trigrams: { upper: '坤', lower: '艮' },
        introduction: '高山隐于地下，象征真正的强大不需要张扬。易经中最圆满的卦。',
        introductionEn: 'Qian represents Modesty. The mountain (highest point) is hidden under the Earth (lowest point). It teaches that true greatness is humble and stays low to the ground to achieve completion.',
        upright: {
            keywords: ['谦虚内敛', '以退为进', '厚德载物', '尊重'],
            keywordsEn: ['Modesty', 'Humility', 'Internal Strength', 'Grace', 'Integrity', 'Success'],
            imageDescription: '高山甘居地下，内蕴深厚，外表谦逊。',
            imageDescriptionEn: 'The massive mountain bows below the soil. It is a sign of immense inner value hidden by a quiet exterior.',
            generalMeaning: '处于需要谦虚的阶段，谦逊的态度能获得更大成功。',
            generalMeaningEn: 'The path is open. By remaining humble and not boasting of your achievements, you gain the respect of all and ensure a happy end.',
            scenes: {
                career: '保持谦虚，虚心学习，以低调赢得尊重。',
                relationship: '放低姿态，尊重对方，关系更加和谐。',
                wealth: '不炫富不张扬，稳健低调更安全。',
                health: '需要内在调养，不过度追求外在表现。',
                universal: '以谦虚态度行事，谦受益满招损。',
                transition: '从张扬状态转向内敛谦虚的正向调整。'
            },
            scenesEn: {
                career: 'Respected leadership; quiet excellence; gaining trust through hard work without bragging.',
                relationship: 'A gentle, selfless bond; putting the partner\'s needs first; harmony.',
                wealth: 'Managing money without ostentation; long-term stable security.',
                health: 'Holistic well-being; internal peace; recovery through patience.',
                universal: 'Be like water or the mountain under earth; your humility is your power.',
                transition: 'A virtuous shift from self-importance to true wisdom.'
            }
        },
        reversed: {
            keywords: ['虚假谦虚', '自卑', '失去自信', '压抑'],
            keywordsEn: ['False Modesty', 'Self-Deprecation', 'Insecurity', 'Suppression', 'Weakness'],
            imageDescription: '山过于隐藏，谦逊变成自卑，失去应有的高度。',
            imageDescriptionEn: 'The mountain is buried too deep. Humility has turned into a lack of self-worth or a "martyr complex."',
            generalMeaning: '过度谦虚变成自卑或虚伪，失去应有的自信。',
            generalMeaningEn: 'You are underestimating yourself or being insincere with your humility. This can lead to missed opportunities or people ignoring your value.',
            scenes: {
                career: '事业上过于退让失机，或假谦虚被看穿。',
                relationship: '感情中过于卑微失自我，或表里不一。',
                wealth: '财务上可能因过于低调错失机会。',
                health: '身心因过度压抑而失去活力。',
                universal: '需区分真谦虚与自卑，保持适度自信。',
                transition: '从谦虚转向自卑或虚伪的负面转折。'
            },
            scenesEn: {
                career: 'Missing a promotion due to staying too quiet; hidden pride pretending to be humble.',
                relationship: 'Losing your voice in a relationship; resentment from being too passive.',
                wealth: 'Poor financial growth due to excessive risk-aversion or lack of ambition.',
                health: 'Lethargy; ignoring your own needs until you are depleted.',
                universal: 'True modesty requires knowing your strength; do not hide your light completely.',
                transition: 'A negative shift from grace to self-sabotage.'
            }
        }
    },

    // 16 - 豫卦 (雷地豫)
    '016': {
        id: '016',
        name: 'Yu (Enthusiasm)',
        nameCn: '豫卦',
        symbol: '☳☷',
        trigrams: { upper: '震', lower: '坤' },
        introduction: '雷从地中奋起，象征欢乐、准备就绪与鼓舞人心的时刻。',
        introductionEn: 'Yu represents Enthusiasm and Harmony. It is the sound of thunder returning to the earth, signifying a time of celebration, art, and being prepared for major action.',
        upright: {
            keywords: ['欢乐振奋', '和谐愉悦', '热情', '准备就绪'],
            keywordsEn: ['Enthusiasm', 'Preparation', 'Joy', 'Inspiration', 'Leisure', 'Alignment'],
            imageDescription: '春雷奋起，大地回春，万物欢欣鼓舞。',
            imageDescriptionEn: 'Thunder erupts from the silent earth. It is the time for music, dance, and moving the masses toward a vision.',
            generalMeaning: '处于欢乐振奋状态，人心鼓舞，适合顺势推进。',
            generalMeaningEn: 'The mood is high. You have worked hard and now is the time to enjoy life and gather others around your cause. Success comes through joy.',
            scenes: {
                career: '士气高涨，团队振奋，适合推动重要项目。',
                relationship: '感情愉悦和谐，相处快乐，氛围轻松。',
                wealth: '财运上扬期，心态积极有助增长。',
                health: '身心愉悦，精神振奋，状态良好。',
                universal: '欢乐振奋时期，趁势推进重要事务。',
                transition: '从沉闷状态转向欢乐振奋的正向转折。'
            },
            scenesEn: {
                career: 'High morale; motivating a team; a perfect time to launch a creative campaign.',
                relationship: 'Playful romance; shared festivities; deep joy in each other\'s company.',
                wealth: 'Prosperity through creative ideas; spending on entertainment or art.',
                health: 'Mental health peak; high vitality; healing through music and joy.',
                universal: 'Share your enthusiasm; your positive energy is contagious.',
                transition: 'A bright shift from stagnant effort into inspired joy.'
            }
        },
        reversed: {
            keywords: ['乐极生悲', '过度享乐', '懈怠', '空欢喜'],
            keywordsEn: ['Overindulgence', 'Illusory Joy', 'Apathy', 'False Security', 'Boredom'],
            imageDescription: '雷声过后无雨，空欢喜或因放纵而疲惫。',
            imageDescriptionEn: 'The thunder is hollow; no rain falls. Joy becomes a distraction from necessary work.',
            generalMeaning: '过度沉溺于享乐或不切实际乐观，导致懈怠。',
            generalMeaningEn: 'A warning of over-excitement. You may be so caught up in the party that you are forgetting to actually do the work. Don\'t let enthusiasm become delusion.',
            scenes: {
                career: '因过度乐观而疏忽，或高兴过头懈怠工作。',
                relationship: '沉溺于甜蜜忽视问题，或期望过高。',
                wealth: '因乐观而冒险过度，或享乐挥霍。',
                health: '可能因放纵享乐而伤害身体。',
                universal: '需保持清醒，不过度沉溺，防乐极生悲。',
                transition: '从欢乐转向失望或懈怠的负面转折。'
            },
            scenesEn: {
                career: 'Distraction; failing to meet deadlines due to procrastination; unrealistic goals.',
                relationship: 'Temporary passion without substance; ignoring warning signs in love.',
                wealth: 'Wasting money on fads; living beyond your means; poor financial preparation.',
                health: 'Exhaustion from too much social life; neglecting basic self-care.',
                universal: 'Wake up from the dream; the party will end and work remains.',
                transition: 'A negative shift from inspiration to idle distraction.'
            }
        }
    },

    // 17 - 随卦 (泽雷随)
    '017': {
        id: '017',
        name: 'Sui (Following)',
        nameCn: '随卦',
        symbol: '☱☳',
        trigrams: { upper: '兑', lower: '震' },
        introduction: '雷入泽中休息，象征顺应时势、知时通变的智慧。',
        introductionEn: 'Sui represents the wisdom of following the natural flow. Just as a leader must serve to lead, one must adapt to the times and the hearts of others to find success.',
        upright: {
            keywords: ['随顺时势', '知时通变', '灵活', '适应'],
            keywordsEn: ['Following', 'Adaptability', 'Observation', 'Yielding', 'Ease', 'Acceptance'],
            imageDescription: '雷鸣入泽，顺其自然，不与时势相争。',
            imageDescriptionEn: 'Thunder sinking into the lake to rest. It shows a powerful force choosing to adapt to its surroundings.',
            generalMeaning: '处于需要随顺的阶段，灵活应变能获更好结果。',
            generalMeaningEn: 'Do not force your own will. By following the current trend or a wise leader, you avoid conflict and reach your destination with ease.',
            scenes: {
                career: '事业需灵活应变，顺应市场或组织变化。',
                relationship: '需互相迁就，随顺对方节奏，和美。',
                wealth: '需根据市场变化调整策略，灵活应对。',
                health: '身体需顺应自然节律，调整作息。',
                universal: '随时而变，不固执己见，顺应时势。',
                transition: '从固定模式转向灵活应变的调整。'
            },
            scenesEn: {
                career: 'Going with the flow of the market; taking cues from mentors; adjusting your style.',
                relationship: 'Compromise; following a partner\'s lead for peace; happy coexistence.',
                wealth: 'Following reliable financial trends; staying flexible with assets.',
                health: 'Adjusting to your body\'s needs; seasonal health changes.',
                universal: 'Be flexible like a willow; those who bend do not break.',
                transition: 'A graceful shift from rigid effort into harmonious flow.'
            }
        },
        reversed: {
            keywords: ['盲目追随', '随波逐流', '被动', '固执'],
            keywordsEn: ['Blind Following', 'Drifting', 'Stubbornness', 'Missed Lead', 'Resistance'],
            imageDescription: '雷不入泽，不知休息或盲目震动，失随顺。',
            imageDescriptionEn: 'The thunder struggles against the water. You are either resisting a necessary change or following someone blindly into danger.',
            generalMeaning: '盲从他人失主见，或过于固执无法适应。',
            generalMeaningEn: 'Following for the wrong reasons. You might be a "yes man" without principles, or you are stubbornly trying to swim upstream when the tide is too strong.',
            scenes: {
                career: '事业上盲目跟风或过于固执导致问题。',
                relationship: '感情过于顺从失自我，或固执不妥协。',
                wealth: '财务上跟风投资亏损，或固守过时。',
                health: '不顺应身体信号，作息紊乱加重。',
                universal: '需在顺从与主见间找平衡，不盲从不固。',
                transition: '从适应状态转向失衡的负面转折。'
            },
            scenesEn: {
                career: 'Sticking to outdated technology; following a bad leader into failure; lack of core vision.',
                relationship: 'Being a doormat; or refusing to adapt to a partner\'s changing needs.',
                wealth: 'Financial disaster by following "hype"; being too slow to pivot.',
                health: 'Ignoring your body\'s warning signs; forced routine that doesn\'t fit.',
                universal: 'Check who or what you are following; lead your own life if the path is wrong.',
                transition: 'A negative shift from harmony into aimless drifting.'
            }
        }
    },

    // 18 - 蛊卦 (山风蛊)
    '018': {
        id: '018',
        name: 'Gu (Work on the Decayed)',
        nameCn: '蛊卦',
        symbol: '☶☴',
        trigrams: { upper: '艮', lower: '巽' },
        introduction: '山下有风，积弊待治。象征拨乱反正、革新事业的时机。',
        introductionEn: 'Gu represents the process of repairing what has been damaged or neglected. It signifies that things have gone wrong due to inaction, and energetic reform is now required.',
        upright: {
            keywords: ['革故鼎新', '拨乱反正', '整治积弊', '改革'],
            keywordsEn: ['Repair', 'Reform', 'Renewal', 'Correction', 'Action', 'Overcoming Stagnation'],
            imageDescription: '风在山下被阻，空气浑浊，需强风清理积垢。',
            imageDescriptionEn: 'Wind stilled at the base of a mountain, leading to decay. It is a call to clean up "the rot" and start fresh.',
            generalMeaning: '处于整治积弊阶段，面对问题根源进行改革。',
            generalMeaningEn: 'A time for fixing deep-seated issues. Whether it is a family habit or a business error, you must take active steps to "heal the wound" and ensure it doesn\'t return.',
            scenes: {
                career: '事业存积弊需改革，整顿优化的时机。',
                relationship: '积累的问题需面对处理，痛定思痛。',
                wealth: '财务可能有历史遗留问题需要清理。',
                health: '身体积累问题需彻底调理，根治病因。',
                universal: '面对问题、革故鼎新的关键时期。',
                transition: '从问题积累转向主动整治的正向转折。'
            },
            scenesEn: {
                career: 'Fixing administrative errors; cleaning up workplace culture; restructuring a company.',
                relationship: 'Addressing long-term resentment; resolving family generational trauma; deep healing.',
                wealth: 'Clearing old debts; fixing bad financial systems; audits.',
                health: 'Detoxing the body; treating chronic issues; getting to the root of the pain.',
                universal: 'The rot is visible; pick up the tools and fix it now.',
                transition: 'A courageous shift from passive neglect into active healing.'
            }
        },
        reversed: {
            keywords: ['积弊恶化', '无力改革', '腐败', '逃避'],
            keywordsEn: ['Neglect', 'Decay', 'Inertia', 'Corruption', 'Failing to Act'],
            imageDescription: '风更加阻滞，积弊无法整治，持续恶化。',
            imageDescriptionEn: 'The rot spreads further. Failure to act has led to a situation that is becoming toxic.',
            generalMeaning: '无力或逃避面对积弊，导致问题持续恶化。',
            generalMeaningEn: 'You are letting things slide. By ignoring the problem, you are allowing the corruption or decay to take over completely. Disaster is approaching if you stay silent.',
            scenes: {
                career: '事业问题积重难返，改革受阻或不彻底。',
                relationship: '感情问题持续恶化，回避只会更糟。',
                wealth: '财务积弊加深，不处理会越加严重。',
                health: '健康问题因拖延而加重，需立即处理。',
                universal: '必须直面问题，继续逃避只会恶化。',
                transition: '从积弊转向更严重腐败的负面转折。'
            },
            scenesEn: {
                career: 'Systemic failure; ignoring fraud; professional decay due to laziness.',
                relationship: 'A relationship rotting from within; toxic silence; total breakdown of trust.',
                wealth: 'Mounting debt; ignoring legal warnings; financial collapse through neglect.',
                health: 'Ignoring a growing illness; total lack of hygiene or self-care.',
                universal: 'You cannot hide the truth anymore; the consequences are here.',
                transition: 'A dangerous shift from minor problems to total systemic collapse.'
            }
        }
    },

    // 19 - 临卦 (地泽临)
    '019': {
        id: '019',
        name: 'Lin (Approach)',
        nameCn: '临卦',
        symbol: '☷☱',
        trigrams: { upper: '坤', lower: '兑' },
        introduction: '大地临于泽上，居高临下。象征好运来临、机会降临。',
        introductionEn: 'Lin represents the approach of great opportunity. The Earth is near the Lake, signifying a superior person looking down with care and the beginning of a prosperous time.',
        upright: {
            keywords: ['亲临俯视', '时机来临', '好运', '把握机会'],
            keywordsEn: ['Approach', 'Opportunity', 'Encouragement', 'Leadership', 'Coming Spring'],
            imageDescription: '泽水上涨润泽大地，春暖花开，时机到来。',
            imageDescriptionEn: 'The lake rises toward the land. Like the coming of spring, everything is ready to bloom and grow.',
            generalMeaning: '处于好运来临阶段，时机已到需把握，有时限。',
            generalMeaningEn: 'The time of success is approaching. Be active, be helpful, and take the initiative. However, remember that spring does not last forever; act while you can.',
            scenes: {
                career: '事业好运来临，机会出现，需及时把握。',
                relationship: '感情中缘分来临，需主动把握。',
                wealth: '财运来临，适合积极行动把握投资。',
                health: '身体状态向好，是调理保养的好时机。',
                universal: '好运来临，需积极把握，时机有限。',
                transition: '从等待阶段进入时机来临的正向转折。'
            },
            scenesEn: {
                career: 'Promotion; seeing a project gain momentum; a time to lead and inspire.',
                relationship: 'A new romance blooming; coming closer to a partner; warmth and joy.',
                wealth: 'Financial growth; money coming your way; good timing for purchases.',
                health: 'Improvement in physical state; high vitality; natural healing.',
                universal: 'Welcome the new growth with open arms; stay alert.',
                transition: 'A hopeful shift from preparation into active success.'
            }
        },
        reversed: {
            keywords: ['时机错失', '机会流逝', '好运将尽', '焦虑'],
            keywordsEn: ['Missed Opportunity', 'Withdrawal', 'Regression', 'Fading Power'],
            imageDescription: '地泽分离，临近之势消退，机会窗口关闭。',
            imageDescriptionEn: 'The lake recedes. The window for action is closing, or you have arrived too late.',
            generalMeaning: '时机错失或好运将尽，未能把握机会。',
            generalMeaningEn: 'You have missed the peak moment. Your luck is turning, or you are withdrawing when you should be engaging. Stagnation may return.',
            scenes: {
                career: '事业机会可能错失，或好运期即将过去。',
                relationship: '感情中可能因犹豫错过良缘。',
                wealth: '财务机会流逝，需反思是否错过时机。',
                health: '调理好时机可能错过，需加紧行动。',
                universal: '需抓紧时机，或反思错失原因避再错。',
                transition: '从好运来临转向时机消退的消极转折。'
            },
            scenesEn: {
                career: 'Losing momentum; missed deadlines; being demoted or losing influence.',
                relationship: 'Missed romantic chance; cooling of feelings; distance growing.',
                wealth: 'Missed investment window; financial stagnation; income slowing down.',
                health: 'Ignoring the recovery phase; physical setback after a good period.',
                universal: 'Do not be complacent; the opportunity is slipping away.',
                transition: 'A negative shift from growth to slow regression.'
            }
        }
    },

    // 20 - 观卦 (风地观)
    '020': {
        id: '020',
        name: 'Guan (Contemplation)',
        nameCn: '观卦',
        symbol: '☴☷',
        trigrams: { upper: '巽', lower: '坤' },
        introduction: '风行地上，无所不见。象征观察、洞察与成为榜样。',
        introductionEn: 'Guan represents contemplation and looking inward. It is the wind blowing over the earth, observing all and being observed by all. It is about spiritual focus and vision.',
        upright: {
            keywords: ['观察审视', '洞察本质', '榜样', '反省'],
            keywordsEn: ['Contemplation', 'Observation', 'Vision', 'Insight', 'Perspective', 'Reflection'],
            imageDescription: '高处远眺，洞察世间万物，以此修身省。',
            imageDescriptionEn: 'A high tower overlooking the land. It is the time to gain a broader view and understand the patterns of your life.',
            generalMeaning: '需仔细观察审视的阶段，通过观察洞察本质。',
            generalMeaningEn: 'Pause and observe. Do not act yet. Seek a higher perspective and understand the deeper meaning of events before making your move.',
            scenes: {
                career: '需仔细观察学习，以榜样为师提升。',
                relationship: '需仔细观察对方，了解真实想法。',
                wealth: '需仔细分析观察市场，再做决策。',
                health: '身体需仔细观察症状，了解健康状况。',
                universal: '观察学习时期，静心审视洞察本质。',
                transition: '从行动阶段转向观察反思的过渡期。'
            },
            scenesEn: {
                career: 'Strategic analysis; observing the industry; learning from leaders; re-evaluating goals.',
                relationship: 'Observing a partner\'s true character; self-reflection in love; wait and see.',
                wealth: 'Market research; seeing the long-term trend; cautious observation.',
                health: 'Self-diagnosis; body awareness; mindfulness as medicine.',
                universal: 'Witness the world around you; silence is your best tool right now.',
                transition: 'A calm shift from active striving to deep understanding.'
            }
        },
        reversed: {
            keywords: ['一叶障目', '表面观察', '误判', '盲目'],
            keywordsEn: ['Narrow Vision', 'Ignorance', 'Superficiality', 'Misunderstanding'],
            imageDescription: '风被阻滞无法遍观，观察受限流于表面。',
            imageDescriptionEn: 'The tower is low or the view is blocked. You are looking at small details and missing the big picture.',
            generalMeaning: '观察流于表面或受限于偏见，无法洞察。',
            generalMeaningEn: 'Short-sightedness. You are making judgments based on superficial appearances or refused to see the truth right in front of you.',
            scenes: {
                career: '事业上可能因观察不周而误判形势。',
                relationship: '可能只看表面，忽视对方真实感受。',
                wealth: '财务分析不够深入，做出错误决策。',
                health: '可能忽视身体发出的细微信号。',
                universal: '需打破偏见，进行深入全面观察。',
                transition: '从表面观察转向深入洞察的调整。'
            },
            scenesEn: {
                career: 'Missing a major threat; poor data analysis; professional blindness.',
                relationship: 'Being fooled by appearances; ignoring red flags; shallow understanding.',
                wealth: 'Missing the "fine print"; making assumptions; superficial financial advice.',
                health: 'Ignoring underlying causes; only treating the surface symptoms.',
                universal: 'Open your eyes wider; you are only seeing what you want to see.',
                transition: 'A negative shift from clarity to muddled confusion.'
            }
        }
    },

    // 21 - 噬嗑卦 (火雷噬嗑)
    '021': {
        id: '021',
        name: 'Shi Ke (Biting Through)',
        nameCn: '噬嗑卦',
        symbol: '☲☳',
        trigrams: { upper: '离', lower: '震' },
        introduction: '雷电交加，咬合消障。象征排除障碍、以果断手段解决问题的时机。',
        introductionEn: 'Shi Ke represents biting through an obstacle. Just as teeth must bite through food to unite, one must use energetic action and legal force to remove barriers and achieve justice.',
        upright: {
            keywords: ['咬合消障', '排除障碍', '解决问题', '决断', '果断'],
            keywordsEn: ['Breakthrough', 'Justice', 'Obstacles', 'Judgment', 'Decisiveness', 'Punishment'],
            imageDescription: '火（雷电）在上而震（动）在下，象征以威严手段清除障碍。',
            imageDescriptionEn: 'Thunder and lightning together. A brilliant light reveals the obstruction, and the thunderous action clears it away.',
            generalMeaning: '处于需要果断排除障碍的阶段，以决断之力解决问题。',
            generalMeaningEn: 'A time for firm action. Identify the root cause of the conflict or delay and "bite through" it with clarity and legal/principled force.',
            scenes: {
                career: '事业需果断解决障碍性问题，不可拖延。',
                relationship: '感情中存在的障碍需正面解决，不可回避。',
                wealth: '财务障碍需果断处理，该断则断。',
                health: '健康问题需积极治疗，排除病根。',
                universal: '当下是果断排除障碍的时机，以决断力解决。',
                transition: '从障碍困扰转向主动解决的正向转折。'
            },
            scenesEn: {
                career: 'Settling disputes; firing a toxic employee; making a tough decision to clear project bottlenecks.',
                relationship: 'Confronting a lie; clearing the air; breaking through emotional walls.',
                wealth: 'Resolving legal financial issues; debt collection; clearing fraudulent charges.',
                health: 'Surgery (if needed); aggressive treatment to remove toxins or disease.',
                universal: 'Justice is needed; be firm and do not hesitate to act.',
                transition: 'A sharp shift from being stuck to a powerful breakthrough.'
            }
        },
        reversed: {
            keywords: ['障碍难除', '处理不当', '判断失误', '冤屈'],
            keywordsEn: ['Ineffective Action', 'Injustice', 'Misjudgment', 'Harshness', 'Stalled'],
            imageDescription: '雷电失调，咬合无力，障碍无法消除或处理过当。',
            imageDescriptionEn: 'A blunt tooth or weak jaw. The force used is either too weak to solve the problem or so harsh that it creates new ones.',
            generalMeaning: '障碍难以清除或处理方式不当，可能导致新问题。',
            generalMeaningEn: 'Failure to resolve conflict. You may be using too much force or making a biased judgment, leading to more complications.',
            scenes: {
                career: '事业障碍处理不当，可能加剧问题或造成冤屈。',
                relationship: '感情问题处理方式不对，可能伤害关系。',
                wealth: '财务问题处理失误，可能损失更大。',
                health: '治疗方式可能不当，需要调整方案。',
                universal: '需反思解决问题的方式，避免过激或不当。',
                transition: '从处理问题转向方式失误的负面转折。'
            },
            scenesEn: {
                career: 'Unfair punishment; workplace grievances ignored; messy legal outcome.',
                relationship: 'Cruel words during a fight; failing to address the actual issue.',
                wealth: 'Losing a legal battle; fines due to poor management; unfair settlements.',
                health: 'Misdiagnosis; side effects from too much medication; slow recovery.',
                universal: 'You are using power wrongly; step back and reassess the facts.',
                transition: 'A negative shift from resolution to complex conflict.'
            }
        }
    },

    // 22 - 贲卦 (山火贲)
    '022': {
        id: '022',
        name: 'Bi (Grace)',
        nameCn: '贲卦',
        symbol: '☶☲',
        trigrams: { upper: '艮', lower: '离' },
        introduction: '山下有火，光彩照人。象征文饰、艺术美感与适当的修饰。',
        introductionEn: 'Bi represents beauty, grace, and elegance. It is the fire at the foot of the mountain, lighting up the world with art and culture, but reminding us that form should follow substance.',
        upright: {
            keywords: ['文饰修饰', '美化', '礼仪', '艺术', '形象'],
            keywordsEn: ['Grace', 'Art', 'Elegance', 'Form', 'Style', 'Aesthetics', 'Courtesy'],
            imageDescription: '火在山下，照映山林。虽非主色调，却增添了光彩。',
            imageDescriptionEn: 'A fire illuminating the mountain base. It symbolizes the light of civilization and the beauty of external appearance.',
            generalMeaning: '处于需要适当装饰美化的阶段，注重形象与礼仪。',
            generalMeaningEn: 'Focus on small improvements and aesthetics. It is a good time for artistic pursuits, branding, or social etiquette, but don\'t neglect the core truth.',
            scenes: {
                career: '事业需注重形象包装，但内容仍是根本。',
                relationship: '感情中适当浪漫与仪式感有助关系。',
                wealth: '财务上适当门面投资有助事业，但不可过度。',
                health: '外表保养重要，但内在健康更关键。',
                universal: '注重形象美化的时期，但需内外兼顾。',
                transition: '从实质阶段转向注重形式的调整期。'
            },
            scenesEn: {
                career: 'Polishing a presentation; personal branding; small wins through charm and style.',
                relationship: 'A romantic date; sweet words; focusing on the beauty of the partner.',
                wealth: 'Investing in art or home decor; spending on quality over quantity.',
                health: 'Dermatology or cosmetic care; feeling good through looking good.',
                universal: 'Add grace to your actions; beauty opens doors.',
                transition: 'A subtle shift from raw reality to refined expression.'
            }
        },
        reversed: {
            keywords: ['虚有其表', '过度装饰', '华而不实', '肤浅'],
            keywordsEn: ['Superficiality', 'Flashy', 'Empty Form', 'Pretense', 'Deception'],
            imageDescription: '光芒过盛掩盖山体，或装饰过度掩盖了本质。',
            imageDescriptionEn: 'The fire is too bright, hiding the mountain\'s true shape. Substance is lost to excessive decoration.',
            generalMeaning: '过度注重外表而忽视内在，虚有其表或欺骗。',
            generalMeaningEn: 'Hollow beauty. You may be focusing so much on how things look that you are ignoring a rotting foundation. Be wary of shallow promises.',
            scenes: {
                career: '事业上可能虚有其表，内容不足被人看穿。',
                relationship: '感情表面光鲜实则空洞，缺乏真心。',
                wealth: '财务上可能过度包装导致资源浪费。',
                health: '过度注重外表而忽视内在健康。',
                universal: '需回归本质，不可被表象迷惑。',
                transition: '从适度装饰转向华而不实的负面转折。'
            },
            scenesEn: {
                career: 'A project with no depth; marketing lies; being judged only by your cover.',
                relationship: 'A relationship based only on looks; lack of soul connection; pretense.',
                wealth: 'Buying luxury items you can\'t afford; financial vanity.',
                health: 'Ignoring internal pain for the sake of appearance; burnout.',
                universal: 'Strip away the mask; find the truth beneath the style.',
                transition: 'A shift from elegance to deceptive emptiness.'
            }
        }
    },

    // 23 - 剥卦 (山地剥)
    '023': {
        id: '023',
        name: 'Bo (Splitting Apart)',
        nameCn: '剥卦',
        symbol: '☶☷',
        trigrams: { upper: '艮', lower: '坤' },
        introduction: '山附于地，剥落衰败。象征阴盛阳衰、基础动摇的时刻。',
        introductionEn: 'Bo represents splitting apart or erosion. The mountain is crumbling onto the earth. It is a time when the old structure must fall, and forward action is dangerous.',
        upright: {
            keywords: ['剥落衰败', '基础动摇', '静待时变', '收敛'],
            keywordsEn: ['Erosion', 'Decay', 'Deterioration', 'Stripping', 'Retreat', 'End of a Cycle'],
            imageDescription: '五阴之气向上剥蚀最后一阳，象征局势危殆。',
            imageDescriptionEn: 'A mountain worn down by the elements. The foundation is weak; things are falling away layer by layer.',
            generalMeaning: '处于衰败剥落的阶段，不宜妄动，应收敛防守。',
            generalMeaningEn: 'The situation is collapsing. Do not try to start anything new. Accept the loss and retreat into silence to wait for the cycle to end.',
            scenes: {
                career: '事业处于低谷，宜收敛锋芒，不宜冒进。',
                relationship: '感情可能处于不稳定期，需小心维护。',
                wealth: '财运低迷，宜保守理财，避损。',
                health: '身体可能较弱，需静养调理，不宜过劳。',
                universal: '收敛防守时期，静待时变，不宜妄动。',
                transition: '进入衰退剥落的低谷阶段。'
            },
            scenesEn: {
                career: 'Downsizing; loss of position; a project being cancelled; need for professional survival.',
                relationship: 'A relationship falling apart; erosion of trust; time to let go.',
                wealth: 'Financial loss; property devaluation; dwindling savings.',
                health: 'Declining health; chronic weakness; need for complete rest.',
                universal: 'Accept the ending; you cannot stop the mountain from falling.',
                transition: 'A painful shift from stability to inevitable collapse.'
            }
        },
        reversed: {
            keywords: ['剥极必复', '触底反弹', '转机出现', '恢复'],
            keywordsEn: ['Turning Point', 'Recovery', 'Rebuilding', 'Bottoming Out', 'Resilience'],
            imageDescription: '剥落到极致，虽见其毁，但复苏之芽已现。',
            imageDescriptionEn: 'The mountain has finished falling. The debris is cleared, and the first sign of a new foundation appears.',
            generalMeaning: '剥落到极致即将复苏，转机出现。',
            generalMeaningEn: 'The worst is over. You have hit rock bottom and are now starting to slowly rebuild from the rubble. The new cycle is beginning.',
            scenes: {
                career: '事业触底后出现转机，坚持就是胜利。',
                relationship: '感情最困难时期过去，曙光初现。',
                wealth: '财务触底反弹，最坏时期已过。',
                health: '身体开始恢复，度过最虚弱阶段。',
                universal: '虽困难但转机将至，坚持信念。',
                transition: '从衰败低谷转向复苏的正向转折。'
            },
            scenesEn: {
                career: 'Finding a new job after a layoff; beginning to see growth after a long drought.',
                relationship: 'Starting to heal after a painful breakup; a small spark of hope in a bond.',
                wealth: 'Stabilizing after a loss; the end of financial bleeding.',
                health: 'Slow return of strength; the turning point of an illness.',
                universal: 'The light is returning; start building again, slowly.',
                transition: 'A hopeful shift from total loss toward regeneration.'
            }
        }
    },

    // 24 - 复卦 (地雷复)
    '024': {
        id: '024',
        name: 'Fu (Return)',
        nameCn: '复卦',
        symbol: '☷☳',
        trigrams: { upper: '坤', lower: '震' },
        introduction: '一阳复始，万物复苏。象征新的循环开始、回归正轨。',
        introductionEn: 'Fu represents the Return. After a period of darkness, the light returns at the winter solstice. It is the beginning of a new cycle and the restoration of natural order.',
        upright: {
            keywords: ['一阳复始', '回归复苏', '循环往复', '新生'],
            keywordsEn: ['Return', 'Turning Point', 'Renewal', 'Restoration', 'Healing', 'Fresh Start'],
            imageDescription: '雷在地中蓄势，阳光初现，生命再次律动。',
            imageDescriptionEn: 'Thunder deep within the earth. It is the first heartbeat of spring; the energy is quiet but unstoppable.',
            generalMeaning: '处于回归复苏阶段，经历低谷后，新的循环开始。',
            generalMeaningEn: 'Recovery is here. You are finding your way back to your true self or a better path. Move forward slowly and naturally.',
            scenes: {
                career: '事业走出低谷，新机会出现，再出发吉。',
                relationship: '感情经历波折后重开始，或与旧友重逢。',
                wealth: '财运开始恢复，新的收益来源出现。',
                health: '身体康复，活力逐渐恢复。',
                universal: '新循环开始的时期，把握复苏契机。',
                transition: '从低谷结束进入复苏新生的正向转折。'
            },
            scenesEn: {
                career: 'Project reboot; returning to a successful habit; a fresh career path after rest.',
                relationship: 'Reconciliation; falling in love again; returning to emotional balance.',
                wealth: 'Finances bouncing back; seeing the first profits of a new plan.',
                health: 'Convalescence; the body regaining its natural rhythm; vitality.',
                universal: 'Welcome the new light; follow your natural cycle.',
                transition: 'A gentle shift from darkness back into the light.'
            }
        },
        reversed: {
            keywords: ['复而又失', '循环受阻', '回归失败', '退步'],
            keywordsEn: ['Missed Return', 'Relapse', 'Confusion', 'Lost Direction', 'Wasted Chance'],
            imageDescription: '雷欲发而受阻，阳光又被云遮，复苏不顺。',
            imageDescriptionEn: 'The light flickers and goes out. You missed the turning point or went back to your old mistakes too soon.',
            generalMeaning: '复苏过程受阻，回归方向迷失，可能再入低谷。',
            generalMeaningEn: 'A missed opportunity for healing. You are repeating bad patterns and turning away from the path that could save you.',
            scenes: {
                career: '事业复苏过程不顺，可能出现反复。',
                relationship: '感情重新开始后又遇挫折，走回老路。',
                wealth: '财务刚有起色又遇问题。',
                health: '身体恢复后可能复发，需更加注意。',
                universal: '需谨慎前行，复苏可能有反复，耐心。',
                transition: '从复苏阶段可能再次受挫的波折。'
            },
            scenesEn: {
                career: 'Failed business relaunch; falling back into bad work habits; loss of momentum.',
                relationship: 'Breaking up again after a failed reunion; unable to change for the better.',
                wealth: 'Financial relapse; falling back into debt; missing a recovery window.',
                health: 'Relapse of illness; ignoring the need for continued rest.',
                universal: 'Do not go back to the darkness; stay on the path of growth.',
                transition: 'A negative shift from recovery back into stagnation.'
            }
        }
    },

    // 25 - 无妄卦 (天雷无妄)
    '025': {
        id: '025',
        name: 'Wu Wang (Innocence)',
        nameCn: '无妄卦',
        symbol: '☰☳',
        trigrams: { upper: '乾', lower: '震' },
        introduction: '天下雷行，顺应自然。象征真实无欺、意外之福。',
        introductionEn: 'Wu Wang represents Innocence or being without pretense. It is the thunder rolling under heaven; all things must act according to their true nature. Success comes through sincerity.',
        upright: {
            keywords: ['无妄无欺', '顺应天道', '真诚自然', '正直'],
            keywordsEn: ['Innocence', 'Sincerity', 'Uncalculated Action', 'Spontaneity', 'Purity'],
            imageDescription: '天下雷行，万物感其威而顺其性，真实无妄。',
            imageDescriptionEn: 'Nature acting without motive. Like a child or the wild forest, it is pure, honest, and in direct alignment with the divine.',
            generalMeaning: '顺应自然、不强求，意外之福可能降临。',
            generalMeaningEn: 'Act from the heart without calculating your profit. If you stay true and honest, the universe will provide unexpected rewards. Avoid clever schemes.',
            scenes: {
                career: '事业顺其自然，不妄为反而有意外收获。',
                relationship: '感情真诚相待，不刻意追求，发展更好。',
                wealth: '财务不投机取巧，正当经营反而顺利。',
                health: '顺应自然规律，不过度干预更健康。',
                universal: '顺应自然，真诚行事自有回报。',
                transition: '从刻意追求转向顺其自然的正向调整。'
            },
            scenesEn: {
                career: 'Doing your job for its own sake; success through integrity; unexpected career gifts.',
                relationship: 'Pure, uncalculated love; meeting someone while not looking; honesty.',
                wealth: 'Wealth through honest labor; avoiding risky shortcuts; windfall luck.',
                health: 'Natural healing; letting the body find its own balance; vitality.',
                universal: 'Do what is right because it is right; let the results take care of themselves.',
                transition: 'A peaceful shift from striving to spontaneous flow.'
            }
        },
        reversed: {
            keywords: ['妄念妄为', '意外之灾', '违背天道', '灾祸'],
            keywordsEn: ['Calculation', 'Misfortune', 'Error', 'Hidden Motive', 'Unexpected Blow'],
            imageDescription: '天雷击中不合道之物，妄求导致灾殃。',
            imageDescriptionEn: 'Thunder striking the arrogant. Action taken with selfish greed leads to disaster or a "stroke of bad luck."',
            generalMeaning: '因妄念妄为违背自然法则，招致意外之灾。',
            generalMeaningEn: 'A warning of unexpected misfortune caused by a lack of sincerity or by trying to force a result. You are out of sync with your true self.',
            scenes: {
                career: '事业上妄想走捷径，可能遭遇挫折。',
                relationship: '感情中虚伪或强求，可能适得其反。',
                wealth: '财务投机取巧可能遭受意外损失。',
                health: '不顺应自然规律，导致健康问题。',
                universal: '需反思是否有妄念，回归真诚自然。',
                transition: '从自然顺遂转向因妄为而遭灾的转折。'
            },
            scenesEn: {
                career: 'Scandal; failed shortcuts; professional calamity due to lack of ethics.',
                relationship: 'Manipulation backfiring; being caught in a lie; forceful pursuits causing pain.',
                wealth: 'Financial accidents; bad luck with investments; being cheated.',
                health: 'Sudden illness; injuries from taking risks; stress from lies.',
                universal: 'Check your heart; are you being honest or just being clever?',
                transition: 'A sharp shift from innocence into complicated trouble.'
            }
        }
    },

    // 26 - 大畜卦 (山天大畜)
    '026': {
        id: '026',
        name: 'Da Xu (Great Accumulating)',
        nameCn: '大畜卦',
        symbol: '☶☰',
        trigrams: { upper: '艮', lower: '乾' },
        introduction: '高山蓄纳天气。象征博学多识、厚积薄发的储备期。',
        introductionEn: 'Da Xu represents Great Accumulation. The mountain holds the vast sky within it. It signifies building a massive reserve of knowledge, character, and resources for future greatness.',
        upright: {
            keywords: ['大有积蓄', '厚积薄发', '蓄德养能', '储备'],
            keywordsEn: ['Great Reserves', 'Self-Control', 'Wisdom', 'Potential', 'Nourishing Talent'],
            imageDescription: '山中有金石之气，蓄积深厚，足以利涉大川。',
            imageDescriptionEn: 'A mountain containing the power of heaven. A vault of energy and truth being guarded and increased.',
            generalMeaning: '处于大量积蓄阶段，为将来的大事做准备。',
            generalMeaningEn: 'A time of training and study. Do not rush to the finish line; instead, focus on strengthening your character and your bank account. Mastery is coming.',
            scenes: {
                career: '是大量学习积累时期，储备能力。',
                relationship: '感情中积累信任，为长久打基础。',
                wealth: '财务大量储蓄，为将来大投资做准备。',
                health: '身体储备充足能量，养精蓄锐。',
                universal: '厚积时期，大量积累为厚发做准备。',
                transition: '进入大量积蓄储备的关键阶段。'
            },
            scenesEn: {
                career: 'Advanced training; accumulating power in an organization; long-term strategic success.',
                relationship: 'Deep emotional reserves; a relationship that grows richer through shared learning.',
                wealth: 'Saving for a major goal; sound asset accumulation; financial wisdom.',
                health: 'Building a robust immune system; training for physical endurance.',
                universal: 'Be a container of wisdom; wait for the perfect time to pour it out.',
                transition: 'A disciplined shift into a period of high potential.'
            }
        },
        reversed: {
            keywords: ['积蓄不足', '挥霍无度', '急于求成', '耗尽'],
            keywordsEn: ['Drained', 'Lack of Prep', 'Wasteful', 'Superficial', 'Impatience'],
            imageDescription: '山裂气泄，积蓄流失，根基动摇。',
            imageDescriptionEn: 'The vault is empty or leaking. You are trying to act without having the necessary experience or funds.',
            generalMeaning: '积蓄不足就急于行动，或挥霍已有积累。',
            generalMeaningEn: 'Spiritual or financial exhaustion. You are running on empty. If you continue to output without input, you will collapse.',
            scenes: {
                career: '根基不够扎实，急于表现可能失败。',
                relationship: '感情积累不够就急于进展，根基不稳。',
                wealth: '财务积蓄被挥霍，储备不足难应风险。',
                health: '身体能量耗尽，需要补充休养。',
                universal: '需继续积累，不可急于求成或挥霍。',
                transition: '从积蓄阶段因急躁或挥霍受阻。'
            },
            scenesEn: {
                career: 'Pretending to have expertise; job hopping without skill; burnout from lack of prep.',
                relationship: 'An empty connection; rushing to marriage without knowing each other.',
                wealth: 'Spending your savings; no safety net; financial vanity.',
                health: 'Physical burnout; ignoring the need for nutrition and sleep.',
                universal: 'Stop the outflow; you are losing more than you are gaining.',
                transition: 'A negative shift from potential to depletion.'
            }
        }
    },

    // 27 - 颐卦 (山雷颐)
    '027': {
        id: '027',
        name: 'Yi (Nourishment)',
        nameCn: '颐卦',
        symbol: '☶☳',
        trigrams: { upper: '艮', lower: '震' },
        introduction: '观颐养之道。象征摄取正确的养分，养生、养心、养德。',
        introductionEn: 'Yi represents the Corners of the Mouth or Nourishment. It focuses on how we feed ourselves—both physically and spiritually—and the words that come out of our mouths.',
        upright: {
            keywords: ['颐养', '饮食调养', '养生养心', '自我修养'],
            keywordsEn: ['Nourishment', 'Health', 'Speech', 'Self-Care', 'Consumption', 'Sustenance'],
            imageDescription: '上下两阳中夹四阴，形如张开的口。',
            imageDescriptionEn: 'The shape of an open mouth. It symbolizes the intake of energy and the output of influence.',
            generalMeaning: '处于需要颐养调理阶段，滋养身心，注意言行。',
            generalMeaningEn: 'Pay attention to your health and your words. Choose healthy food and positive information. What you "ingest" now determines your future strength.',
            scenes: {
                career: '需"充电"补充知识，不可只输出不输入。',
                relationship: '感情需滋养经营，给予彼此精神养分。',
                wealth: '财务需节制有度，不可暴饮暴食。',
                health: '需正确饮食调养，重视营养摄取。',
                universal: '滋养身心时期，注意摄入的质与量。',
                transition: '从消耗阶段转向滋养补充的调整期。'
            },
            scenesEn: {
                career: 'Professional development; networking for sustenance; using speech wisely to influence.',
                relationship: 'Nurturing a partner; kind words; providing emotional support.',
                wealth: 'Financial balance; spending money on self-improvement and health.',
                health: 'Clean diet; mindful eating; focusing on digestion and wellness.',
                universal: 'Feed your soul with good things; watch what you say.',
                transition: 'A vital shift from depletion back to nourishment.'
            }
        },
        reversed: {
            keywords: ['饮食无度', '养分不当', '放纵', '不知节制'],
            keywordsEn: ['Overindulgence', 'Toxins', 'Gossip', 'Gluttony', 'Neglect'],
            imageDescription: '口中之物不洁，或暴饮暴食，导致不适。',
            imageDescriptionEn: 'An imbalanced mouth. Eating junk food or spreading harmful gossip. The cycle of nourishment is broken.',
            generalMeaning: '颐养方式不当，可能饮食无度或摄取不良。',
            generalMeaningEn: 'Poor self-care. You are "eating" poison—be it junk food, toxic social media, or bitter thoughts. Your health and reputation are at risk.',
            scenes: {
                career: '摄入知识资源不当，方向有误。',
                relationship: '给予或接受方式不对，关系无滋养。',
                wealth: '财务挥霍或不当投资，有出无进。',
                health: '饮食不节或营养失衡，身体受损。',
                universal: '需反思摄取方式内容，调整养护。',
                transition: '从正常颐养转向失调的负面转折。'
            },
            scenesEn: {
                career: 'Misinformation; creative burnout; losing respect through bad communication.',
                relationship: 'A "vampire" relationship where one only takes; bitter arguments.',
                wealth: 'Wasting money on temporary pleasures; addiction-related costs.',
                health: 'Obesity or malnutrition; ignoring physical warnings.',
                universal: 'Stop poisoning yourself; change your habits immediately.',
                transition: 'A negative shift from health to self-destruction.'
            }
        }
    },

    // 28 - 大过卦 (泽风大过)
    '028': {
        id: '028',
        name: 'Da Guo (Great Exceeding)',
        nameCn: '大过卦',
        symbol: '☱☴',
        trigrams: { upper: '兑', lower: '巽' },
        introduction: '栋梁过重，压力巨大。象征非常时期、需突破常规。',
        introductionEn: 'Da Guo represents Preponderance of the Great. The beam is bending under too much weight. It is a time of extraordinary stress where normal rules do not apply, and bold action is required to avoid collapse.',
        upright: {
            keywords: ['大过失衡', '重压', '非常时期', '突破常规'],
            keywordsEn: ['Overload', 'Critical Mass', 'Bold Action', 'Stress', 'Extremes', 'Crisis'],
            imageDescription: '水在木上，木被淹没，象征重荷与危机。',
            imageDescriptionEn: 'A lake flooding the trees. The situation is beyond normal limits; the structure is at its breaking point.',
            generalMeaning: '处于超常负荷或非常时期，压力极大但也是机会。',
            generalMeaningEn: 'A crisis point. You are under immense pressure. You must make a radical move or find a new way to support the "beam" before it snaps.',
            scenes: {
                career: '事业承担超常压力，需特殊努力渡过。',
                relationship: '感情面临重大考验，需非常规方式应对。',
                wealth: '财务可能有超常支出或投资机会。',
                health: '身体处于高负荷，需特别注意保养。',
                universal: '非常时期，以非常之法应对，可有成。',
                transition: '进入超常负荷的极端阶段。'
            },
            scenesEn: {
                career: 'Immense workload; a "sink or swim" project; taking a huge professional risk.',
                relationship: 'Relationship at a breaking point; need for a major change or sacrifice.',
                wealth: 'Massive financial move; heavy debt or heavy investment; high stakes.',
                health: 'Critical illness; extreme exhaustion; the need for urgent intervention.',
                universal: 'Be brave and move forward; staying still will cause the structure to break.',
                transition: 'A tense shift from normal life to a state of high emergency.'
            }
        },
        reversed: {
            keywords: ['不堪重负', '栋梁折断', '崩溃', '失败'],
            keywordsEn: ['Collapse', 'Failure', 'Overwhelmed', 'Broken Spirit', 'Disaster'],
            imageDescription: '栋梁折断，房屋崩塌。无法承受负荷。',
            imageDescriptionEn: 'The beam snaps. The pressure was too much and the structure has failed. Everything is underwater.',
            generalMeaning: '无法承受超常负荷，导致崩溃或失败。',
            generalMeaningEn: 'Total collapse. You waited too long to act or the burden was simply too great. Now you must deal with the fallout of the breakdown.',
            scenes: {
                career: '事业压力超极限，可能崩溃或失败。',
                relationship: '感情在重压下破裂，无法继续。',
                wealth: '财务负担过重导致危机。',
                health: '身体不堪重负，健康严重受损。',
                universal: '需立即减轻负担，防止全面崩溃。',
                transition: '从高负荷转向崩溃失败的危险转折。'
            },
            scenesEn: {
                career: 'Business bankruptcy; workplace breakdown; failing a high-stakes test.',
                relationship: 'Final breakup under pressure; emotional explosion.',
                wealth: 'Financial ruin; the bubble bursts; inability to pay massive debts.',
                health: 'Total physical breakdown; hospitalization; nervous collapse.',
                universal: 'The limit has been reached; the old way is now destroyed.',
                transition: 'A catastrophic shift from stress to complete failure.'
            }
        }
    },

    // 29 - 坎卦 (坎为水)
    '029': {
        id: '029',
        name: 'Kan (The Abysmal)',
        nameCn: '坎卦',
        symbol: '☵☵',
        trigrams: { upper: '坎', lower: '坎' },
        introduction: '重重深渊，水流不息。象征深陷险境、需诚心求通。',
        introductionEn: 'Kan represents the Abysmal or Water. It is danger repeated—one pit after another. It signifies a very difficult time that requires total sincerity and the flow of spirit to survive.',
        upright: {
            keywords: ['险难重重', '习坎', '险中求通', '诚心渡难'],
            keywordsEn: ['Danger', 'Risk', 'Testing', 'Flow', 'Sincerity', 'Perseverance', 'Abyss'],
            imageDescription: '水在水中，深不见底。唯有不断流动方能出险。',
            imageDescriptionEn: 'Water flowing into a deep chasm. To stop is to drown; to stay true and keep moving is the only way through.',
            generalMeaning: '处于险难重重阶段，面临考验，诚心可渡。',
            generalMeaningEn: 'You are in the middle of a major life test. Do not panic. Keep your heart steady and flow like water around the rocks. Success comes through endurance.',
            scenes: {
                career: '事业遭遇重重困难，需坚持不懈。',
                relationship: '面临严峻考验，只有真心才能渡难关。',
                wealth: '财务陷困境，需谨慎应对逐步脱困。',
                health: '身体可能遇较大问题，需认真对待。',
                universal: '险难时期，以诚心和坚持渡过。',
                transition: '进入险难重重的考验阶段。'
            },
            scenesEn: {
                career: 'Extreme professional risk; navigating a dangerous market; persistent hurdles.',
                relationship: 'A relationship in deep crisis; a "test of fire" for love; hidden dangers.',
                wealth: 'Financial abyss; debt cycles; extreme caution needed with every cent.',
                health: 'Chronic illness; surgery; psychological depths; blood or kidney issues.',
                universal: 'Keep moving; stopping now is the only true danger.',
                transition: 'A frightening shift into the heart of a crisis.'
            }
        },
        reversed: {
            keywords: ['陷入深渊', '险难加剧', '绝境', '恐惧'],
            keywordsEn: ['Drowning', 'Giving Up', 'Paralysis', 'Overwhelmed by Fear'],
            imageDescription: '水入穴中而不流，越陷越深。',
            imageDescriptionEn: 'Falling into the pit. You have lost your nerve and the water has become stagnant. You are overwhelmed.',
            generalMeaning: '险难加剧，陷入难以脱困境地，需外助。',
            generalMeaningEn: 'Succumbing to danger. You may be paralyzed by fear or making the wrong moves that pull you deeper into the pit. You cannot do this alone.',
            scenes: {
                career: '事业困境加深，可能需寻求外部帮助。',
                relationship: '感情陷僵局，难以自行化解。',
                wealth: '财务危机严重，可能需紧急援助。',
                health: '健康状况恶化，需要专业帮助。',
                universal: '需寻求帮助，独自难脱困，不可拖。',
                transition: '从险难转向更深困境的危险转折。'
            },
            scenesEn: {
                career: 'Professional ruin; failing to act in a crisis; trapped in a toxic work cycle.',
                relationship: 'Being emotionally "swallowed" by a situation; despair in love.',
                wealth: 'Bankruptcy; being cheated while already down; total loss.',
                health: 'Critical medical failure; losing the will to recover.',
                universal: 'Reach out for a lifeline; you are sinking.',
                transition: 'A desperate shift from challenge to potential tragedy.'
            }
        }
    },

    // 30 - 离卦 (离为火)
    '030': {
        id: '030',
        name: 'Li (The Clinging)',
        nameCn: '离卦',
        symbol: '☲☲',
        trigrams: { upper: '离', lower: '离' },
        introduction: '火附于木，光照四方。象征依附、文明与洞察力。',
        introductionEn: 'Li represents the Clinging or Fire. Fire has no form of its own but clings to the wood to burn. It signifies intelligence, clarity, and the need to find a proper foundation to shine.',
        upright: {
            keywords: ['光明依附', '文明', '智慧', '热情', '洞察'],
            keywordsEn: ['Clarity', 'Intelligence', 'Brightness', 'Attachment', 'Vision', 'Warmth'],
            imageDescription: '双火重叠，如日中天，洞见一切真理。',
            imageDescriptionEn: 'The sun shining at noon. Everything is clear and visible. It is the light of wisdom and cultural achievement.',
            generalMeaning: '处于光明智慧阶段，需依附正确事物方持久。',
            generalMeaningEn: 'A time of great insight. You see the truth clearly. To keep this light burning, make sure you are attached to a virtuous path or person.',
            scenes: {
                career: '事业光明有成，需依附正确平台理念。',
                relationship: '感情热烈明朗，彼此照亮，需互依托。',
                wealth: '财运光明，智慧理财可获益。',
                health: '精神明朗，但需注意不可过于燥热。',
                universal: '光明智慧时期，依附正道可获成功。',
                transition: '从晦暗转向光明的正向转折。'
            },
            scenesEn: {
                career: 'Professional recognition; clarity of vision; working for an inspiring company.',
                relationship: 'A brilliant, open romance; seeing each other truly; shared warmth.',
                wealth: 'Transparent financial dealings; prosperity through smart ideas.',
                health: 'Eye health; fever control; mental clarity; high energy.',
                universal: 'Cultivate your inner light; stay attached to what is good.',
                transition: 'A brilliant shift from confusion to absolute clarity.'
            }
        },
        reversed: {
            keywords: ['虚火上炎', '失去依托', '光芒散尽', '焦虑'],
            keywordsEn: ['Burnout', 'Extinguished', 'Confusion', 'False Light', 'Volatility'],
            imageDescription: '火失去燃料，烟尘弥漫，光芒熄灭。',
            imageDescriptionEn: 'The fire flickers out or rages out of control. Without a steady source of fuel, the light becomes a destructive force.',
            generalMeaning: '光明失去依托，可能虚火上炎或陷入黑暗。',
            generalMeaningEn: 'Loss of clarity. You may be over-excited and burning out, or you have lost the "support" that kept you successful. Beware of erratic behavior.',
            scenes: {
                career: '事业失正确方向，空有热情无处施展。',
                relationship: '感情过热反伤人，或热情消退。',
                wealth: '理财可能因虚火妄动而损失。',
                health: '可能虚火上炎，心神不宁。',
                universal: '需找正确依附，否则光明难持久。',
                transition: '从光明转向失落的负面转折。'
            },
            scenesEn: {
                career: 'Sudden end to a career phase; burnout; lack of vision; being fired.',
                relationship: 'A "flash in the pan" romance; arguments; sudden cooling of feelings.',
                wealth: 'Financial "fire" (losses); impulsive spending; being blinded by greed.',
                health: 'Vision problems; heart issues; mental instability; high fever.',
                universal: 'Your light is failing; find a new source of inspiration.',
                transition: 'A dark shift from brilliance to sudden obscurity.'
            }
        }
    },

    // 31 - 咸卦 (泽山咸)
    '031': {
        id: '031',
        name: 'Xian (Influence)',
        nameCn: '咸卦',
        symbol: '☱☶',
        trigrams: { upper: '兑', lower: '艮' },
        introduction: '山上有泽，柔上刚下。象征感应、相互吸引与纯真情。',
        introductionEn: 'Xian represents Influence or Attraction. The Lake is on the Mountain. It symbolizes the universal law of mutual attraction, especially the pure, spontaneous connection between hearts.',
        upright: {
            keywords: ['感应交流', '吸引', '心灵相通', '共鸣'],
            keywordsEn: ['Attraction', 'Influence', 'Harmony', 'Romance', 'Connection', 'Spontaneity'],
            imageDescription: '泽气下流，山气上通，二气交感和谐。',
            imageDescriptionEn: 'The mountain holds the water, and the water moistens the mountain. It is a state of perfect mutual sensitivity.',
            generalMeaning: '处于感应交流美好阶段，心灵相通吸引。',
            generalMeaningEn: 'A time of beautiful connection. Be open and receptive. In relationships or business, an intuitive and honest attraction will lead to success.',
            scenes: {
                career: '事业上与合作者心意相通，默契配合。',
                relationship: '缘分来临，心灵相通，婚恋吉时。',
                wealth: '财务上可能因人脉感应而获得机会。',
                health: '身心和谐，情绪稳定愉悦。',
                universal: '心灵感应好时期，注意把握缘分。',
                transition: '从隔阂转向心灵相通的正向转折。'
            },
            scenesEn: {
                career: 'Great chemistry with partners; persuasive leadership; professional magnetism.',
                relationship: 'Falling in love; mutual attraction; the start of a deep bond.',
                wealth: 'Attracting financial opportunities through social ease and integrity.',
                health: 'Hormonal balance; feeling "in tune" with the body; peace.',
                universal: 'Let your heart be your guide; sincerity attracts the best results.',
                transition: 'A magical shift from isolation to mutual attraction.'
            }
        },
        reversed: {
            keywords: ['感应受阻', '缘分错过', '无法共鸣', '疏离'],
            keywordsEn: ['Misunderstanding', 'Coldness', 'Failed Attraction', 'Selfishness', 'Insincerity'],
            imageDescription: '山泽不交，心门紧锁，感应无法传递。',
            imageDescriptionEn: 'The water is frozen or the mountain is too rigid. There is no feeling, only a cold and empty distance.',
            generalMeaning: '感应交流受阻，缘分错过或无法相通。',
            generalMeaningEn: 'A lack of connection. You may be trying to force an attraction that isn\'t there, or you are being too self-centered to feel what others need.',
            scenes: {
                career: '事业上与他人无法配合，沟通不畅。',
                relationship: '缘分错过或存障碍，无法心灵相通。',
                wealth: '财务机会因人脉不通而错失。',
                health: '情绪可能受阻郁结。',
                universal: '需打开心扉，化解障碍，重建感应。',
                transition: '从相通转向隔阂的负面转折。'
            },
            scenesEn: {
                career: 'Lack of workplace harmony; failing to persuade; awkward networking.',
                relationship: 'Cooling of feelings; distance; unrequited love; manipulated emotions.',
                wealth: 'Missed deals due to poor rapport; social awkwardness costing money.',
                health: 'Emotional blockages causing physical tension; feeling "out of sync."',
                universal: 'Break the silence; you are too focused on yourself.',
                transition: 'A cold shift from resonance to indifference.'
            }
        }
    },

    // 32 - 恒卦 (雷风恒)
    '032': {
        id: '032',
        name: 'Heng (Duration)',
        nameCn: '恒卦',
        symbol: '☳☴',
        trigrams: { upper: '震', lower: '巽' },
        introduction: '雷风相应，恒久不息。象征稳定性、长久承诺。',
        introductionEn: 'Heng represents Duration and Consistency. Like the movements of the sun and moon, success here comes from maintaining your course and staying true to long-term commitments.',
        upright: {
            keywords: ['恒久持续', '始终如一', '稳定', '承诺'],
            keywordsEn: ['Duration', 'Consistency', 'Stability', 'Commitment', 'Endurance', 'Loyalty'],
            imageDescription: '雷鸣风动，循环有常，象征持久的生命力。',
            imageDescriptionEn: 'Thunder and wind together. They are dynamic but part of a permanent natural cycle. It is the image of a long-lasting marriage.',
            generalMeaning: '处于需恒久坚持阶段，始终如一终将成功。',
            generalMeaningEn: 'Stay the course. This is not a time for change, but for sticking to your principles and routines. Persistence is your key to long-term success.',
            scenes: {
                career: '需持之以恒，坚持方向不动摇终有成。',
                relationship: '需长期经营，恒久承诺带稳定幸福。',
                wealth: '需长期规划，稳定积累终有回报。',
                health: '需持续养护，形成良好习惯。',
                universal: '保持恒心，坚持正道，终将通达。',
                transition: '进入需要恒久坚持的稳定阶段。'
            },
            scenesEn: {
                career: 'A stable, long-term job; steady professional growth; staying loyal to your path.',
                relationship: 'Marriage; long-term commitment; a bond that withstands the test of time.',
                wealth: 'Steady accumulation; long-term investments; financial security through habit.',
                health: 'Sustainable fitness; a lifestyle, not a diet; consistent energy.',
                universal: 'Consistency is greatness; do not be swayed by temporary trends.',
                transition: 'A stable shift from excitement to enduring strength.'
            }
        },
        reversed: {
            keywords: ['半途而废', '朝三暮四', '无法持久', '不稳定'],
            keywordsEn: ['Inconsistency', 'Giving Up', 'Capriciousness', 'Instability', 'Broken Vows'],
            imageDescription: '风雷失序，无法维持动力，恒久破裂。',
            imageDescriptionEn: 'The thunder is scattered and the wind is erratic. Nothing lasts; the center cannot hold.',
            generalMeaning: '无法持之以恒，半途而废或无常导致失败。',
            generalMeaningEn: 'A lack of follow-through. You are changing your mind too often or giving up just before the finish line. Inconsistency is leading to failure.',
            scenes: {
                career: '因无法坚持而失败，或频繁变化。',
                relationship: '因无法长久经营出问题，承诺落空。',
                wealth: '规划无法坚持执行，收益不稳定。',
                health: '健康习惯无法持续，反复无常。',
                universal: '需反思为何难坚持，重建恒心。',
                transition: '从稳定持续转向不稳定的负面转折。'
            },
            scenesEn: {
                career: 'Job hopping; abandoning a project too early; lack of professional focus.',
                relationship: 'Fear of commitment; breaking up over small things; infidelity.',
                wealth: 'Financial instability; stopping and starting plans; wasted effort.',
                health: 'Yo-yo dieting; lack of discipline; health fluctuations.',
                universal: 'You must commit to a path; a rolling stone gathers no moss.',
                transition: 'A negative shift from stability to chaotic change.'
            }
        }
    },

    // 33 - 遁卦 (天山遁)
    '033': {
        id: '033',
        name: 'Dun (Retreat)',
        nameCn: '遁卦',
        symbol: '☰☶',
        trigrams: { upper: '乾', lower: '艮' },
        introduction: '天高山远，适时退避。象征明哲保身、战略撤退。',
        introductionEn: 'Dun represents Retreat. The mountain is high, but Heaven is higher and recedes. It is not a sign of defeat, but a strategic withdrawal to preserve your strength and integrity.',
        upright: {
            keywords: ['适时退隐', '以退为进', '明哲保身', '退避'],
            keywordsEn: ['Retreat', 'Withdrawal', 'Strategic Peace', 'Preservation', 'Detachment'],
            imageDescription: '天在上而后退，山在下而静止，主动拉开距离。',
            imageDescriptionEn: 'Heaven moving away from the mountain. It shows the wise man withdrawing from a situation that has become unfavorable or small-minded.',
            generalMeaning: '处于需退避隐遁阶段，适时退让是明智。',
            generalMeaningEn: 'Do not fight a losing battle. By withdrawing now, you protect yourself and wait for a better time to act. Distance provides perspective and power.',
            scenes: {
                career: '事业上可能需暂时退让，不宜硬拼。',
                relationship: '需给对方空间，适当后退反而更好。',
                wealth: '财务上宜保守，退出高风险投资。',
                health: '身体需要休养，减少消耗。',
                universal: '战略撤退时机，适时退隐是智慧。',
                transition: '从前进转向后退隐遁的调整阶段。'
            },
            scenesEn: {
                career: 'Resigning gracefully; avoiding office politics; stepping back from a failing project.',
                relationship: 'Giving a partner space; taking a "break" to heal; emotional detachment.',
                wealth: 'Exiting a dangerous market; liquidating assets; being extremely cautious.',
                health: 'Convalescence in isolation; reducing social stress for mental health.',
                universal: 'Strength is knowing when to walk away; preserve your peace.',
                transition: 'A wise shift from struggle to protective solitude.'
            }
        },
        reversed: {
            keywords: ['退路受阻', '无法隐遁', '逃避现实', '被困'],
            keywordsEn: ['Blocked Retreat', 'Trapped', 'Forced Confrontation', 'Cowardice', 'Hesitation'],
            imageDescription: '想退而无路，或因逃避而陷入死角。',
            imageDescriptionEn: 'The retreat is failed. You are trapped by your obligations or your own fear, unable to find the exit.',
            generalMeaning: '无法顺利退隐，或因逃避现实而更糟。',
            generalMeaningEn: 'You are struggling to let go. Either you are being pursued by problems you tried to ignore, or you are hesitating when you should be leaving, leading to a trap.',
            scenes: {
                career: '想退无路，或因逃避问题而恶化。',
                relationship: '逃避问题不能解决，反而积累矛盾。',
                wealth: '财务上想撤出但已被套牢。',
                health: '逃避健康问题只会加重病情。',
                universal: '需正视问题，逃避不是办法。',
                transition: '从想退转向无路可退的困境转折。'
            },
            scenesEn: {
                career: 'Inability to leave a toxic job; being trapped in a contract; facing the music.',
                relationship: 'Forced to deal with an ex; unable to gain space; emotional claustrophobia.',
                wealth: 'Being unable to sell a losing asset; debt collectors closing in.',
                health: 'A condition that can no longer be ignored; the bill for neglect is due.',
                universal: 'Stop running; you must turn and face the situation.',
                transition: 'A difficult shift from attempted avoidance to forced reality.'
            }
        }
    },

    // 34 - 大壮卦 (雷天大壮)
    '034': {
        id: '034',
        name: 'Da Zhuang (Great Power)',
        nameCn: '大壮卦',
        symbol: '☳☰',
        trigrams: { upper: '震', lower: '乾' },
        introduction: '雷鸣天上，声势浩大。象征强盛、力量与刚健。',
        introductionEn: 'Da Zhuang represents the Power of the Great. Thunder is high in the sky. It is a time of immense strength and influence, but warns that true power must be governed by justice and self-control.',
        upright: {
            keywords: ['强盛壮大', '刚健有力', '声势浩大', '力量'],
            keywordsEn: ['Power', 'Strength', 'Vitality', 'Influence', 'Authority', 'Righteousness'],
            imageDescription: '四阳齐升，势不可挡，如公羊冲篱。',
            imageDescriptionEn: 'A ram pushing through a hedge. The force is great; make sure it is used for the right reasons and not just as a show of ego.',
            generalMeaning: '处于强盛壮大阶段，力量充沛适合进取。',
            generalMeaningEn: 'You have the advantage. You are in a position of power. However, be careful not to bully others. Power without morality leads to its own destruction.',
            scenes: {
                career: '事业强势发展，适合积极进取。',
                relationship: '感情主动强势，需防过于霸道。',
                wealth: '财运强劲，适合积极投资。',
                health: '身体强健有力，精力充沛。',
                universal: '壮盛时期，积极进取同时保持正道。',
                transition: '进入强盛壮大的高峰阶段。'
            },
            scenesEn: {
                career: 'Rapid expansion; leading with confidence; dominating your field; successful deals.',
                relationship: 'A powerful romantic pursuit; strong attraction; taking the lead in love.',
                wealth: 'Bold financial growth; high returns; being a "power player" in business.',
                health: 'High physical power; extreme fitness; fast recovery.',
                universal: 'Use your strength to do good; do not become a tyrant.',
                transition: 'A massive shift from growth to established power.'
            }
        },
        reversed: {
            keywords: ['壮而不正', '刚愎自用', '莽撞', '暴躁'],
            keywordsEn: ['Abuse of Power', 'Aggression', 'Recklessness', 'Entanglement', 'Stubbornness'],
            imageDescription: '公羊角扣篱笆，进退维谷，因刚而受困。',
            imageDescriptionEn: 'The ram\'s horns are stuck in the fence. By using too much force and not enough wisdom, you have trapped yourself.',
            generalMeaning: '过于强势或刚愎自用，招致困顿。',
            generalMeaningEn: 'A loss of control. You have pushed too hard and now you are stuck. Your aggression or arrogance has created a situation where you can neither advance nor retreat easily.',
            scenes: {
                career: '因过于强势而树敌或出错。',
                relationship: '感情中过于霸道导致矛盾。',
                wealth: '投资过于激进可能遭受损失。',
                health: '精力过亢可能损伤身体。',
                universal: '需控制力量，强而能柔才能持久。',
                transition: '从壮盛转向衰退或受困的预警。'
            },
            scenesEn: {
                career: 'Office bullying backfiring; failed aggressive expansion; professional deadlock.',
                relationship: 'Controlling behavior ruining a bond; anger issues; a partner pushing back.',
                wealth: 'Loss through reckless gambling or ego-driven spending; financial trap.',
                health: 'Injury from overtraining; burnout; high stress leading to heart strain.',
                universal: 'Relax your grip; you are your own obstacle.',
                transition: 'A negative shift from powerful action to stuck frustration.'
            }
        }
    },

    // 35 - 晋卦 (火地晋)
    '035': {
        id: '035',
        name: 'Jin (Progress)',
        nameCn: '晋卦',
        symbol: '☲☷',
        trigrams: { upper: '离', lower: '坤' },
        introduction: '日出地上，光明渐进。象征晋升、受到赏识。',
        introductionEn: 'Jin represents Progress. The Sun (Fire) rises above the Earth, illuminating all things. It signifies advancement, being noticed for your merits, and moving into a higher position.',
        upright: {
            keywords: ['晋升进步', '前景光明', '升职', '受赏'],
            keywordsEn: ['Progress', 'Advancement', 'Recognition', 'Success', 'Clarity', 'Rising'],
            imageDescription: '太阳升起，大地沐浴光辉，前途开阔。',
            imageDescriptionEn: 'A brilliant sun ascending in the morning sky. The path ahead is clear and you are being welcomed into the light.',
            generalMeaning: '处于晋升进步阶段，前途光明受到赏识。',
            generalMeaningEn: 'Your time has come. You are stepping into a period of growth where your talents are recognized and rewarded. Keep your integrity as you rise.',
            scenes: {
                career: '事业晋升发达，可能升职加薪或获认可。',
                relationship: '感情进展顺利，关系升级。',
                wealth: '财运上升，收益增加。',
                health: '身体状态逐步改善。',
                universal: '上升进步好时期，把握机会。',
                transition: '从平稳转向晋升发达的正向转折。'
            },
            scenesEn: {
                career: 'Promotion; winning a large contract; successful public speaking; professional rising.',
                relationship: 'Relationship moving to a new stage; engagement; public acknowledgment of love.',
                wealth: 'Increasing income; financial clarity; successful asset building.',
                health: 'Rising energy levels; successful treatment; radiant physical health.',
                universal: 'Step forward; the light is shining on you.',
                transition: 'A bright shift from the shadows into high progress.'
            }
        },
        reversed: {
            keywords: ['晋升受阻', '光明蒙蔽', '退步', '不被认可'],
            keywordsEn: ['Obstacles to Growth', 'Hidden Talents', 'Regression', 'Loss of Visibility', 'Setback'],
            imageDescription: '云遮旭日，向上之路艰难。',
            imageDescriptionEn: 'The sun is obscured by thick clouds. You are working hard but no one sees you, or your path is blocked by external forces.',
            generalMeaning: '晋升进步受阻，遭遇挫折或不被认可。',
            generalMeaningEn: 'Progress is stalled. You may feel like you are moving backward or that your efforts are being ignored. It is a time to stay patient and keep your inner light burning.',
            scenes: {
                career: '事业晋升受阻，被忽视或遭遇挫折。',
                relationship: '感情进展不顺，关系停滞或倒退。',
                wealth: '财运受阻，收益减少。',
                health: '身体恢复缓慢。',
                universal: '晋升受阻，需耐心等待或调整。',
                transition: '从上升转向停滞或受阻的负面转折。'
            },
            scenesEn: {
                career: 'Passed over for promotion; project delays; lack of professional recognition.',
                relationship: 'A relationship losing its spark; feelings of being unappreciated.',
                wealth: 'Stagnant finances; income not matching effort; missed opportunities.',
                health: 'Slow recovery; feeling "clouded" or sluggish physically.',
                universal: 'Do not lose hope; the sun will rise again after the clouds pass.',
                transition: 'A discouraging shift from progress back into waiting.'
            }
        }
    },

    // 36 - 明夷卦 (地火明夷)
    '036': {
        id: '036',
        name: 'Ming Yi (Darkening of the Light)',
        nameCn: '明夷卦',
        symbol: '☷☲',
        trigrams: { upper: '坤', lower: '离' },
        introduction: '日入地中，光明受损。象征艰难时期，需韬光养晦。',
        introductionEn: 'Ming Yi represents the Darkening of the Light. The Sun (Fire) is buried under the Earth. It signifies a time of persecution or hardship where one must hide their wisdom to survive.',
        upright: {
            keywords: ['光明受损', '韬光养晦', '艰难守正', '隐藏'],
            keywordsEn: ['Censorship', 'Hardship', 'Persistence', 'Hiding your Light', 'Adversity', 'Inner Resilience'],
            imageDescription: '太阳沉入地平线之下，黑暗降临，需保护内心。',
            imageDescriptionEn: 'Darkness covering the world. The noble person hides their brilliance so as not to attract the envy or malice of others.',
            generalMeaning: '处于光明受损阶段，需韬光养晦、守正。',
            generalMeaningEn: 'A time of danger. Do not show off. Maintain your internal standards while appearing to comply with external pressure. Survival depends on your inner light.',
            scenes: {
                career: '事业处于低谷，暂时隐藏锋芒，待时。',
                relationship: '可能遇困难，需低调处理，保全。',
                wealth: '财运低迷，宜保守，不宜冒险。',
                health: '身体可能有隐疾，需关注调理。',
                universal: '韬光养晦时期，保全自身等待光明。',
                transition: '从光明转向晦暗的逆境阶段。'
            },
            scenesEn: {
                career: 'Hostile work environment; keeping your head down; working in the shadows; surviving a bad boss.',
                relationship: 'Keeping a love secret; protecting a bond from outside gossip; emotional endurance.',
                wealth: 'Protecting your assets; absolute financial caution; living simply to save.',
                health: 'Low energy; needing to "hide" and rest; treating internal issues.',
                universal: 'Trust your inner truth while the outside world is dark.',
                transition: 'A difficult shift from brilliance into survival mode.'
            }
        },
        reversed: {
            keywords: ['走出黑暗', '光明恢复', '重见天日'],
            keywordsEn: ['End of Darkness', 'Emergence', 'Truth Revealed', 'Recovery', 'New Hope'],
            imageDescription: '黎明初现，太阳即将跃出地平线。',
            imageDescriptionEn: 'The sun begins to rise from beneath the earth. The time of hiding is ending, and the truth can be spoken again.',
            generalMeaning: '晦暗时期即将过去，光明逐渐恢复。',
            generalMeaningEn: 'The danger is passing. You can finally step back into the world and show your true self. The long night of the soul is over.',
            scenes: {
                career: '事业困境解除，机会重现。',
                relationship: '感情困难过去，关系好转。',
                wealth: '财运开始回升。',
                health: '健康状况开始改善。',
                universal: '困境即将过去，保持信心迎光明。',
                transition: '从晦暗转向光明恢复的正向转折。'
            },
            scenesEn: {
                career: 'End of workplace persecution; finding a new, better role; ideas finally being heard.',
                relationship: 'Secrets coming out; clearing a major misunderstanding; moving back into the light together.',
                wealth: 'Financial recovery; ending a period of extreme lack.',
                health: 'Healing begins; mental fog lifts; return of physical strength.',
                universal: 'The dawn has come; step out of your shell.',
                transition: 'A hopeful shift from survival back into active life.'
            }
        }
    },

    // 37 - 家人卦 (风火家人)
    '037': {
        id: '037',
        name: 'Jia Ren (The Family)',
        nameCn: '家人卦',
        symbol: '☴☲',
        trigrams: { upper: '巽', lower: '离' },
        introduction: '风自火出，家道兴盛。象征家庭和谐、各司其职。',
        introductionEn: 'Jia Ren represents the Family or Household. It emphasizes the importance of internal order, mutual respect, and the roles individuals play within a domestic or close-knit unit.',
        upright: {
            keywords: ['家庭和睦', '内外有序', '齐家', '责任'],
            keywordsEn: ['Family', 'Domestic Order', 'Duty', 'Cooperation', 'Household', 'Warmth'],
            imageDescription: '温暖的炉火产生微风，象征家中温馨的律动。',
            imageDescriptionEn: 'Fire within, Wind blowing outward. It is the heat of the hearth creating life and order within the home walls.',
            generalMeaning: '处于家庭和睦阶段，家事顺遂，亲情融洽。',
            generalMeaningEn: 'Focus on your home and inner circle. When the home is in order, all external affairs succeed. Respect for roles and boundaries brings lasting peace.',
            scenes: {
                career: '事业如家般需各司其职，团队和谐。',
                relationship: '感情入家庭阶段，生活美满。',
                wealth: '家庭财务管理得当，共同富裕。',
                health: '家人健康需关注，家庭饮食养生。',
                universal: '家庭事务是重点，齐家可兴业。',
                transition: '进入家庭生活和睦的稳定阶段。'
            },
            scenesEn: {
                career: 'Treating a team like a family; clarity of roles; stable office environment.',
                relationship: 'Marriage; strong domestic bond; nesting; mutual respect between partners.',
                wealth: 'Family wealth; saving for a home; shared financial security.',
                health: 'Nurturing through home cooking; family health history; emotional support.',
                universal: 'Take care of your foundations; the home is the center of your power.',
                transition: 'A stabilizing shift toward communal and domestic harmony.'
            }
        },
        reversed: {
            keywords: ['家庭不和', '内部矛盾', '失序', '分裂'],
            keywordsEn: ['Domestic Conflict', 'Disorganization', 'Betrayal of Duty', 'Family Feud', 'Alienation'],
            imageDescription: '炉火熄灭或风势过烈，家中秩序崩坏。',
            imageDescriptionEn: 'A broken hearth. Arguments and lack of order are causing the family structure to crumble from within.',
            generalMeaning: '家庭不和或内部矛盾，需化解修复。',
            generalMeaningEn: 'Disorder in your private life. There may be power struggles or a lack of communication with those closest to you. Fix the inner circle first.',
            scenes: {
                career: '团队内部不和，需协调。',
                relationship: '感情或家庭出矛盾，需沟通化解。',
                wealth: '家庭财务管理不善或因财起争。',
                health: '家庭成员健康问题需关注。',
                universal: '需修复关系，化解内部矛盾。',
                transition: '从和睦转向不和的负面转折。'
            },
            scenesEn: {
                career: 'Internal power struggles; breakdown of workplace hierarchy; lack of team loyalty.',
                relationship: 'Domestic disputes; feeling like an outsider at home; neglecting family duties.',
                wealth: 'Money arguments with relatives; inheritance disputes; financial chaos.',
                health: 'Stress from home life affecting well-being; neglecting family health needs.',
                universal: 'Address the rift in your circle; you cannot hide from the family truth.',
                transition: 'A negative shift from unity to internal discord.'
            }
        }
    },

    // 38 - 睽卦 (火泽睽)
    '038': {
        id: '038',
        name: 'Kui (Opposition)',
        nameCn: '睽卦',
        symbol: '☲☱',
        trigrams: { upper: '离', lower: '离' },
        introduction: '火炎上，泽润下。象征差异、分歧与不同视角。',
        introductionEn: 'Kui represents Opposition or Estrangement. Fire goes up and the Lake goes down. It signifies a time when people are moving in opposite directions, requiring a focus on small commonalities to succeed.',
        upright: {
            keywords: ['睽违乖离', '意见相左', '不同视角', '独立'],
            keywordsEn: ['Opposition', 'Difference', 'Individuality', 'Small Gains', 'Perspectives', 'Neutrality'],
            imageDescription: '二女同居而志不同，象征虽在一起却各有想法。',
            imageDescriptionEn: 'Two forces pulling apart. They are separate and distinct, but can still coexist if they respect their differences.',
            generalMeaning: '处于存在分歧的阶段，小事可成，大事需慎。',
            generalMeaningEn: 'You are dealing with people who do not agree with you. Do not try to force big changes. Focus on small areas of agreement and maintain your own identity.',
            scenes: {
                career: '可能有意见分歧，小项目成，大项目需协调。',
                relationship: '存在小分歧，需理解和包容。',
                wealth: '小投资可行，大投资需谨慎。',
                health: '小问题可自愈，大问题需就医。',
                universal: '存在分歧但不严重，包容求共存。',
                transition: '进入存在分歧但尚可调和的阶段。'
            },
            scenesEn: {
                career: 'Varying opinions in a meeting; specialized niche roles; small progress in a divided team.',
                relationship: 'Opposite personalities attracting; minor misunderstandings; needing space.',
                wealth: 'Small financial wins through unique niche investments; avoiding large joint ventures.',
                health: 'Odd symptoms; seeing different specialists; needing a second opinion.',
                universal: 'Accept the difference; harmony doesn\'t mean being the same.',
                transition: 'A shift from unity to a healthy recognition of individuality.'
            }
        },
        reversed: {
            keywords: ['分歧加剧', '关系破裂', '无法调和', '对立'],
            keywordsEn: ['Deep Conflict', 'Hostility', 'Alienation', 'Isolation', 'Mistrust'],
            imageDescription: '火泽彻底背离，无法共存，分裂加剧。',
            imageDescriptionEn: 'The distance becomes too great. Mutual suspicion leads to a complete breakdown of any possible cooperation.',
            generalMeaning: '分歧加剧无法调和，可能导致破裂。',
            generalMeaningEn: 'Serious estrangement. Suspicion and misunderstanding have turned into open hostility. It is very difficult to find common ground now.',
            scenes: {
                career: '分歧严重，导致合作破裂。',
                relationship: '分歧无法化解，分手或冷战。',
                wealth: '判断分歧导致损失或错失。',
                health: '病情可能因误判而加重。',
                universal: '需努力弥合分歧，否则可能决裂。',
                transition: '从小分歧转向决裂的负面转折。'
            },
            scenesEn: {
                career: 'Irreconcilable professional differences; failed partnerships; open office warfare.',
                relationship: 'Severe bitterness; a toxic cycle of suspicion; permanent separation.',
                wealth: 'Financial loss through lawsuits or bitter disputes; total lack of cooperation.',
                health: 'Mental stress from isolation; worsening conditions due to neglected care.',
                universal: 'Beware of your own suspicion; it is poisoning the well.',
                transition: 'A negative shift from simple difference to total alienation.'
            }
        }
    },

    // 39 - 蹇卦 (水山蹇)
    '039': {
        id: '039',
        name: 'Jian (Obstruction)',
        nameCn: '蹇卦',
        symbol: '☵☶',
        trigrams: { upper: '坎', lower: '艮' },
        introduction: '前有险水，后有高山。象征行路艰难、进退维谷。',
        introductionEn: 'Jian represents Obstruction. Water (Danger) is ahead and Mountain (Stillness) is behind. It signifies a situation where movement is blocked and a new strategy or help is required.',
        upright: {
            keywords: ['行路艰难', '险阻重重', '知难而进', '迂回'],
            keywordsEn: ['Hurdles', 'Obstacles', 'Limping', 'Difficulty', 'Need for Help', 'Patience'],
            imageDescription: '跛行于崎岖山路，且面临激流。',
            imageDescriptionEn: 'A traveler on a mountain path facing a deep abyss. He cannot go forward safely and cannot go back easily.',
            generalMeaning: '处于行路艰难阶段，险阻重重需迂回或求助。',
            generalMeaningEn: 'You are facing significant hurdles. Do not try to force your way through. Stop, re-evaluate, and seek out a wise friend or expert to help you navigate the danger.',
            scenes: {
                career: '事业遇重重障碍，需迂回或求助。',
                relationship: '感情发展遇阻，需耐心克服。',
                wealth: '财务遇到困难，需谨慎应对。',
                health: '健康可能遇到较难治愈的问题。',
                universal: '艰难时期，知难而进或迂回绕行。',
                transition: '进入险阻艰难的阶段。'
            },
            scenesEn: {
                career: 'A stalled career; difficult project environment; need for a mentor or outside consultant.',
                relationship: 'A relationship facing heavy external pressure; social or financial obstacles to love.',
                wealth: 'Frozen assets; financial "limping"; very slow progress in income.',
                health: 'Mobility issues; chronic pain; needing a long-term therapy plan.',
                universal: 'Accept the delay; it is a time to improve your inner strength.',
                transition: 'A sobering shift from activity into a major roadblock.'
            }
        },
        reversed: {
            keywords: ['险阻加重', '寸步难行', '求助无门', '停滞'],
            keywordsEn: ['Complete Blockage', 'Peril', 'Despair', 'Missing the Way'],
            imageDescription: '风雪封山，险水暴涨。前路彻底断绝。',
            imageDescriptionEn: 'The path is completely gone. Attempting to move now will only lead to total disaster or injury.',
            generalMeaning: '险阻加重，几乎无法前进，需等待或彻底变。',
            generalMeaningEn: 'Total obstruction. Your refusal to wait has led you into a trap. You are exhausted and lost. You must stop everything and wait for a total change of season.',
            scenes: {
                career: '事业完全受阻，可能需放弃或转向。',
                relationship: '感情障碍无法克服，需重新考虑。',
                wealth: '财务陷入泥潭，需外部援助。',
                health: '健康严重，需专业帮助。',
                universal: '几乎无法前进，需等待或求助。',
                transition: '从艰难转向绝境的危险转折。'
            },
            scenesEn: {
                career: 'Professional bankruptcy; a career path that has completely ended; utter stagnation.',
                relationship: 'A relationship that is truly impossible; being crushed by external circumstances.',
                wealth: 'Absolute financial freeze; inability to move money; severe poverty.',
                health: 'Critical illness; physical paralysis; total lack of vitality.',
                universal: 'Surrender to the stillness; you cannot move right now.',
                transition: 'A dark shift from difficulty to total paralysis.'
            }
        }
    },

    // 40 - 解卦 (雷水解)
    '040': {
        id: '040',
        name: 'Xie (Deliverance)',
        nameCn: '解卦',
        symbol: '☳☵',
        trigrams: { upper: '震', lower: '坎' },
        introduction: '雷雨过后，云开雾散。象征险难消解、获得释放。',
        introductionEn: 'Xie represents Deliverance or Release. Like a thunderstorm clearing a heavy summer afternoon, it signifies the removal of tension and the resolution of long-term problems.',
        upright: {
            keywords: ['险难消解', '困境解除', '释放', '解脱'],
            keywordsEn: ['Deliverance', 'Release', 'Freedom', 'Forgiveness', 'Solution', 'Action'],
            imageDescription: '惊雷震散了阴云，雨水冲刷了污垢。',
            imageDescriptionEn: 'Thunder and rain breaking the tension. The obstacles dissolve, and the world feels fresh and open again.',
            generalMeaning: '处于困难解除阶段，险阻消解，事务畅通。',
            generalMeaningEn: 'The struggle is over. Move quickly to return to your normal life. Forgive past hurts and enjoy the new freedom. The way is finally clear.',
            scenes: {
                career: '事业困境解除，障碍消失，恢复顺利。',
                relationship: '矛盾化解，误解消除，关系恢复。',
                wealth: '财务困难解除，资金流畅。',
                health: '健康问题解决，康复顺利。',
                universal: '困境解除，把握时机快速行动。',
                transition: '从困境转向解脱的正向转折。'
            },
            scenesEn: {
                career: 'Solving a major problem; end of a workplace feud; clearing a deadline; relief.',
                relationship: 'Making up after a big fight; understanding a partner\'s secret; emotional release.',
                wealth: 'Debt relief; a financial knot being untied; money starts moving again.',
                health: 'The end of a disease cycle; finding the right cure; mental relief.',
                universal: 'Forgive and move on; the path is wide open now.',
                transition: 'A liberating shift from tension to absolute relief.'
            }
        },
        reversed: {
            keywords: ['无法解脱', '困境延续', '解决不彻底', '反复'],
            keywordsEn: ['Incomplete Release', 'Lingering Trouble', 'Recurrence', 'Hesitation'],
            imageDescription: '雨虽落而云不散，忧虑仍然笼罩。',
            imageDescriptionEn: 'The storm didn\'t clear the air. You are out of immediate danger, but the root cause remains, waiting to return.',
            generalMeaning: '困境无法完全解除，问题可能反复。',
            generalMeaningEn: 'A false sense of freedom. You have solved the surface problem but not the deep issue. Be alert—the trouble may return if you are not thorough.',
            scenes: {
                career: '事业困境解决不彻底，可能反复。',
                relationship: '矛盾表面化解实则未根除。',
                wealth: '财务暂时缓解但未根本解决。',
                health: '健康问题可能复发。',
                universal: '需彻底解决，否则会反复。',
                transition: '从看似解脱转向问题延续的波折。'
            },
            scenesEn: {
                career: 'A temporary fix for a big work problem; the threat of a returning issue.',
                relationship: 'Patching things up without a real apology; lingering resentment.',
                wealth: 'Borrowing money to pay debt; temporary financial ease followed by more stress.',
                health: 'Temporary recovery followed by a relapse; masking symptoms.',
                universal: 'Do not celebrate too early; ensure the problem is truly dead.',
                transition: 'A disappointing shift from relief back to lingering stress.'
            }
        }
    },

    // 41 - 损卦 (山泽损)
    '041': {
        id: '041',
        name: 'Sun (Decrease)',
        nameCn: '损卦',
        symbol: '☶☱',
        trigrams: { upper: '艮', lower: '兑' },
        introduction: '损下益上，节制欲望。象征通过适当的减损与自我约束来获得长远的吉祥。',
        introductionEn: 'Sun represents Decrease or Reduction. It symbolizes the wisdom of reducing the lower (desires, excess) to increase the higher (spirit, integrity). It teaches that simplification leads to success.',
        upright: {
            keywords: ['适当减损', '损己益人', '节制', '简化', '克制欲望'],
            keywordsEn: ['Decrease', 'Reduction', 'Restraint', 'Simplification', 'Sacrifice', 'Discipline'],
            imageDescription: '泽水减损而滋润高山。通过局部的减损，成就整体的稳固。',
            imageDescriptionEn: 'The lake losing water to moisten the mountain. By decreasing excess at the bottom, the foundation above is strengthened.',
            generalMeaning: '处于需要适当减损的阶段，简化生活、克制欲望可获吉祥。',
            generalMeaningEn: 'A time to cut back. Less is more. By letting go of unnecessary burdens or selfish desires, you open the door to true spiritual and material growth.',
            scenes: {
                career: '事业上可能需牺牲短期利益，换取长远发展。',
                relationship: '感情中适当付出牺牲，可增进彼此关系。',
                wealth: '财务需节制消费，适度投入可获长远回报。',
                health: '减少不良嗜好，克制欲望有益身体。',
                universal: '当下是节制减损的时期，以小损换大益。',
                transition: '从扩张阶段转向节制收敛的调整。'
            },
            scenesEn: {
                career: 'Short-term sacrifice for long-term strategy; cutting costs; lean management.',
                relationship: 'Selfless giving; letting go of ego in an argument; building a deeper bond.',
                wealth: 'Budgeting; cutting unnecessary luxury; investing in education over items.',
                health: 'Detoxification; fasting; reducing stress by doing less; self-discipline.',
                universal: 'Reduce the complex to the simple; your power lies in your restraint.',
                transition: 'A disciplined shift from excess toward essential focus.'
            }
        },
        reversed: {
            keywords: ['损失过度', '得不偿失', '吝啬', '耗损'],
            keywordsEn: ['Excessive Loss', 'Imbalance', 'Miserliness', 'Fruitless Sacrifice', 'Depletion'],
            imageDescription: '减损过度导致泽枯山秃，失去了本该保有的资源。',
            imageDescriptionEn: 'The lake is dry and the mountain is barren. The reduction was handled poorly, leading to unnecessary depletion.',
            generalMeaning: '减损过度导致得不偿失，或牺牲没有价值，陷入空耗。',
            generalMeaningEn: 'Mismanaged decrease. You are either losing too much through negligence or holding on so tightly (stinginess) that you cause an internal rot.',
            scenes: {
                career: '事业上牺牲太多却无回报，资源耗尽。',
                relationship: '过度付出导致失衡，感到委屈或被掏空。',
                wealth: '过度节俭影响质量，或盲目投入颗粒无收。',
                health: '过度节食或锻炼导致身体受损。',
                universal: '需评估减损是否值得，避免无谓消耗。',
                transition: '从适度减损转向过度损耗的负面转折。'
            },
            scenesEn: {
                career: 'Exhaustion of resources; cutting staff to the point of failure; bad ROI.',
                relationship: 'Giving until you have nothing left; emotional burnout; unhealthy martyrdom.',
                wealth: 'Financial bleeding; "penny wise and pound foolish" behavior.',
                health: 'Weakness from lack of nutrition; over-restricting leading to illness.',
                universal: 'Stop the leak; you are losing vital energy for no gain.',
                transition: 'A damaging shift from discipline to total depletion.'
            }
        }
    },

    // 42 - 益卦 (风雷益)
    '042': {
        id: '042',
        name: 'Yi (Increase)',
        nameCn: '益卦',
        symbol: '☴☳',
        trigrams: { upper: '巽', lower: '震' },
        introduction: '风雷相益，损上益下。象征全面增益、互利共赢的成长期。',
        introductionEn: 'Yi represents Increase or Expansion. Wind and Thunder reinforce each other. It symbolizes the higher (leaders) giving to the lower (people), creating universal progress and abundance.',
        upright: {
            keywords: ['增益进步', '互利共赢', '收获', '成长', '贵人'],
            keywordsEn: ['Increase', 'Growth', 'Benefit', 'Expansion', 'Prosperity', 'Assistance'],
            imageDescription: '风助雷声，雷助风势，万物受其滋养而繁荣。',
            imageDescriptionEn: 'Wind and Thunder combined in a powerful storm that nourishes the earth. It is a time of rapid, beneficial development.',
            generalMeaning: '处于增益进步的阶段，有贵人相助，适合积极行动。',
            generalMeaningEn: 'The wind is at your back. It is a supreme time for action, starting new ventures, and seeking improvement. What you do now will bring great benefit.',
            scenes: {
                career: '事业获增益，可能有贵人相助或获得晋升。',
                relationship: '感情互利互助，关系更加亲密。',
                wealth: '财运增长，收益增加，利于投资。',
                health: '身体状态改善，活力明显增加。',
                universal: '当下是增益的好时期，积极行动有收获。',
                transition: '进入全面增益与发展的正向阶段。'
            },
            scenesEn: {
                career: 'Promotion; expansion of a business; gaining new skills; a very productive period.',
                relationship: 'Growing together; mutual inspiration; a relationship that adds value to life.',
                wealth: 'Increased income; profitable deals; a time of financial harvest.',
                health: 'Increased vitality; successful strengthening; physical blossoming.',
                universal: 'The universe is giving to you; use this energy to move forward.',
                transition: 'A powerful shift from preparation to rapid expansion.'
            }
        },
        reversed: {
            keywords: ['增益受阻', '得而复失', '过度扩张', '失去帮助'],
            keywordsEn: ['Halted Growth', 'Greed', 'Wasted Benefit', 'Selfishness', 'Decline'],
            imageDescription: '风雷不调，增益变为虚耗，或因贪婪而损。',
            imageDescriptionEn: 'The storm is destructive rather than nourishing. Benefit is being drained away through selfishness or poor timing.',
            generalMeaning: '增益受阻或因贪多而失去，需要节制。',
            generalMeaningEn: 'A warning against greed. By trying to take too much for yourself, you are actually causing a decrease. The flow of benefit has stopped.',
            scenes: {
                career: '事业增长受阻，或因扩张过度导致问题。',
                relationship: '感情因过度索取或私心而失衡。',
                wealth: '财富增长受阻，或出现得而复失。',
                health: '身体可能因过度补养或放纵而不适。',
                universal: '需适度，过度追求增益反而有害。',
                transition: '从增益转向受阻或损耗的负面转折。'
            },
            scenesEn: {
                career: 'Failed expansion; loss of a key supporter; stagnation in growth.',
                relationship: 'Selfishness in a partnership; emotional drain; taking without giving.',
                wealth: 'Financial loss through over-leveraging; missing a profit window.',
                health: 'Complications from over-treatment; loss of energy; metabolic issues.',
                universal: 'Do not be greedy; you are chasing hollow gains.',
                transition: 'A disappointing shift from prosperity toward waste.'
            }
        }
    },

    // 43 - 夬卦 (泽天夬)
    '043': {
        id: '043',
        name: 'Guai (Breakthrough)',
        nameCn: '夬卦',
        symbol: '☱☰',
        trigrams: { upper: '兑', lower: '乾' },
        introduction: '泽上于天，决断突破。象征排除最后阻碍、果断采取行动。',
        introductionEn: 'Guai represents Breakthrough or Resoluteness. The lake has risen to the sky and is ready to burst forth as rain. It signifies the final decision to remove a lingering obstacle or evil.',
        upright: {
            keywords: ['决断突破', '排除障碍', '果断行动', '清除', '胜利'],
            keywordsEn: ['Breakthrough', 'Resolution', 'Decisiveness', 'Clearing', 'Removal of Evil'],
            imageDescription: '五阳决断一阴，如洪水决堤，势不可挡。',
            imageDescriptionEn: 'The dam is about to break. A powerful, focused force is ready to sweep away the last remaining blockage.',
            generalMeaning: '处于需要果断决断的阶段，排除障碍，势在必行。',
            generalMeaningEn: 'The time for talk is over. You must make a firm decision and act with resolve. Be public and honest about your intentions to ensure success.',
            scenes: {
                career: '事业上需果断决策，排除阻碍。',
                relationship: '需明确态度，断绝不良关系或纠缠。',
                wealth: '需果断决策，清理不良资产。',
                health: '健康问题需果断治疗，排除病根。',
                universal: '当下是果断决断的时机，排除障碍向前。',
                transition: '进入果断突破与清除障碍的关键期。'
            },
            scenesEn: {
                career: 'Making a final executive decision; clearing workplace obstacles; decisive leadership.',
                relationship: 'Cutting ties with a toxic person; choosing a definitive path in love; honesty.',
                wealth: 'Settling accounts; getting out of a bad investment; bold financial move.',
                health: 'A decisive medical procedure; successfully removing a harmful habit.',
                universal: 'Do not hesitate; the path to victory requires a firm hand.',
                transition: 'A sharp shift from tension to sudden resolution.'
            }
        },
        reversed: {
            keywords: ['决断受阻', '优柔寡断', '无法突破', '小人反扑'],
            keywordsEn: ['Indecision', 'Failed Breakthrough', 'Lingering Trouble', 'Compromise', 'Danger'],
            imageDescription: '决断失败，水未能决口反而造成淤积。',
            imageDescriptionEn: 'The breakthrough is half-hearted. The old problems remain because you were not firm enough in your resolve.',
            generalMeaning: '决断受阻或因优柔寡断错失时机，存在风险。',
            generalMeaningEn: 'A warning of weakness. You are hesitating at the critical moment. This allows the very obstacle you were trying to remove to regain its strength.',
            scenes: {
                career: '事业决策受阻，或因犹豫错失机会。',
                relationship: '无法做出决断，关系陷入僵持。',
                wealth: '财务决策失误或因贪婪而错失良机。',
                health: '治疗决策延误，导致病情反复。',
                universal: '需克服犹豫，不彻底的决断后患无穷。',
                transition: '从决断转向受阻或动荡的负面转折。'
            },
            scenesEn: {
                career: 'Muddled management; missing a critical market window; workplace sabotage.',
                relationship: 'Ambiguity in love; unable to say no; returning to a bad dynamic.',
                wealth: 'Financial indecision; being tricked by small details; wealth slipping away.',
                health: 'Procrastinating on health needs; lingering symptoms.',
                universal: 'Half-measures will avail you nothing; be firm or be defeated.',
                transition: 'A messy shift from potential breakthrough back into conflict.'
            }
        }
    },

    // 44 - 姤卦 (天风姤)
    '044': {
        id: '044',
        name: 'Gou (Coming to Meet)',
        nameCn: '姤卦',
        symbol: '☰☴',
        trigrams: { upper: '乾', lower: '巽' },
        introduction: '天下有风，不期而遇。象征偶然的邂逅、潜伏的危机与诱惑。',
        introductionEn: 'Gou represents Coming to Meet. A single Yin line appears beneath five Yang lines. It signifies a sudden, unexpected encounter or the subtle entry of a dangerous influence.',
        upright: {
            keywords: ['不期而遇', '邂逅', '机缘', '诱惑', '警觉'],
            keywordsEn: ['Unexpected Encounter', 'Temptation', 'Seduction', 'Sudden Meeting', 'Subtle Danger'],
            imageDescription: '风行天下，无所不至，万物相遇但阴气初萌。',
            imageDescriptionEn: 'Wind blowing under Heaven. It reaches everywhere. It is a meeting that occurs by chance, carrying both beauty and a warning.',
            generalMeaning: '处于不期而遇的阶段，有偶然机缘，需警惕诱惑。',
            generalMeaningEn: 'A powerful new element is entering your life. While it may seem attractive, do not rush into a commitment. Observe its true nature before joining with it.',
            scenes: {
                career: '事业上有意外机会，但需谨慎评估。',
                relationship: '感情上有偶遇，但需看清对方本质。',
                wealth: '财务上有突然机会，但需防诱饵。',
                health: '可能有突发状况，需及时应对。',
                universal: '有偶遇机缘，但需保持高度警觉。',
                transition: '进入偶遇与诱惑并存的敏感期。'
            },
            scenesEn: {
                career: 'A sudden job offer or partnership; be wary of "too good to be true" deals.',
                relationship: 'A magnetic but potentially toxic attraction; sudden romance; short-term passion.',
                wealth: 'Tempting financial shortcuts; sudden opportunity with hidden strings.',
                health: 'Sudden onset of symptoms; reacting to environmental changes.',
                universal: 'Do not ignore the small thing that has arrived; it has power.',
                transition: 'A subtle shift from absolute control to unexpected encounter.'
            }
        },
        reversed: {
            keywords: ['诱惑陷阱', '不良邂逅', '被骗', '诡计'],
            keywordsEn: ['Deceptive Meeting', 'Scandal', 'Treachery', 'Mistaken Choice', 'Trap'],
            imageDescription: '一阴潜入极深，防不胜防，诱惑变成灾殃。',
            imageDescriptionEn: 'The hidden influence has taken root. The accidental meeting has turned into a source of trouble or regret.',
            generalMeaning: '偶遇中隐藏陷阱，或因轻信而陷入被动。',
            generalMeaningEn: 'Misfortune through lack of discernment. You have let something into your life that is now causing harm. It is a time to cut your losses and regain your distance.',
            scenes: {
                career: '事业机会可能是陷阱，有人暗算。',
                relationship: '邂逅可能是不良缘分，甚至遭遇欺骗。',
                wealth: '财务上遭遇诈骗或诱导性损失。',
                health: '因疏忽导致病情突变或意外。',
                universal: '需高度警惕，不要被表象迷惑。',
                transition: '从机缘转向陷阱的危险转折。'
            },
            scenesEn: {
                career: 'Being undermined by a new associate; falling for a scam; professional scandal.',
                relationship: 'Discovering a partner\'s hidden agenda; unfaithful behavior; regretful encounter.',
                wealth: 'Financial trap; being "sold" a lie; sudden loss due to misplaced trust.',
                health: 'Infection; ignoring a small symptom until it becomes a crisis.',
                universal: 'The predator is in the house; protect your boundaries.',
                transition: 'A sharp shift from curiosity into regret.'
            }
        }
    },

    // 45 - 萃卦 (泽地萃)
    '045': {
        id: '045',
        name: 'Cui (Gathering Together)',
        nameCn: '萃卦',
        symbol: '☱☷',
        trigrams: { upper: '兑', lower: '坤' },
        introduction: '泽聚地上，精英荟萃。象征资源汇集、人脉凝聚与集体力量。',
        introductionEn: 'Cui represents Gathering Together or Collection. The Lake is above the Earth. It signifies the gathering of people or resources around a common center of power or faith.',
        upright: {
            keywords: ['聚集荟萃', '凝聚力', '集中', '资源汇集'],
            keywordsEn: ['Gathering', 'Assembly', 'Massing', 'Cohesion', 'Unity', 'Community', 'Abundance'],
            imageDescription: '泽水汇聚于广阔大地，万物聚集。',
            imageDescriptionEn: 'Water gathering to form a great lake upon the fertile land. It is a scene of social abundance and shared purpose.',
            generalMeaning: '处于资源与人脉汇聚阶段，适合开展团体活动。',
            generalMeaningEn: 'A time for community. You are bringing people and ideas together. To succeed, you need a strong central vision and high moral standards to keep the group united.',
            scenes: {
                career: '适合团队合作，资源汇集有利大发展。',
                relationship: '适合团聚、婚礼等，人脉助力感情。',
                wealth: '资金汇聚，适合集中投资或筹集资金。',
                health: '可参加集体活动，心情愉快有助恢复。',
                universal: '聚集凝聚的好时期，团结力量大。',
                transition: '从分散状态转向聚集团结的正向转折。'
            },
            scenesEn: {
                career: 'Mergers; successful team building; centralized power; professional gatherings.',
                relationship: 'Large family events; meeting someone through a group; social success in love.',
                wealth: 'Pooling resources; crowdfunding; high-volume financial growth.',
                health: 'Group healing; belonging to a wellness community; mental abundance.',
                universal: 'Unite with others; find your center of gravity.',
                transition: 'A positive shift from individual effort to collective synergy.'
            }
        },
        reversed: {
            keywords: ['人心涣散', '资源分散', '散伙', '混乱'],
            keywordsEn: ['Dispersal', 'Discord', 'Scattered Resources', 'Loneliness', 'Broken Group'],
            imageDescription: '泽水溃决，资源外溢，众人离心。',
            imageDescriptionEn: 'The lake wall breaks; the water leaks away. The center cannot hold the group together.',
            generalMeaning: '聚集失败或人心涣散，难以凝聚力量。',
            generalMeaningEn: 'A lack of unity. You are trying to gather people who are not in alignment. The group is falling apart due to a lack of shared goals or trust.',
            scenes: {
                career: '团队凝聚力下降，合作严重受阻。',
                relationship: '聚会取消，或在人群中感到孤立。',
                wealth: '资金分散，无法有效利用，小额损耗。',
                health: '缺乏支持系统，感到力不从心。',
                universal: '需重建凝聚力，化解内部矛盾。',
                transition: '从凝聚转向涣散的负面转折。'
            },
            scenesEn: {
                career: 'Disorganized team; internal cliques; failure to reach a consensus.',
                relationship: 'Feeling alone in a crowd; broken social ties; family arguments.',
                wealth: 'Scattered assets; financial waste through lack of focus; group debt.',
                health: 'Scattered focus on wellness; lack of motivation from isolation.',
                universal: 'Rebuild the core; without a center, there is no assembly.',
                transition: 'A messy shift from unity toward fragmentation.'
            }
        }
    },

    // 46 - 升卦 (地风升)
    '046': {
        id: '046',
        name: 'Sheng (Pushing Upward)',
        nameCn: '升卦',
        symbol: '☷☴',
        trigrams: { upper: '坤', lower: '巽' },
        introduction: '木生土中，稳步上升。象征通过不懈努力实现的渐进式晋升。',
        introductionEn: 'Sheng represents Pushing Upward or Ascending. Like a tree growing from the earth, it signifies steady, organic growth and climbing toward a higher goal through persistence.',
        upright: {
            keywords: ['渐进上升', '稳步发展', '晋升', '成长'],
            keywordsEn: ['Ascension', 'Promotion', 'Effort', 'Growth', 'Advancement', 'Success'],
            imageDescription: '柔木从地下破土而出，顺势向上生长。',
            imageDescriptionEn: 'Wood (Wind/Plants) growing within the Earth. The upward movement is quiet, persistent, and supported by the environment.',
            generalMeaning: '处于稳步上升阶段，循序渐进终获大成。',
            generalMeaningEn: 'This is your time to rise. Your hard work has prepared the way. Seek out influential people and continue your steady efforts; success is assured.',
            scenes: {
                career: '事业稳步上升，适合晋升或业务扩张。',
                relationship: '感情稳步进展，关系逐渐升温。',
                wealth: '财运稳步增长，资产积累见效。',
                health: '身体状态逐步改善，精力回升。',
                universal: '稳步上升好时期，坚持就是胜利。',
                transition: '从平稳阶段进入稳步发展的正向转折。'
            },
            scenesEn: {
                career: 'Consistent promotion; building a career step-by-step; moving up the corporate ladder.',
                relationship: 'A relationship reaching new heights; growing trust and depth.',
                wealth: 'Compound interest; steady accumulation of capital; long-term financial rise.',
                health: 'Consistent improvement in fitness; rising energy levels.',
                universal: 'Keep climbing; each small step is bringing you closer to the top.',
                transition: 'A hopeful shift from potential into actual growth.'
            }
        },
        reversed: {
            keywords: ['上升受阻', '停滞不前', '退步', '受挫'],
            keywordsEn: ['Blocked Ascent', 'Stagnation', 'Over-reaching', 'Failure to Rise'],
            imageDescription: '木被压在重土之下，生长乏力，向上受阻。',
            imageDescriptionEn: 'The plant is struggling against a stone. The upward movement is forced or the path is blocked by external limits.',
            generalMeaning: '上升势头受阻，可能因操之过急或方向错误。',
            generalMeaningEn: 'Stalled progress. You may be trying to rise too fast without the necessary foundation, or your path is blocked. It is a time to re-evaluate your efforts.',
            scenes: {
                career: '事业发展受阻，晋升机会推迟。',
                relationship: '感情进展停滞，难以进入下一阶段。',
                wealth: '财运增长受阻，收益不如预期。',
                health: '身体恢复缓慢，感到力不从心。',
                universal: '需调整策略，不可强行求升。',
                transition: '从上升转向停滞或受挫的调整。'
            },
            scenesEn: {
                career: 'A dead-end job; promotion cancelled; stagnation in professional rank.',
                relationship: 'Relationship hitting a ceiling; inability to commit further.',
                wealth: 'Financial plateau; investments failing to grow; lack of momentum.',
                health: 'Stagnant recovery; energy hitting a low point.',
                universal: 'Stop pushing; the way up is closed for now.',
                transition: 'A discouraging shift from progress to standstill.'
            }
        }
    },

    // 47 - 困卦 (泽水困)
    '047': {
        id: '047',
        name: 'Kun (Oppression)',
        nameCn: '困卦',
        symbol: '☱☵',
        trigrams: { upper: '兑', lower: '坎' },
        introduction: '泽中无水，困顿穷迫。象征生命中最艰难的时刻，需守正。',
        introductionEn: 'Kun represents Oppression or Exhaustion. The water has leaked out of the lake. It signifies a time of extreme hardship, restriction, and being misunderstood, where one\'s words are ignored.',
        upright: {
            keywords: ['困顿穷迫', '艰难困苦', '守正待时', '磨练'],
            keywordsEn: ['Oppression', 'Exhaustion', 'Restriction', 'Poverty', 'Hardship', 'Silence'],
            imageDescription: '泽无水而干涸，象征资源枯竭、英雄无用武之地。',
            imageDescriptionEn: 'A dry lake bed. A person trapped within walls. It is a time of testing where only character remains.',
            generalMeaning: '处于极度困顿阶段，言语无信，需坚守信念。',
            generalMeaningEn: 'A severe test of character. Do not try to explain yourself to others; they will not believe you. Stay true to your inner light and endure; this too will pass.',
            scenes: {
                career: '事业陷困境，资源匮乏，需艰苦守正。',
                relationship: '可能遇困难期，相互理解受到考验。',
                wealth: '财务极度紧张，资金周转困难。',
                health: '身体虚弱，需关注生命力的维护。',
                universal: '黑暗时期，唯有守正待时方可获吉。',
                transition: '进入极其艰难困顿的低谷阶段。'
            },
            scenesEn: {
                career: 'Professional exhaustion; lack of funding; being silenced in the office; the lowest point.',
                relationship: 'Severe relationship strain; isolation from a partner; total misunderstanding.',
                wealth: 'Financial ruin; total lack of cash flow; severe debt.',
                health: 'Critical illness; exhaustion; feeling completely drained physically.',
                universal: 'Hold on to your soul; when everything is taken, only who you are remains.',
                transition: 'A heavy shift from normal life into an oppressive crisis.'
            }
        },
        reversed: {
            keywords: ['困境加深', '绝望', '无法脱困', '绝路'],
            keywordsEn: ['Deepening Despair', 'Fatalism', 'Failed Escape', 'Permanent Loss'],
            imageDescription: '泽底崩裂，生机全无，陷入无解之境。',
            imageDescriptionEn: 'The walls close in further. A refusal to accept the spiritual lesson of hardship leads to total internal collapse.',
            generalMeaning: '困境进一步加深，难以脱困，需大勇气改变。',
            generalMeaningEn: 'Surrendering to despair. Instead of using the hardship to grow, you are being crushed by it. The oppression is now coming from within as much as without.',
            scenes: {
                career: '事业面临绝境，可能需要彻底清盘。',
                relationship: '感情彻底破裂，难以修复。',
                wealth: '财务陷入死局，需极大外力相助。',
                health: '健康状况恶化，意志消沉。',
                universal: '需背水一战，彻底打破旧局。',
                transition: '从困境转向绝望或彻底毁灭的转折。'
            },
            scenesEn: {
                career: 'Total professional collapse; loss of will to work; permanent unemployment.',
                relationship: 'The death of a relationship; total emotional abandonment.',
                wealth: 'Bankruptcy without recovery; permanent loss of assets.',
                health: 'Giving up on recovery; chronic state of depletion.',
                universal: 'The shadows are winning; you must find a radical new source of light.',
                transition: 'A dark shift from struggle to total defeat.'
            }
        }
    },

    // 48 - 井卦 (水风井)
    '048': {
        id: '048',
        name: 'Jing (The Well)',
        nameCn: '井卦',
        symbol: '☵☴',
        trigrams: { upper: '坎', lower: '巽' },
        introduction: '木入水下，汲水养人。象征不变的资源、深度与滋养。',
        introductionEn: 'Jing represents the Well. Towns may be changed, but the well remains the same. It symbolizes an inexhaustible source of spiritual and communal nourishment that must be kept clean and accessible.',
        upright: {
            keywords: ['滋养之源', '不变根基', '深度', '资源'],
            keywordsEn: ['Source', 'Nourishment', 'Foundation', 'Consistency', 'Wisdom', 'Reliability'],
            imageDescription: '井水清澈稳定，滋养一方土地，源源不断。',
            imageDescriptionEn: 'Water rising through a wooden shaft. A deep, reliable source of life that serves everyone without being depleted.',
            generalMeaning: '处于提供或寻找滋养阶段，需维护核心根基。',
            generalMeaningEn: 'Focus on your core resources and values. Success comes from tapping into deep, consistent sources rather than chasing temporary trends. Keep your "well" clean.',
            scenes: {
                career: '成为平台型角色，或需深耕专业领域。',
                relationship: '需互相关怀滋养，建立深层连接。',
                wealth: '财务有稳定来源，适合长线布局。',
                health: '需调养身体根基，重视内在调理。',
                universal: '当下是深耕根基、养护资源的时机。',
                transition: '进入稳健且具有深度滋养的阶段。'
            },
            scenesEn: {
                career: 'Providing long-term value; deep expertise; a reliable company culture.',
                relationship: 'Deep, soul-level nourishment; a partner who is always there.',
                wealth: 'Passive income; reliable assets; financial foundation is solid.',
                health: 'Hydration; treating the root cause; long-term wellness.',
                universal: 'Look deeper for the solution; the source is always there.',
                transition: 'A stabilizing shift from seeking to finding a source.'
            }
        },
        reversed: {
            keywords: ['井枯无水', '滋养枯竭', '基础受损', '失效'],
            keywordsEn: ['Drought', 'Uselessness', 'Neglect', 'Pollution', 'Inefficiency'],
            imageDescription: '井泥淤塞，绳断瓶碎，无法取水养人。',
            imageDescriptionEn: 'A broken bucket at the bottom of the well. The water is there, but you cannot reach it, or the well has been neglected and is now dirty.',
            generalMeaning: '资源枯竭或因管理不善而无法发挥作用。',
            generalMeaningEn: 'A failure to utilize your resources. You have the potential but are failing to "reach the water" due to laziness, broken systems, or neglect.',
            scenes: {
                career: '事业失去动力源，核心能力未能发挥。',
                relationship: '感情干涸，缺乏真正的沟通滋养。',
                wealth: '收入中断，或理财方式彻底失效。',
                health: '身体极度缺水或养分不足。',
                universal: '需立即修缮根基，清除积弊。',
                transition: '从充裕稳定转向枯竭失效的转折。'
            },
            scenesEn: {
                career: 'Wasted talent; professional burnout; failing at the last step of a project.',
                relationship: 'Emotional drought; neglecting a partner\'s needs; a stale bond.',
                wealth: 'Financial leak; unproductive assets; "dry" period in business.',
                health: 'Dehydration; ignoring essential needs; physical stagnation.',
                universal: 'Fix the tools and clean the source; you are close to the water but cannot drink.',
                transition: 'A frustrating shift from reliability into uselessness.'
            }
        }
    },

    // 49 - 革卦 (泽火革)
    '049': {
        id: '049',
        name: 'Ge (Revolution)',
        nameCn: '革卦',
        symbol: '☱☲',
        trigrams: { upper: '兑', lower: '离' },
        introduction: '水火相息，豹变新生。象征彻底的改革、转型与重生。',
        introductionEn: 'Ge represents Revolution or Molting. Just as an animal sheds its skin, old ways must be discarded. It signifies a radical change that is necessary and well-timed.',
        upright: {
            keywords: ['变革革命', '除旧布新', '转型', '革新'],
            keywordsEn: ['Revolution', 'Change', 'Renewal', 'Transformation', 'Abolishing the Old'],
            imageDescription: '火在泽下，相克相激，引发质的飞跃。',
            imageDescriptionEn: 'Fire within a Lake. The elements struggle until a new form emerges. It is the image of a skin being shed to reveal the new self.',
            generalMeaning: '需彻底变革阶段，时机已至，变革必成。',
            generalMeaningEn: 'The time for the old way has ended. You must innovate and change. If your motives are correct and the timing is right, people will support the revolution.',
            scenes: {
                career: '事业面临大转型，适合推出新业务。',
                relationship: '关系需彻底改变模式，焕发新生。',
                wealth: '需彻底革新财务策略，清理旧账。',
                health: '生活方式需大改，重塑体质。',
                universal: '变革时机已至，勇于打破旧枷锁。',
                transition: '进入彻底变革与重塑的关键期。'
            },
            scenesEn: {
                career: 'Company restructuring; switching industries; leading a major innovation.',
                relationship: 'Relationship reset; dynamic shift; moving from old patterns to a new level.',
                wealth: 'A complete financial overhaul; switching investment philosophies.',
                health: 'Radical lifestyle change; plastic surgery or major physical reset.',
                universal: 'Burn the old bridges; the future is waiting for your new self.',
                transition: 'A powerful shift from stagnation into radical change.'
            }
        },
        reversed: {
            keywords: ['变革受阻', '守旧受损', '乱改', '不合时宜'],
            keywordsEn: ['Failed Reform', 'Resisting Change', 'Hasty Action', 'Chaos', 'Reactionary'],
            imageDescription: '变革时机未到而妄动，或旧势力反扑。',
            imageDescriptionEn: 'Forcing a change before people are ready, or attempting to change something that is not yet ready to molt.',
            generalMeaning: '变革受阻或因盲目乱改导致混乱。',
            generalMeaningEn: 'Poorly timed change. You are either changing for change\'s sake, or you are resisting a necessary shift, leading to unnecessary tension.',
            scenes: {
                career: '转型不顺，或频繁变动导致人心惶惶。',
                relationship: '为了改而改，导致关系不稳定。',
                wealth: '财务策略变动过快导致损失。',
                health: '乱吃药或乱投医，健康受损。',
                universal: '需三思而行，看清时机再谈变革。',
                transition: '从变革势头转向受阻或混乱的波动。'
            },
            scenesEn: {
                career: 'Unsuccessful reorganization; resisting new technology; workplace chaos.',
                relationship: 'Unstable feelings; changing partners too quickly; lack of true transformation.',
                wealth: 'Financial instability due to reckless changes; losing money on trends.',
                health: 'Harmful fad diets; inconsistent medical advice.',
                universal: 'Do not change just to be different; ensure the timing is right.',
                transition: 'A confusing shift from progress back into resistance.'
            }
        }
    },

    // 50 - 鼎卦 (火风鼎)
    '050': {
        id: '050',
        name: 'Ding (The Cauldron)',
        nameCn: '鼎卦',
        symbol: '☲☴',
        trigrams: { upper: '离', lower: '巽' },
        introduction: '三足鼎立，炼石成金。象征转化、成就与崇高的地位。',
        introductionEn: 'Ding represents the Cauldron. It is a vessel used for cooking and spiritual offerings. It signifies the transformation of raw materials into something sacred and precious.',
        upright: {
            keywords: ['鼎新转化', '养贤', '成就', '尊贵'],
            keywordsEn: ['Transformation', 'Nourishment', 'Sovereignty', 'Success', 'Culture', 'High Status'],
            imageDescription: '木在火下，鼎中烹饪，象征文明与进化。',
            imageDescriptionEn: 'Fire above Wood. The cauldron is firm and stable on its three legs, cooking a feast that nourishes the spirit and the body.',
            generalMeaning: '处于升华转化阶段，获得极高成就与认可。',
            generalMeaningEn: 'A time of alchemy. You are turning your hard work into true success. It is a period of high culture, stable authority, and providing for others.',
            scenes: {
                career: '事业有质的飞跃，地位尊崇，受重用。',
                relationship: '感情稳定且尊贵，互相成就。',
                wealth: '财务丰厚且稳定，利于长远建设。',
                health: '炼体见效，精神面貌焕然一新。',
                universal: '升华转化好时期，成就斐然。',
                transition: '从变革成功进入成果丰硕的顶峰。'
            },
            scenesEn: {
                career: 'Reaching the top of your field; cultural leadership; a prestigious new role.',
                relationship: 'A relationship of high quality and mutual respect; stable marriage.',
                wealth: 'Prosperity; sophisticated financial management; lasting wealth.',
                health: 'Peak physical condition; the body transformed through discipline.',
                universal: 'Nourish what is noble; you are reaching your highest potential.',
                transition: 'A magnificent shift from raw effort to refined achievement.'
            }
        },
        reversed: {
            keywords: ['鼎折足', '转化失败', '失去地位', '浪费'],
            keywordsEn: ['Failed Transformation', 'Broken Vessel', 'Loss of Honor', 'Clumsiness', 'Waste'],
            imageDescription: '鼎足折断，佳肴倾覆。象征能力不足导致失败。',
            imageDescriptionEn: 'The cauldron tips over; a leg is broken. The valuable contents are spilled in the dirt because of a lack of preparation.',
            generalMeaning: '转化失败或失去地位，能力不足以任重。',
            generalMeaningEn: 'A warning of failure due to over-ambition or poor foundation. You may be in a position you cannot handle, leading to a public loss of face.',
            scenes: {
                career: '职位不保，或项目在最后关头崩溃。',
                relationship: '感情关系因处理不当而破裂或降级。',
                wealth: '财务投资失误导致重大损失。',
                health: '调养中断，或因过度而导致反效果。',
                universal: '需反思能力与位置的匹配，切勿勉强。',
                transition: '从鼎盛转向失败或跌落的转折。'
            },
            scenesEn: {
                career: 'Losing a high-ranking position; professional embarrassment; failed project launch.',
                relationship: 'A loss of respect in love; sudden breakdown of a stable bond.',
                wealth: 'Financial collapse; losing accumulated wealth through a single mistake.',
                health: 'Sudden injury; failure to maintain health gains.',
                universal: 'You are carrying more than you can balance; expect a fall.',
                transition: 'A sharp shift from prestige to sudden failure.'
            }
        }
    },

    // 51 - 震卦 (震为雷)
    '051': {
        id: '051',
        name: 'Zhen (The Arousing)',
        nameCn: '震卦',
        symbol: '☳☳',
        trigrams: { upper: '震', lower: '震' },
        introduction: '重雷震动，惊天动地。象征突发事件带来的觉醒与活力。',
        introductionEn: 'Zhen represents Thunder or the Arousing. It is a shock that startles the world into action. It signifies the power of life breaking through stillness, leading to fear followed by joy.',
        upright: {
            keywords: ['震惊行动', '惊醒', '活力', '振作'],
            keywordsEn: ['Shock', 'Awakening', 'Action', 'Stimulation', 'Thunder', 'Quick Change'],
            imageDescription: '雷声接连不断，虽令人恐惧却能扫除沉闷。',
            imageDescriptionEn: 'Thunder following thunder. It is a terrifying yet vitalizing force that clears the air and wakes the sleeping.',
            generalMeaning: '处于受到震动阶段，震惊带来自省与觉醒。',
            generalMeaningEn: 'Expect the unexpected. A sudden event will shake your world. Do not panic; use this energy to wake up and take bold action. The fear will turn to laughter.',
            scenes: {
                career: '可能有突发大事件，需积极应变。',
                relationship: '感情有震动事件，带来说清真相机会。',
                wealth: '财运有剧烈波动，需敏捷反应。',
                health: '身体有明显警示，需立即振作调理。',
                universal: '震动事件是转机，积极应对可获吉。',
                transition: '从沉静转向剧烈震动与行动的阶段。'
            },
            scenesEn: {
                career: 'Sudden market shifts; an unexpected promotion or crisis; high-energy environments.',
                relationship: 'A sudden realization or confession; "shaking up" a boring relationship.',
                wealth: 'Rapid money movement; sudden gains or necessary fast pivots.',
                health: 'A health "wake-up call"; rapid recovery of energy.',
                universal: 'Stay calm while others panic; the shock is your catalyst for growth.',
                transition: 'A sudden shift from peace to high-voltage action.'
            }
        },
        reversed: {
            keywords: ['惊恐过度', '慌乱', '无法行动', '震慑'],
            keywordsEn: ['Panic', 'Paralysis', 'Senseless Shock', 'Inaction', 'Fear of Change'],
            imageDescription: '雷声过大导致丧胆，陷入瘫痪状态。',
            imageDescriptionEn: 'The thunder causes terror rather than movement. One is so afraid of the noise that they cannot act.',
            generalMeaning: '震动过度导致恐慌，错失自省与转机。',
            generalMeaningEn: 'Paralysis by fear. You are so startled by a change that you are unable to function. You are missing the opportunity for growth because you are focusing on the noise.',
            scenes: {
                career: '因恐慌而决策失误，或因乱动惹祸。',
                relationship: '因惊吓或猜疑导致关系处理不当。',
                wealth: '财务上因慌乱操作导致重大损失。',
                health: '身心因受惊而出现功能性障碍。',
                universal: '需冷静应对，不可被恐惧夺去意志。',
                transition: '从震动转向恐慌或瘫痪的负面转折。'
            },
            scenesEn: {
                career: 'Panic in the boardroom; making rash errors under pressure; loss of nerve.',
                relationship: 'Hysteria; emotional paralysis; fear of a partner\'s reaction.',
                wealth: 'Panic selling; financial loss through fear; frozen accounts.',
                health: 'Nervous breakdown; high anxiety; trauma-induced symptoms.',
                universal: 'Breathe; the noise cannot hurt you unless you let it.',
                transition: 'A negative shift from vitality to paralyzing terror.'
            }
        }
    },

    // 52 - 艮卦 (艮为山)
    '052': {
        id: '052',
        name: 'Gen (Keeping Still)',
        nameCn: '艮卦',
        symbol: '☶☶',
        trigrams: { upper: '艮', lower: '艮' },
        introduction: '重山静止，止于所当止。象征静定、冥想与明确界限。',
        introductionEn: 'Gen represents Keeping Still or the Mountain. It signifies the wisdom of meditation and stopping when action is unnecessary. It is about inner peace and knowing your limits.',
        upright: {
            keywords: ['止息静定', '适时止步', '稳定', '界限'],
            keywordsEn: ['Stillness', 'Meditation', 'Restraint', 'Solitude', 'Boundaries', 'Peace'],
            imageDescription: '山外有山，连绵静止。象征不动如山的定力。',
            imageDescriptionEn: 'Mountain upon mountain. A path that leads to a place of absolute quiet where the mind finds rest.',
            generalMeaning: '处于需静止休整阶段，止于所当止方为道。',
            generalMeaningEn: 'Stop all outer activity. It is a time for silence and internal work. By not moving, you gain the strength and clarity needed for the future.',
            scenes: {
                career: '事业需暂停整顿，不宜冒进，坚守岗位。',
                relationship: '需冷静期，保持安全边界与自我反省。',
                wealth: '财务宜守不宜动，暂停一切大额支出。',
                health: '需静养，重视冥想与深度睡眠。',
                universal: '止息内省时期，不动如山方获吉。',
                transition: '从动荡转向深度静止与安定的阶段。'
            },
            scenesEn: {
                career: 'Taking a break; professional boundaries; refusing to move in a bad direction.',
                relationship: 'Quiet companionship; needing space; a peaceful, undramatic bond.',
                wealth: 'Financial freeze; saving without spending; rock-solid stability.',
                health: 'Bed rest; yoga; spinal health; mental stillness.',
                universal: 'Be still and know; movement right now is an error.',
                transition: 'A calming shift from action into profound stillness.'
            }
        },
        reversed: {
            keywords: ['停滞不前', '僵化', '顽固', '无法静心'],
            keywordsEn: ['Stagnation', 'Restlessness', 'Rigidity', 'Blocked Energy', 'Isolation'],
            imageDescription: '山石崩裂或过于沉重，静止变成压抑。',
            imageDescriptionEn: 'The mountain is cold and oppressive. Stillness has turned into a stubborn refusal to grow or a state of anxiety.',
            generalMeaning: '过于静止导致僵化，或内心浮躁无法安静。',
            generalMeaningEn: 'A warning of rigid thinking. You are either stuck in a rut you refuse to leave, or your mind is racing even though your body is still. Imbalance in rest.',
            scenes: {
                career: '事业僵化停滞，思维陈旧不化。',
                relationship: '感情冷战，陷入死气沉沉的冷漠。',
                wealth: '财务如死水，毫无增长或周转不灵。',
                health: '身体僵硬不灵，或心理上有顽固执念。',
                universal: '需打破僵局，不可死守过时。',
                transition: '从静止转向僵化或浮躁的波动。'
            },
            scenesEn: {
                career: 'Professional paralysis; refusal to innovate; a dead-end office culture.',
                relationship: 'The "silent treatment"; emotional isolation; lack of life in a home.',
                wealth: 'Assets that are not working; stinginess causing social loss.',
                health: 'Arthritis; stiffness; mental fixations that cause stress.',
                universal: 'Even the mountain must weather; do not become a stone.',
                transition: 'A negative shift from peace to a restrictive rut.'
            }
        }
    },

    // 53 - 渐卦 (风山渐)
    '053': {
        id: '053',
        name: 'Jian (Development)',
        nameCn: '渐卦',
        symbol: '☴☶',
        trigrams: { upper: '巽', lower: '艮' },
        introduction: '木生山上，循序渐进。象征持之以恒、按部就班的成功。',
        introductionEn: 'Jian represents Gradual Progress or Development. Like a tree growing on a mountain or geese flying in formation, it emphasizes that lasting success comes through following proper procedures and taking one step at a time.',
        upright: {
            keywords: ['循序渐进', '稳步发展', '按部就班', '成长'],
            keywordsEn: ['Gradualness', 'Evolution', 'Consistency', 'Marriage', 'Procedure', 'Steady Growth'],
            imageDescription: '鸿鹄飞于高空，队列有序，渐进至高点。',
            imageDescriptionEn: 'A tree on a mountain peak. It grows slowly but its roots are deep and its height is undeniable. It is the image of a proper wedding.',
            generalMeaning: '处于需循序渐进阶段，不可操之过急。',
            generalMeaningEn: 'Slow and steady wins the race. Follow the established rules and move forward with patience. This is especially auspicious for long-term partnerships and careers.',
            scenes: {
                career: '事业需按部就班，不求速成终有成。',
                relationship: '按步骤发展，水到渠成，婚恋大吉。',
                wealth: '财务稳步增长，长线投资见效。',
                health: '身体逐步康复，需持之以恒。',
                universal: '耐心渐进时期，每一步都要走扎实。',
                transition: '从起点进入稳步上升的正轨。'
            },
            scenesEn: {
                career: 'Organic career growth; following a set career path; long-term project success.',
                relationship: 'A relationship developing naturally toward marriage; deep commitment.',
                wealth: 'Steady financial accumulation; sound, slow-growth investments.',
                health: 'Gradual recovery; sustainable fitness habits.',
                universal: 'Trust the timing of your life; haste will ruin the results.',
                transition: 'A positive shift from stillness into organized movement.'
            }
        },
        reversed: {
            keywords: ['急于求成', '欲速不达', '不按程序', '受阻'],
            keywordsEn: ['Hastiness', 'Irregularity', 'Stalled Growth', 'Broken Order', 'Impatience'],
            imageDescription: '木强行求高而根浅，或鸿鹄失序坠落。',
            imageDescriptionEn: 'The tree is falling or the geese are out of formation. By skipping steps, you have created an unstable situation.',
            generalMeaning: '进展受阻，或因急于求成而导致混乱。',
            generalMeaningEn: 'A warning of trying to skip the process. You are rushing into a commitment or a job you are not ready for, leading to a loss of balance and order.',
            scenes: {
                career: '事业想走捷径受挫，或不按规矩惹麻烦。',
                relationship: '感情操之过急吓跑对方，或未熟而婚。',
                wealth: '追求暴利导致损失，财务节奏乱。',
                health: '急于康复过度用药，欲速不达。',
                universal: '需放慢节奏，回归正规程序。',
                transition: '从渐进转向受阻或混乱的波动。'
            },
            scenesEn: {
                career: 'Failing because of missed steps; "faking it" before you make it; professional chaos.',
                relationship: 'Elopement gone wrong; rushing a partner; lack of solid foundation.',
                wealth: 'Financial "get rich quick" schemes failing; inconsistent income.',
                health: 'Pushing the body too hard too soon; unstable recovery.',
                universal: 'Go back to basics; you cannot fly before you walk.',
                transition: 'A negative shift from order to chaotic haste.'
            }
        }
    },

    // 54 - 归妹卦 (雷泽归妹)
    '054': {
        id: '054',
        name: 'Gui Mei (The Marrying Maiden)',
        nameCn: '归妹卦',
        symbol: '☳☱',
        trigrams: { upper: '震', lower: '兑' },
        introduction: '少女出嫁，终始之义。象征关系结合、亦警示位不正。',
        introductionEn: 'Gui Mei represents the Marrying Maiden. It signifies the union of people, but warns that the relationship may be based on impulse or an incorrect position. It is a card of high emotion and potential instability.',
        upright: {
            keywords: ['结合', '归宿', '情感冲动', '责任', '转折'],
            keywordsEn: ['Union', 'Impulse', 'Attraction', 'Subordinate Position', 'Commitment', 'Endings'],
            imageDescription: '雷动泽上，如情欲冲动，需以礼节制。',
            imageDescriptionEn: 'Thunder over the Lake. The water is stirred by the sound. It is a time of intense desire and seeking a place to belong.',
            generalMeaning: '处于涉及承诺结合阶段，需保持理性守正。',
            generalMeaningEn: 'A new relationship or partnership is beginning. However, ensure you are joining for the right reasons. Do not let passion blind you to your long-term duties.',
            scenes: {
                career: '可能有合作结盟，但需看清主从关系。',
                relationship: '感情进入实质结合，需面对现实生活。',
                wealth: '财务上有合并或大笔开支，需理性。',
                health: '情绪波动大影响身心，需调整。',
                universal: '涉及重大承诺时期，需审慎。',
                transition: '进入结合承诺与责任重担的阶段。'
            },
            scenesEn: {
                career: 'New business partnerships; being a junior partner; high-energy but temporary roles.',
                relationship: 'Falling in love quickly; marriage based on passion; needing to find your place.',
                wealth: 'Merging finances; emotional spending; unexpected marriage-related costs.',
                health: 'Nervous energy; focus on the reproductive system or heart; mood swings.',
                universal: 'Find your true home, but do not lose your head in the process.',
                transition: 'A dramatic shift from single focus to shared responsibility.'
            }
        },
        reversed: {
            keywords: ['结合失败', '错位', '承诺落空', '反目'],
            keywordsEn: ['Failed Union', 'Infidelity', 'Unstable Marriage', 'Wrong Timing', 'Disappointment'],
            imageDescription: '结合不合礼制，或感情终成镜花水月。',
            imageDescriptionEn: 'The marriage fails or the union is inappropriate. The impulsive start has led to a disappointing end.',
            generalMeaning: '结合出现问题，可能因地位不平等或不诚。',
            generalMeaningEn: 'A relationship that cannot last. You may be in the wrong position or with the wrong person. The promises made are being broken or were never sincere.',
            scenes: {
                career: '合作破裂，或职位安排严重不合理。',
                relationship: '感情不顺，甚至出现背叛或冷战。',
                wealth: '财务合作出纠纷，投入无法收回。',
                health: '由于情绪极端导致身心受损严重。',
                universal: '需及时止损，看清关系的真相。',
                transition: '从结合转向破裂或不顺的波动。'
            },
            scenesEn: {
                career: 'Dissolved partnership; being fired from a temporary role; toxic workplace dynamics.',
                relationship: 'Divorce; unrequited love; discovering a partner\'s true motives; broken engagement.',
                wealth: 'Financial loss through marriage or poor partnership; legal money issues.',
                health: 'Exhaustion from emotional drama; fertility issues; heart strain.',
                universal: 'The union was flawed from the start; face the truth and move on.',
                transition: 'A negative shift from attraction to disillusionment.'
            }
        }
    },

    // 55 - 丰卦 (雷火丰)
    '055': {
        id: '055',
        name: 'Feng (Abundance)',
        nameCn: '丰卦',
        symbol: '☳☲',
        trigrams: { upper: '震', lower: '离' },
        introduction: '日正中天，光大盛大。象征丰盛的高峰与不可持久的警示。',
        introductionEn: 'Feng represents Abundance and Fullness. Like the sun at noon, everything is brilliantly lit and successful. However, it also reminds us that after the zenith comes the sunset.',
        upright: {
            keywords: ['丰盛', '盛大', '高峰', '成功', '光明'],
            keywordsEn: ['Abundance', 'Zenith', 'Success', 'Clarity', 'Grandeur', 'Fullness'],
            imageDescription: '雷火齐发，光明而壮丽，如日中天之象。',
            imageDescriptionEn: 'Thunder and Lightning combined. A display of immense power and clarity that reaches every corner of the world.',
            generalMeaning: '处于丰盛巅峰阶段，宜把握机会大展宏图。',
            generalMeaningEn: 'A time of peak achievement. You have the resources and the clarity to do great things. Enjoy your success and use your power to bring light to others.',
            scenes: {
                career: '事业达高峰，声势浩大，利于决策。',
                relationship: '感情生活丰富多彩，热烈而满足。',
                wealth: '财运丰盛，适合大规模投资或扩张。',
                health: '精力极其充沛，身体状态巅峰。',
                universal: '当下是盛大时期，尽情施展才华。',
                transition: '进入成果丰硕、名利双收的顶峰。'
            },
            scenesEn: {
                career: 'Major career breakthrough; peak influence; dominating a market; grand success.',
                relationship: 'A vibrant and rich emotional life; high passion; absolute clarity in love.',
                wealth: 'Maximum prosperity; large-scale financial gains; abundance in all forms.',
                health: 'Radiant vitality; high energy; perfect physical state.',
                universal: 'Be like the sun at noon; shine your brightest and be fearless.',
                transition: 'A magnificent peak shift into total abundance.'
            }
        },
        reversed: {
            keywords: ['盛极而衰', '日偏', '过度', '虚假繁荣'],
            keywordsEn: ['Decline', 'Shadows', 'Overwhelmed', 'Diminishing Luck', 'Obscurity'],
            imageDescription: '日过中午，阴影渐生。繁荣背后现隐忧。',
            imageDescriptionEn: 'The sun begins to set. The brightness is fading, and shadows are starting to grow. The peak has passed.',
            generalMeaning: '丰盛过后开始下滑，需防微虑远。',
            generalMeaningEn: 'The peak has passed. You may be trying to hold on to a level of success that is naturally receding. It is a time to prepare for a quieter cycle.',
            scenes: {
                career: '事业开始出现小滑坡，需收缩战线。',
                relationship: '感情热度减退，或被生活琐事蒙蔽。',
                wealth: '财务增长放缓，需警惕意外开支。',
                health: '体能开始下降，需注意劳逸结合。',
                universal: '需居安思危，盛世之中防乱。',
                transition: '从丰盛巅峰转向缓慢下滑的过渡。'
            },
            scenesEn: {
                career: 'Losing market share; professional burnout; the end of a "lucky" streak.',
                relationship: 'Cooling of passion; misunderstandings; focusing on the negative.',
                wealth: 'Income starting to drop; hidden financial leaks; excessive ego-spending.',
                health: 'Fatigue setting in; eye strain; need for more rest.',
                universal: 'The light is fading; do not be saddened, just prepare for the evening.',
                transition: 'A sobering shift from zenith toward the shadows.'
            }
        }
    },

    // 56 - 旅卦 (火山旅)
    '056': {
        id: '056',
        name: 'Lü (The Wanderer)',
        nameCn: '旅卦',
        symbol: '☲☶',
        trigrams: { upper: '离', lower: '艮' },
        introduction: '山上有火，行旅在外。象征客居、变动与谦虚的处世。',
        introductionEn: 'Lü represents the Wanderer or the Traveler. Like fire on a mountain, it moves quickly and has no permanent home. It signifies a time of transition, travel, and the need for adaptability.',
        upright: {
            keywords: ['旅行', '漂泊', '适应', '暂时', '过客'],
            keywordsEn: ['Travel', 'Transition', 'Adaptability', 'Wandering', 'Stranger', 'Modesty'],
            imageDescription: '火在山上，草木燃过即熄，象征暂时停驻。',
            imageDescriptionEn: 'A small fire moving across the mountain ridges. It stays in one place only for a short time before moving on.',
            generalMeaning: '处于变动漂泊阶段，需谦逊适应新环境。',
            generalMeaningEn: 'You are a stranger in a new situation. Do not be arrogant. Be polite, follow local rules, and stay alert. Your stay here is temporary.',
            scenes: {
                career: '事业可能有变动，如出差或换环境。',
                relationship: '感情处于客旅状态，难长久或需适应。',
                wealth: '财务动荡，利于流动性投资。',
                health: '需注意旅途健康及水土不服。',
                universal: '变动期需灵活，保持客居者的谦卑。',
                transition: '从安定状态进入变动漂泊的过渡。'
            },
            scenesEn: {
                career: 'Business travel; relocating for a job; being a "consultant" rather than an employee.',
                relationship: 'Meeting someone while traveling; a relationship without deep roots; transition.',
                wealth: 'Money gained through travel or small trades; keeping assets liquid.',
                health: 'Travel-related illness; focus on inner stability while moving.',
                universal: 'The world is your home for now; be a respectful guest.',
                transition: 'A restless shift from home into the wide world.'
            }
        },
        reversed: {
            keywords: ['流离失所', '傲慢惹祸', '孤独', '无依'],
            keywordsEn: ['Exile', 'Homelessness', 'Arrogance in a New Place', 'Stuck', 'Isolation'],
            imageDescription: '火势失控或熄灭在荒野。象征旅途受挫。',
            imageDescriptionEn: 'The traveler is lost or has offended their hosts. The fire has burned out, leaving the wanderer cold and alone.',
            generalMeaning: '漂泊无依且遭遇挫折，可能因傲慢惹祸。',
            generalMeaningEn: 'A difficult journey. You have failed to adapt to your new surroundings, and now you are facing isolation or hostility. You have lost your way.',
            scenes: {
                career: '异地发展不顺，或因不合群而受挫。',
                relationship: '感情漂泊无依，感到孤独与被排斥。',
                wealth: '财务流失，旅途中遭遇意外损失。',
                health: '由于旅途劳累导致大病，身心交瘁。',
                universal: '需寻找归宿，停止无谓的漂泊。',
                transition: '从变动转向流离失所或被困的波动。'
            },
            scenesEn: {
                career: 'Failed business trip; getting fired in a foreign city; lack of cultural fit.',
                relationship: 'Loneliness on the road; a romance that ends in a fight; no place to belong.',
                wealth: 'Financial loss while traveling; being cheated in a new market.',
                health: 'Severe exhaustion; chronic homesickness affecting the body.',
                universal: 'You are lost in the dark; find your way back to a solid foundation.',
                transition: 'A painful shift from adventure to rootless suffering.'
            }
        }
    },

    // 57 - 巽卦 (巽为风)
    '057': {
        id: '057',
        name: 'Xun (The Gentle)',
        nameCn: '巽卦',
        symbol: '☴☴',
        trigrams: { upper: '巽', lower: '巽' },
        introduction: '随风潜入，柔顺渗透。象征通过柔和、持续的努力获成功。',
        introductionEn: 'Xun represents the Gentle or the Wind. Like the wind that penetrates everything, it signifies the power of small, persistent efforts and the wisdom of gradual influence.',
        upright: {
            keywords: ['柔顺渗透', '谦逊', '适应', '传播'],
            keywordsEn: ['Gentleness', 'Influence', 'Penetration', 'Flexibility', 'Persistence', 'Following'],
            imageDescription: '双风相继，无孔不入。象征思想与影响力的传播。',
            imageDescriptionEn: 'Wind blowing after wind. It does not force its way; it flows through every opening until its influence is total.',
            generalMeaning: '处于需柔顺适应阶段，持之以恒终获通达。',
            generalMeaningEn: 'Avoid brute force. Success comes through gentle persuasion, adaptability, and clear instructions. Be consistent like the wind to reach your goal.',
            scenes: {
                career: '事业需灵活，适合市场推广或公关。',
                relationship: '需柔顺包容，润物无声地影响对方。',
                wealth: '财务宜稳健，通过小额积累获大利。',
                health: '顺应身体信号，采取温和调理法。',
                universal: '以柔克刚时期，保持谦逊与定力。',
                transition: '从强硬手段转向柔性渗透的调整。'
            },
            scenesEn: {
                career: 'Effective marketing; subtle leadership; adapting to new office cultures; persuasion.',
                relationship: 'Soft-hearted love; understanding a partner through intuition; gentle bonding.',
                wealth: 'Steady small gains; money through communication or trade; consistent growth.',
                health: 'Breath work; gentle exercise; aromatherapy; mental flexibility.',
                universal: 'Insinuate yourself into the situation; do not attack head-on.',
                transition: 'A graceful shift from action to subtle influence.'
            }
        },
        reversed: {
            keywords: ['优柔寡断', '软弱', '飘忽不定', '意志动摇'],
            keywordsEn: ['Indecision', 'Weakness', 'Vacillation', 'Ineffectiveness', 'Lack of Will'],
            imageDescription: '风过而无定，或风力过弱无法成事。',
            imageDescriptionEn: 'The wind is a mere whisper or an erratic gust. There is no direction, only a confusing loss of focus.',
            generalMeaning: '由于优柔寡断或软弱而导致机会流失。',
            generalMeaningEn: 'A lack of follow-through. You are changing your mind too much and letting others sway you. Your gentleness has turned into spinelessness.',
            scenes: {
                career: '决策反复，执行力差，错失商机。',
                relationship: '感情中毫无主见，被动且令人生厌。',
                wealth: '财务计划朝令夕改，导致损失。',
                health: '身体虚弱，意志消沉，难以坚持锻炼。',
                universal: '需找回主见，在柔顺中保持骨气。',
                transition: '从柔性渗透转向无原则退让的波动。'
            },
            scenesEn: {
                career: 'Flip-flopping on decisions; lack of authority; being pushed around by others.',
                relationship: 'Being a doormat; lack of emotional boundaries; vacillating feelings.',
                wealth: 'Financial indecision; missing the peak of a trend; small losses.',
                health: 'Hypersensitivity; nervous energy causing physical fatigue.',
                universal: 'Make up your mind; the wind is blowing you nowhere.',
                transition: 'A negative shift from influence to instability.'
            }
        }
    },

    // 58 - 兑卦 (兑为泽)
    '058': {
        id: '058',
        name: 'Dui (The Joyous)',
        nameCn: '兑卦',
        symbol: '☱☱',
        trigrams: { upper: '兑', lower: '兑' },
        introduction: '双泽相润，和悦交流。象征发自内心的喜悦与和谐沟通。',
        introductionEn: 'Dui represents the Joyous or the Lake. It is the image of two lakes joined, refreshing each other. It signifies the pleasure of sharing knowledge and the strength found in happiness.',
        upright: {
            keywords: ['喜悦和谐', '交流', '满足', '说服力'],
            keywordsEn: ['Joy', 'Happiness', 'Communication', 'Pleasure', 'Exchange', 'Harmony'],
            imageDescription: '平静的湖面，互相映照，宁静而欢愉。',
            imageDescriptionEn: 'A lake reflecting the sky. It is open, calm, and invites others to drink and be refreshed.',
            generalMeaning: '处于喜悦和谐阶段，利于社交、谈判与分享。',
            generalMeaningEn: 'Success through joy. Share your happiness and communicate openly with others. Your friendly attitude will win people over and bring prosperity.',
            scenes: {
                career: '事业有喜悦成就，适合公关、演讲。',
                relationship: '感情生活愉悦，沟通顺畅，甜蜜。',
                wealth: '财运亨通，利于谈成订单或获利。',
                health: '身心愉悦，笑口常开，健康良好。',
                universal: '喜悦时期，真诚分享带来更多快乐。',
                transition: '从严肃紧绷转向和悦快乐的阶段。'
            },
            scenesEn: {
                career: 'Winning through charm; successful sales; a happy work team; clear public image.',
                relationship: 'Mutual joy; flirty and fun love; deep emotional satisfaction through talk.',
                wealth: 'Money through social connections; enjoying your wealth; spending on fun.',
                health: 'High spirits; physical comfort; healing through social interaction.',
                universal: 'Smile and the world smiles with you; joy is a serious power.',
                transition: 'A radiant shift into a period of contentment.'
            }
        },
        reversed: {
            keywords: ['过度享乐', '谄媚', '口舌是非', '空虚'],
            keywordsEn: ['Hollow Joy', 'Flattery', 'Gossip', 'Superficiality', 'Addiction'],
            imageDescription: '泽水溢出或干涸。喜悦流于表面。',
            imageDescriptionEn: 'The lake is turbulent or muddy. Joy has become shallow, and communication has turned into gossip or manipulation.',
            generalMeaning: '喜悦变质为纵欲，或因多言而招损。',
            generalMeaningEn: 'False happiness. You may be using charm to manipulate others, or you are seeking pleasure to escape from a reality you dislike.',
            scenes: {
                career: '因享乐懈怠工作，或口舌之争。',
                relationship: '感情流于表面，或因流言蜚语而裂痕。',
                wealth: '财务因贪图享乐而损耗，或被骗。',
                health: '过度享乐伤害身体，或声带问题。',
                universal: '需节制欲望，慎言慎行，防空虚。',
                transition: '从真喜悦转向空虚或口舌的波动。'
            },
            scenesEn: {
                career: 'Workplace gossip; being "all talk"; unprofessional behavior; losing a deal through charm failing.',
                relationship: 'Shallow connections; using a partner for status; arguments over petty words.',
                wealth: 'Wasting money on temporary thrills; empty luxury.',
                health: 'Throat or vocal issues; health problems from eating or drinking too much.',
                universal: 'Do not be seduced by your own reflection; find true depth.',
                transition: 'A negative shift from harmony to superficiality.'
            }
        }
    },

    // 59 - 涣卦 (风水涣)
    '059': {
        id: '059',
        name: 'Huan (Dispersion)',
        nameCn: '涣卦',
        symbol: '☴☵',
        trigrams: { upper: '巽', lower: '坎' },
        introduction: '风行水上，冰雪消融。象征化解僵局、凝聚人心的转机。',
        introductionEn: 'Huan represents Dispersion or Dissolution. Like wind blowing over water, it breaks up ice and disperses ego and rigid structures. It is a card of overcoming separation and finding unity.',
        upright: {
            keywords: ['涣散化解', '跨越隔阂', '重组', '凝聚'],
            keywordsEn: ['Dispersion', 'Dissolution', 'Unity', 'Renewal', 'Moving On', 'Melting Ice'],
            imageDescription: '春风吹散了坚冰，水流开始顺畅，隔阂消除。',
            imageDescriptionEn: 'Wind on the Water. The rigid ice of winter is melting; the flow returns, and a boat can now cross the great river.',
            generalMeaning: '处于化解冰封阶段，虽有分散但利于重组。',
            generalMeaningEn: 'A time to let go of old grudges and narrow self-interest. Focus on a great common goal to bring people together. The deadlock is breaking.',
            scenes: {
                career: '事业僵局化解，适合重组或向外拓展。',
                relationship: '感情冰释前嫌，隔阂化解，重归于好。',
                wealth: '财运散而复聚，适合处理历史债务。',
                health: '身体郁结散开，气血开始通畅。',
                universal: '化解坚冰时期，以大愿景凝聚人心。',
                transition: '从僵持闭塞转向顺畅流通的阶段。'
            },
            scenesEn: {
                career: 'Breaking a stalemate; re-branding; unifying a divided team; global expansion.',
                relationship: 'Forgiving a past hurt; ending a period of silence; emotional breakthrough.',
                wealth: 'Money moving freely; clearing old debts; financial flow returning.',
                health: 'Clearing blockages; circulation improvement; mental relief.',
                universal: 'Melt the ego; unity is the only way forward.',
                transition: 'A liberating shift from frozen conflict to fluid movement.'
            }
        },
        reversed: {
            keywords: ['彻底涣散', '人心离散', '无法凝聚', '崩溃'],
            keywordsEn: ['Total Disintegration', 'Alienation', 'Chaos', 'Lost Center', 'Exhaustion'],
            imageDescription: '风力过烈导致水浪四溅。象征局势失控。',
            imageDescriptionEn: 'The wind is too strong, causing the water to splash and disappear. The center is gone, and everything is flying apart.',
            generalMeaning: '涣散过度导致彻底分裂，失去核心力。',
            generalMeaningEn: 'A warning of total loss of focus. The group or situation is disintegrating because there is no spiritual or ideological center. You are adrift.',
            scenes: {
                career: '团队彻底散伙，项目流产，失去目标。',
                relationship: '感情名存实亡，彻底疏远无法挽回。',
                wealth: '财务失控，资金彻底溃散。',
                health: '元气大伤，神魂不守，处于危险边缘。',
                universal: '需寻找新的核心，否则将彻底消亡。',
                transition: '从化解转向彻底崩溃的负面转折。'
            },
            scenesEn: {
                career: 'The company closing down; team members leaving; loss of organizational spirit.',
                relationship: 'Final separation; loss of any common ground; deep alienation.',
                wealth: 'Financial scatter; bankruptcy; inability to collect what is owed.',
                health: 'Severe loss of vitality; psychological disintegration; confusion.',
                universal: 'The center has failed; everything is falling apart.',
                transition: 'A dark shift from flow into total disintegration.'
            }
        }
    },

    // 60 - 节卦 (水泽节)
    '060': {
        id: '060',
        name: 'Jie (Limitation)',
        nameCn: '节卦',
        symbol: '☵☱',
        trigrams: { upper: '坎', lower: '兑' },
        introduction: '泽上有水，节度有序。象征节制、规范与明确的界限。',
        introductionEn: 'Jie represents Limitation or Regulation. Water in a lake is limited by its shores. It signifies the need for rules, budgeting, and self-restraint to avoid the pain of excess.',
        upright: {
            keywords: ['适度节制', '规范', '界限', '节约'],
            keywordsEn: ['Limitation', 'Regulation', 'Budgeting', 'Self-Control', 'Boundaries', 'Order'],
            imageDescription: '竹子的一节，虽限制了生长却增加了强度。',
            imageDescriptionEn: 'A joint in a bamboo stalk. It limits the rapid growth but provides the strength necessary to stand tall.',
            generalMeaning: '处于需节制规范阶段，适度节制方通达。',
            generalMeaningEn: 'Establish boundaries. Whether it is your budget, your time, or your emotions, you must set limits now. Discipline creates freedom and long-term success.',
            scenes: {
                career: '事业需规范，设定明确KPI与预算。',
                relationship: '感情需分寸，相互尊重独立空间。',
                wealth: '理财宜紧缩，控制开支，开源节流。',
                health: '生活作息规律，饮食有节，利健康。',
                universal: '节制有度时期，苦节不可贞。',
                transition: '从放任转向有序节制的调整。'
            },
            scenesEn: {
                career: 'Adhering to rules; realistic goals; efficient use of time; professional limits.',
                relationship: 'Healthy boundaries; not overwhelming a partner; disciplined love.',
                wealth: 'Strict budgeting; saving money; financial discipline; controlled growth.',
                health: 'Routine; avoiding excess in diet; steady physical maintenance.',
                universal: 'Freedom is found within discipline; respect the limits.',
                transition: 'A stabilizing shift from chaos to controlled order.'
            }
        },
        reversed: {
            keywords: ['苦节难持', '过度苛刻', '放纵', '失序'],
            keywordsEn: ['Extreme Limitation', 'Over-restriction', 'Breaking Point', 'Loss of Control'],
            imageDescription: '由于节制太严导致破裂，或彻底不节制。',
            imageDescriptionEn: 'The shores are too narrow and the lake overflows, or the bamboo snaps from being too rigid. The balance is lost.',
            generalMeaning: '节制过度变成痛苦，或因不节制而遭灾。',
            generalMeaningEn: 'A warning of extremes. Either you are being so harsh with yourself that you will soon break, or you have thrown all caution to the wind and are facing the consequences.',
            scenes: {
                career: '管理过死导致离职，或完全无序。',
                relationship: '压抑过度引发爆发，或因放纵而破裂。',
                wealth: '过度吝啬影响生活，或挥霍无度。',
                health: '由于极端饮食或锻炼导致身体崩溃。',
                universal: '需回归适度，不可走极端。',
                transition: '从节制转向苛刻或失控的波动。'
            },
            scenesEn: {
                career: 'Bureaucracy killing creativity; rigid management; workplace rebellion.',
                relationship: 'Suffocating a partner; emotional repression; loss of freedom.',
                wealth: 'Miserliness causing stress; or total lack of financial restraint.',
                health: 'Eating disorders; injuries from over-restriction; chronic exhaustion.',
                universal: 'The spirit cannot breathe under such weight; loosen the grip.',
                transition: 'A negative shift from discipline toward destructive rigidity.'
            }
        }
    },

    // 61 - 中孚卦 (风泽中孚)
    '061': {
        id: '061',
        name: 'Zhong Fu (Inner Truth)',
        nameCn: '中孚卦',
        symbol: '☴☱',
        trigrams: { upper: '巽', lower: '兑' },
        introduction: '风行泽上，诚信感物。象征内心的真诚与对他人的感化。',
        introductionEn: 'Zhong Fu represents Inner Truth or Sincerity. It is the wind blowing across the surface of the lake, moving the water. It signifies a level of trust so deep it can move even the hardest heart.',
        upright: {
            keywords: ['诚信', '真诚', '感应', '核心价值'],
            keywordsEn: ['Sincerity', 'Trust', 'Integrity', 'Inner Voice', 'Confidence', 'Bonding'],
            imageDescription: '雏鸟在蛋壳中，感受到母鸟的真诚而孵化。',
            imageDescriptionEn: 'A bird sitting on her eggs. The heat of her body communicates the truth of life to the unborn, leading to a miraculous birth.',
            generalMeaning: '处于真诚互信阶段，诚信可感天动地。',
            generalMeaningEn: 'Listen to your inner voice. Be absolutely honest with yourself and others. This transparency will create powerful alliances and solve impossible problems.',
            scenes: {
                career: '事业靠诚信立足，适合建立长久信誉。',
                relationship: '感情真诚无欺，深度信任，心意相通。',
                wealth: '财务需透明，靠信誉获得商机。',
                health: '正视内心需求，身心合一，利康复。',
                universal: '当下以诚感人，诚信是最大力量。',
                transition: '从技巧沟通转向真心互信的阶段。'
            },
            scenesEn: {
                career: 'Business integrity; successful negotiations through honesty; finding your core mission.',
                relationship: 'Deep soul connection; total trust; a bond that needs no words.',
                wealth: 'Reliable financial partnerships; wealth through transparency.',
                health: 'Mind-body connection; spiritual healing; trusting the process.',
                universal: 'Your word is your bond; act from your center.',
                transition: 'A spiritual shift from surface interaction to deep truth.'
            }
        },
        reversed: {
            keywords: ['失信', '虚伪', '无法感应', '疑虑'],
            keywordsEn: ['Lack of Sincerity', 'Distrust', 'Hidden Motives', 'Betrayal', 'Superficiality'],
            imageDescription: '外实内虚，华而不实，承诺沦为谎言。',
            imageDescriptionEn: 'The egg is cold; the bond is broken. The words spoken are empty because they do not come from the heart.',
            generalMeaning: '失信于人或自我欺骗，感应受阻。',
            generalMeaningEn: 'A warning of deception. Either you are lying to yourself or someone else is not being truthful with you. The lack of integrity is causing the situation to fail.',
            scenes: {
                career: '事业因失信受损，或合同存隐患。',
                relationship: '感情充满疑虑与欺瞒，难以长久。',
                wealth: '财务被骗，或因不诚实而惹官司。',
                health: '心理焦虑源于谎言，需正视现实。',
                universal: '需重建诚信，回归真实自我。',
                transition: '从信任转向疑虑与欺瞒的负面转折。'
            },
            scenesEn: {
                career: 'Contractual lies; loss of reputation; corporate dishonesty; empty promises.',
                relationship: 'Suspicion; emotional hiding; unfaithfulness; faking feelings.',
                wealth: 'Financial fraud; being misled; losing money due to bad faith.',
                health: 'Psychosomatic pain from guilt; ignoring internal truth.',
                universal: 'Lies have short legs; the truth will come out painfully.',
                transition: 'A disappointing shift from integrity into confusion.'
            }
        }
    },

    // 62 - 小过卦 (雷山小过)
    '062': {
        id: '062',
        name: 'Xiao Guo (Small Exceeding)',
        nameCn: '小过卦',
        symbol: '☳☶',
        trigrams: { upper: '震', lower: '艮' },
        introduction: '雷在山上，小事可行。象征谦虚过头、不宜图大事。',
        introductionEn: 'Xiao Guo represents Preponderance of the Small. Thunder is on the mountain; the sound is loud but the effect is local. It signifies that small things can be achieved, but great things are currently impossible.',
        upright: {
            keywords: ['小事可成', '谦逊', '慎重', '下行'],
            keywordsEn: ['Small Steps', 'Modesty', 'Caution', 'Conformity', 'Detail-Oriented', 'Humility'],
            imageDescription: '飞鸟遗音，宜下不宜上。象征保持低姿态。',
            imageDescriptionEn: 'A bird flying low. It is safe near the earth but in danger if it flies too high toward the sun. Stay low and humble.',
            generalMeaning: '处于适度过越阶段，小事吉大事宜缓。',
            generalMeaningEn: 'Do not aim too high. Focus on the mundane, the small, and the local. By staying humble and attentive to detail, you avoid the dangers that face those who are over-ambitious.',
            scenes: {
                career: '事业宜守小，处理琐事吉，大投资缓。',
                relationship: '感情小步前进，保持低调与呵护。',
                wealth: '财务小有进项，控制对利润的贪念。',
                health: '注意微小症状，防微杜渐。',
                universal: '处理日常小事，不宜有大动作。',
                transition: '从宏大理想回归现实琐碎的阶段。'
            },
            scenesEn: {
                career: 'Managing routine tasks; small administrative wins; staying out of the spotlight.',
                relationship: 'Gentle, simple romance; focus on domestic life; avoiding big drama.',
                wealth: 'Minor savings; conservative budget; ignoring large-scale risks.',
                health: 'Treating small ailments; consistent but light exercise.',
                universal: 'Small acts of kindness are better than grand, hollow gestures.',
                transition: 'A grounded shift from ambition into practical detail.'
            }
        },
        reversed: {
            keywords: ['小题大做', '错失时机', '盲目高飞', '危险'],
            keywordsEn: ['Over-Ambitious', 'Missed Detail', 'Danger', 'Arrogance', 'Recklessness'],
            imageDescription: '鸟欲冲天而坠，或因固执小利而失大局。',
            imageDescriptionEn: 'The bird flies too high and is struck down. You are ignoring the practical limits of the situation and acting with arrogance.',
            generalMeaning: '过于自大或忽视细节，导致危险临头。',
            generalMeaningEn: 'A warning of pride. You are attempting something far beyond your current capacity. By ignoring small problems, you are creating a major catastrophe.',
            scenes: {
                career: '事业冒进导致惨败，或眼高手低。',
                relationship: '感情中自命不凡，忽视对方感受。',
                wealth: '财务因追求暴利而崩溃。',
                health: '轻视小病导致恶化，或体力透支。',
                universal: '需立即收敛，回归现实与谦卑。',
                transition: '从谨慎转向盲目自大的危险转折。'
            },
            scenesEn: {
                career: 'Professional hubris; project failure due to neglected details; taking on too much.',
                relationship: 'Ego clashing with reality; ignoring a partner\'s simple needs; arrogance.',
                wealth: 'Financial disaster from over-leveraging; ignoring small costs that add up.',
                health: 'Burnout; injuries from pushing the body beyond its limit.',
                universal: 'The higher you fly without a foundation, the harder you fall.',
                transition: 'A sharp shift from practical safety to reckless risk.'
            }
        }
    },

    // 63 - 既济卦 (水火既济)
    '063': {
        id: '063',
        name: 'Ji Ji (After Completion)',
        nameCn: '既济卦',
        symbol: '☵☲',
        trigrams: { upper: '坎', lower: '离' },
        introduction: '水火相济，阴阳定格。象征事情圆满达成，需防后变。',
        introductionEn: 'Ji Ji represents After Completion. Everything is in its proper place, and the goal has been reached. It is a state of perfect balance, but warns that balance is difficult to maintain.',
        upright: {
            keywords: ['圆满完成', '成功', '秩序', '平衡'],
            keywordsEn: ['Completion', 'Success', 'Order', 'Balance', 'Stability', 'Security'],
            imageDescription: '渡河已毕，船已靠岸。万事停当。',
            imageDescriptionEn: 'A boat has successfully crossed the water. The fire is controlled and the water is pure. It is the perfect arrangement of elements.',
            generalMeaning: '处于功成名就阶段，万事如意需防松懈。',
            generalMeaningEn: 'You have won. Everything is as it should be. However, this is the most critical time to stay alert, as the only direction from perfection is toward decay.',
            scenes: {
                career: '事业达成阶段性顶峰，成果显赫。',
                relationship: '感情稳定圆满，进入稳固期。',
                wealth: '财务目标达成，收益丰厚。',
                health: '身体状态极佳，疾病全愈。',
                universal: '当下享受成功，更需居安思危。',
                transition: '从努力奋斗进入圆满享受的巅峰。'
            },
            scenesEn: {
                career: 'Project finished perfectly; receiving rewards; absolute professional order.',
                relationship: 'Total harmony; a settled, happy marriage; domestic peace.',
                wealth: 'Financial goals met; abundance; wealth is stable and secure.',
                health: 'Full recovery; perfect health balance; mental clarity.',
                universal: 'You are at the peak; watch your step as you descend.',
                transition: 'A magnificent peak shift into total realization.'
            }
        },
        reversed: {
            keywords: ['功亏一篑', '盛极而衰', '自满', '乱象萌生'],
            keywordsEn: ['Decay', 'Complacency', 'Chaos after Success', 'Unraveling', 'Instability'],
            imageDescription: '渡过河后不思进取，水火再次失调。',
            imageDescriptionEn: 'The fire goes out or the water boils over. The order is starting to collapse because of a lack of continued effort.',
            generalMeaning: '成功后开始松懈，乱象从内部滋生。',
            generalMeaningEn: 'Complacency is leading to ruin. You have stopped paying attention because you think you are safe. Minor errors are now growing into major problems.',
            scenes: {
                career: '成功后懈怠导致隐患，竞争者追上。',
                relationship: '感情趋于平淡甚至冷漠，危机潜伏。',
                wealth: '财务开始有漏洞，或挥霍无度。',
                health: '好转后不注意保养，导致复发。',
                universal: '需重振旗鼓，防止成功后的坠落。',
                transition: '从圆满转向缓慢衰退的警示。'
            },
            scenesEn: {
                career: 'Losing focus after success; slow decline in quality; professional rot.',
                relationship: 'Taking a partner for granted; relationship becoming stale; internal drifting.',
                wealth: 'Slow financial leakage; overconfidence leading to bad decisions.',
                health: 'Relapse due to neglect; letting habits slide after healing.',
                universal: 'Vigilance is required even in victory; the work is never truly done.',
                transition: 'A sobering shift from perfection to potential decay.'
            }
        }
    },

    // 64 - 未济卦 (火水未济)
    '064': {
        id: '064',
        name: 'Wei Ji (Before Completion)',
        nameCn: '未济卦',
        symbol: '☲☵',
        trigrams: { upper: '离', lower: '坎' },
        introduction: '火在水上，尚未成功。象征黎明前的最后过渡，充满希望。',
        introductionEn: 'Wei Ji represents Before Completion. Fire is above Water, so they do not meet. It is the end of the I Ching, suggesting that the cycle of life never truly ends and new effort is required.',
        upright: {
            keywords: ['未完成', '继续努力', '希望', '新循环'],
            keywordsEn: ['Incomplete', 'Potential', 'Hope', 'Transition', 'New Beginning', 'Caution'],
            imageDescription: '小狐狸即将过河，湿了尾巴。差最后一步。',
            imageDescriptionEn: 'A young fox crossing the ice. He is almost there, but must be extremely careful not to get his tail wet at the very last second.',
            generalMeaning: '处于尚未完成的阶段，前途光明需慎终。',
            generalMeaningEn: 'You are so close to your goal. Do not get sloppy now. Maintain your focus for the final stretch. A new world is about to begin.',
            scenes: {
                career: '事业尚在最后攻坚，需保持斗志。',
                relationship: '感情尚不稳定，需更深层磨合经营。',
                wealth: '财务目标临门一脚，需坚持到底。',
                health: '调养接近尾声，不可大意。',
                universal: '尚未成功，努力终有成，保持警觉。',
                transition: '从旧事未了转向新事开启的过渡。'
            },
            scenesEn: {
                career: 'The final stage of a project; preparing for a relaunch; nearing a breakthrough.',
                relationship: 'A relationship in the making; early stages of commitment; high hope.',
                wealth: 'Almost reaching a financial milestone; need for one last effort.',
                health: 'The very end of recovery; almost back to full strength.',
                universal: 'Keep going; you are on the threshold of success.',
                transition: 'A hopeful shift toward a whole new cycle of life.'
            }
        },
        reversed: {
            keywords: ['功败垂成', '放弃', '失去信心', '混乱'],
            keywordsEn: ['Failure at the End', 'Giving Up', 'Lost Hope', 'Disorganization', 'Aversion'],
            imageDescription: '狐狸溺水，或者在对岸前徘徊不敢前。',
            imageDescriptionEn: 'The fox falls into the water. You have lost your nerve or made a critical mistake right at the finish line.',
            generalMeaning: '可能功亏一篑，或因恐惧而中途放弃。',
            generalMeaningEn: 'Failure to complete the task. You are letting doubt or exhaustion stop you just before the end. This leads to a messy and incomplete outcome.',
            scenes: {
                career: '事业临门一脚失败，或彻底失去斗志。',
                relationship: '感情在关键时刻退缩，缘分中断。',
                wealth: '财务功败垂成，在终点前亏损。',
                health: '意志消沉导致康复中断，病情反复。',
                universal: '需重拾勇气，不可在最后时刻退缩。',
                transition: '从希望转向放弃或失败的转折。'
            },
            scenesEn: {
                career: 'Cancelling a project at the last minute; professional cowardice; missing the goal.',
                relationship: 'Walking away just as things got serious; fear of the final step.',
                wealth: 'Losing money at the end of a deal; financial exhaustion.',
                health: 'Giving up on a treatment; mental and physical fatigue.',
                universal: 'Do not let the final step be the one you fear most.',
                transition: 'A disappointing shift from potential to incompletion.'
            }
        }
    }
};

// 获取卦意的辅助函数
export const getHexagramMeaning = (hexagramId: string): IChingMeaning | undefined => {
    return ichingMeanings[hexagramId];
};

// 获取所有卦的列表
export const getAllHexagrams = (): IChingMeaning[] => {
    return Object.values(ichingMeanings);
};
